import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Headphones, Video, Music, X, ArrowLeft, Lightbulb, Wrench, Copy, Download, Check, ChevronDown, ChevronUp } from 'lucide-react';
import CamelotWheel from '../DJTools/CamelotWheel';
import ProTipsTab from '../ProducerCorner/ProTipsTab';
import BeatAnalyzer from '../BeatAnalyzer';
import { EmoteLabInline } from '../EmoteLab';
import { copyToClipboard, formatChordText, formatScaleText, generateShareImage, downloadImage } from '../../utils/shareUtils';
import * as Tone from 'tone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SampleChainTool = () => {
  const [query, setQuery] = useState('');
  const [artist, setArtist] = useState('');
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setChain(null);
    try {
      const params = new URLSearchParams({ title: query.trim(), depth: '3' });
      if (artist.trim()) params.append('artist', artist.trim());
      const res = await fetch(`${BACKEND_URL}/api/musicbrainz/sample-chain?${params}`);
      const json = await res.json();
      setChain(json);
    } catch (e) {
      console.error('Sample chain error:', e);
    } finally {
      setLoading(false);
    }
  };

  // Color by relationship type
  const typeColor = (type) => {
    switch (type) {
      case 'sample': return '#00ff88';
      case 'remix': return '#ffa500';
      case 'cover': return '#00d4ff';
      default: return '#b06aff';
    }
  };

  return (
    <div data-testid="sample-chain-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Visualize how tracks connect through samples, remixes & covers</p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
        <input
          data-testid="sample-chain-title"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Track title..."
          style={{ flex: 2, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
        />
        <input
          data-testid="sample-chain-artist"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Artist..."
          style={{ flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
        />
        <button
          data-testid="sample-chain-search-btn"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #00ff88, #00cc66)', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          {loading ? '...' : 'Trace'}
        </button>
      </div>

      {chain?.success === false && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>{chain.message || 'Track not found. Try a different search.'}</div>
      )}

      {chain?.success && (
        <div data-testid="sample-chain-results" style={{
          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,255,136,0.15)',
          borderRadius: '12px', padding: '20px',
        }}>
          {/* Stats */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', justifyContent: 'center' }}>
            <span style={{ padding: '4px 12px', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '14px', fontSize: '11px', color: '#00ff88', fontWeight: 600 }}>
              {chain.total_nodes} tracks found
            </span>
            <span style={{ padding: '4px 12px', background: 'rgba(255,165,0,0.1)', border: '1px solid rgba(255,165,0,0.2)', borderRadius: '14px', fontSize: '11px', color: '#ffa500', fontWeight: 600 }}>
              {chain.total_edges} connections
            </span>
            <span style={{ padding: '4px 12px', background: 'rgba(138,43,226,0.1)', border: '1px solid rgba(138,43,226,0.2)', borderRadius: '14px', fontSize: '11px', color: '#b06aff', fontWeight: 600 }}>
              Depth: {chain.max_depth_reached}
            </span>
          </div>

          {/* Visual Chain */}
          <div style={{ position: 'relative' }}>
            {chain.nodes?.map((node, idx) => {
              const incomingEdge = chain.edges?.find(e => e.target === node.id);
              return (
                <div key={node.id} style={{
                  display: 'flex', alignItems: 'flex-start', marginBottom: '12px',
                  paddingLeft: `${node.depth * 28}px`,
                }}>
                  {/* Connector line */}
                  {node.depth > 0 && (
                    <div style={{
                      width: '20px', height: '2px', marginTop: '14px', marginRight: '8px',
                      background: incomingEdge ? typeColor(incomingEdge.type) : '#555',
                    }} />
                  )}
                  {/* Node */}
                  <div style={{
                    flex: 1, padding: '10px 14px',
                    background: node.depth === 0
                      ? 'linear-gradient(135deg, rgba(0,255,136,0.12), rgba(0,212,255,0.08))'
                      : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${node.depth === 0 ? 'rgba(0,255,136,0.3)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: '10px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {incomingEdge && (
                        <span style={{
                          padding: '1px 6px', fontSize: '9px', fontWeight: 700,
                          background: `${typeColor(incomingEdge.type)}20`,
                          border: `1px solid ${typeColor(incomingEdge.type)}40`,
                          borderRadius: '6px', color: typeColor(incomingEdge.type),
                          textTransform: 'uppercase',
                        }}>{incomingEdge.type}</span>
                      )}
                      {node.depth === 0 && (
                        <span style={{ padding: '1px 6px', fontSize: '9px', fontWeight: 700, background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '6px', color: '#00ff88' }}>ROOT</span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '4px' }}>{node.title}</div>
                    <div style={{ fontSize: '11px', color: node.depth === 0 ? '#00ff88' : '#888' }}>{node.artist}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {chain.total_nodes <= 1 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#666', fontSize: '12px' }}>
              No sample/remix connections found for this track. Try a well-known remix or sampled track.
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '8px' }}>
            <span style={{ fontSize: '9px', color: '#444', fontStyle: 'italic' }}>Powered by MusicBrainz Relationships</span>
          </div>
        </div>
      )}
    </div>
  );
};

const TrackLookupTool = () => {
  const [query, setQuery] = useState('');
  const [artist, setArtist] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [relationships, setRelationships] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    setRelationships(null);
    try {
      const params = new URLSearchParams({ title: query.trim() });
      if (artist.trim()) params.append('artist', artist.trim());
      const res = await fetch(`${BACKEND_URL}/api/musicbrainz/recording/search?${params}`);
      const json = await res.json();
      if (json.success && json.data) {
        setResult(json.data);
        if (json.data.mbid) {
          const relRes = await fetch(`${BACKEND_URL}/api/musicbrainz/recording/${json.data.mbid}/relationships`);
          const relJson = await relRes.json();
          if (relJson.success) setRelationships(relJson.data);
        }
      } else {
        setResult({ notFound: true });
      }
    } catch (e) {
      console.error('Track lookup error:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="track-lookup-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Search MusicBrainz for remix chains, credits & relationships</p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
        <input
          data-testid="track-lookup-title"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Track title..."
          style={{
            flex: 2, padding: '10px 14px', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
            color: '#fff', fontSize: '13px', outline: 'none',
          }}
        />
        <input
          data-testid="track-lookup-artist"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Artist (optional)"
          style={{
            flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
            color: '#fff', fontSize: '13px', outline: 'none',
          }}
        />
        <button
          data-testid="track-lookup-search-btn"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          style={{
            padding: '10px 20px', background: 'linear-gradient(135deg, #ff3333, #ff0066)',
            border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700,
            fontSize: '13px', cursor: 'pointer', opacity: loading ? 0.5 : 1,
          }}
        >
          {loading ? '...' : 'Search'}
        </button>
      </div>

      {result?.notFound && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>No results found. Try a different search.</div>
      )}

      {result && !result.notFound && (
        <div data-testid="track-lookup-results" style={{
          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,51,51,0.15)',
          borderRadius: '12px', padding: '16px',
        }}>
          <div style={{ marginBottom: '12px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', margin: 0 }}>{result.title}</h4>
            <p style={{ fontSize: '0.85rem', color: '#ff3333', margin: '2px 0 0', fontWeight: 600 }}>{result.artist_display}</p>
            {result.length_ms && (
              <span style={{ fontSize: '11px', color: '#666' }}>{Math.floor(result.length_ms / 60000)}:{String(Math.floor((result.length_ms % 60000) / 1000)).padStart(2, '0')}</span>
            )}
          </div>

          {/* Credits */}
          {result.artist_credits?.length > 1 && (
            <div style={{ marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Credits</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {result.artist_credits.map((ac, i) => (
                  <span key={i} style={{
                    padding: '2px 8px', background: 'rgba(255,51,51,0.1)',
                    border: '1px solid rgba(255,51,51,0.2)', borderRadius: '8px',
                    fontSize: '11px', color: '#ff6666',
                  }}>{ac.name}{ac.join_phrase && <span style={{ color: '#666' }}>{ac.join_phrase}</span>}</span>
                ))}
              </div>
            </div>
          )}

          {/* Genre Tags */}
          {result.tags?.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Genre Tags</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {result.tags.slice(0, 8).map((tag, i) => (
                  <span key={i} style={{
                    padding: '2px 8px', background: 'rgba(255,0,255,0.08)',
                    border: '1px solid rgba(255,0,255,0.15)', borderRadius: '8px',
                    fontSize: '10px', color: '#ff66ff',
                  }}>{tag.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Releases */}
          {result.releases?.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Releases</span>
              <div style={{ marginTop: '4px' }}>
                {result.releases.slice(0, 4).map((rel, i) => (
                  <div key={i} style={{ fontSize: '11px', color: '#aaa', padding: '2px 0' }}>
                    <span style={{ color: '#00d4ff' }}>{rel.title}</span>
                    {rel.date && <span style={{ color: '#555', marginLeft: '6px' }}>({rel.date})</span>}
                    {rel.country && <span style={{ color: '#555', marginLeft: '4px' }}>{rel.country}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remix Chain / Relationships */}
          {relationships && relationships.total_relationships > 0 && (
            <div data-testid="track-lookup-remix-chain">
              <span style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Remix Chain & Relationships</span>
              {relationships.remixes?.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  {relationships.remixes.map((r, i) => (
                    <div key={i} style={{ fontSize: '11px', padding: '3px 0', color: '#ffa500' }}>
                      {r.direction === 'backward' ? `Remix of "${r.target_title}" by ${r.target_artist}` : `Remixed as "${r.target_title}" by ${r.target_artist}`}
                    </div>
                  ))}
                </div>
              )}
              {relationships.covers?.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {relationships.covers.slice(0, 3).map((c, i) => (
                    <div key={i} style={{ fontSize: '11px', padding: '2px 0', color: '#00d4ff' }}>
                      {c.direction === 'backward' ? `Cover of "${c.target_title}"` : `Covered by ${c.target_artist}`}
                    </div>
                  ))}
                </div>
              )}
              {relationships.samples?.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {relationships.samples.slice(0, 3).map((s, i) => (
                    <div key={i} style={{ fontSize: '11px', padding: '2px 0', color: '#00ff88' }}>
                      Samples "{s.target_title}" by {s.target_artist}
                    </div>
                  ))}
                </div>
              )}
              {relationships.dj_mixes?.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {relationships.dj_mixes.slice(0, 3).map((m, i) => (
                    <div key={i} style={{ fontSize: '11px', padding: '2px 0', color: '#b06aff' }}>
                      DJ Mix: "{m.target_title}" by {m.target_artist}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ textAlign: 'right', marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '6px' }}>
            <span style={{ fontSize: '9px', color: '#444', fontStyle: 'italic' }}>Powered by MusicBrainz</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── BPM Tap Tempo Tool ───
const BpmTapTool = () => {
  const [taps, setTaps] = useState([]);
  const [bpm, setBpm] = useState(null);
  const [lastTap, setLastTap] = useState(null);
  const timeoutRef = useRef(null);

  const handleTap = useCallback(() => {
    const now = Date.now();
    // Reset if more than 3s since last tap
    if (lastTap && now - lastTap > 3000) {
      setTaps([now]);
      setBpm(null);
    } else {
      const newTaps = [...taps, now].slice(-12); // Keep last 12 taps
      setTaps(newTaps);
      if (newTaps.length >= 2) {
        const intervals = [];
        for (let i = 1; i < newTaps.length; i++) {
          intervals.push(newTaps[i] - newTaps[i - 1]);
        }
        const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        setBpm(Math.round(60000 / avgInterval * 10) / 10);
      }
    }
    setLastTap(now);
    // Auto-reset after 3s of no tapping
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setTaps([]);
      setBpm(null);
      setLastTap(null);
    }, 5000);
  }, [taps, lastTap]);

  const reset = () => { setTaps([]); setBpm(null); setLastTap(null); };

  // BPM range indicator
  const getBpmCategory = (b) => {
    if (!b) return { label: '', color: '#555' };
    if (b < 100) return { label: 'Downtempo / Deep', color: '#00d4ff' };
    if (b < 120) return { label: 'House / Disco', color: '#00ff88' };
    if (b < 130) return { label: 'Tech House', color: '#fbbf24' };
    if (b < 140) return { label: 'Techno', color: '#ff6600' };
    if (b < 150) return { label: 'Hard Techno', color: '#ff3333' };
    if (b < 175) return { label: 'Drum & Bass', color: '#ff00ff' };
    return { label: 'Hardcore / Gabber', color: '#ff0066' };
  };
  const cat = getBpmCategory(bpm);

  return (
    <div data-testid="bpm-tap-tool" style={{ padding: '1.5rem', textAlign: 'center' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          fontSize: bpm ? '72px' : '48px', fontWeight: 800, color: bpm ? cat.color : '#333',
          fontFamily: 'monospace', transition: 'all 0.3s ease', lineHeight: 1.1,
        }}>
          {bpm ? bpm.toFixed(1) : '---'}
        </div>
        <div style={{ fontSize: '14px', color: '#888', fontWeight: 600, marginTop: '4px' }}>BPM</div>
        {bpm && (
          <div style={{
            marginTop: '8px', display: 'inline-block',
            padding: '4px 14px', borderRadius: '12px',
            background: `${cat.color}18`, border: `1px solid ${cat.color}40`,
            color: cat.color, fontSize: '12px', fontWeight: 700,
          }}>{cat.label}</div>
        )}
      </div>
      <button
        data-testid="bpm-tap-btn"
        onClick={handleTap}
        style={{
          width: '180px', height: '180px', borderRadius: '50%',
          background: lastTap && Date.now() - lastTap < 300
            ? 'radial-gradient(circle, rgba(255,51,51,0.4) 0%, rgba(255,51,51,0.15) 70%)'
            : 'radial-gradient(circle, rgba(255,51,51,0.2) 0%, rgba(255,51,51,0.05) 70%)',
          border: '3px solid rgba(255,51,51,0.5)',
          color: '#ff3333', fontSize: '18px', fontWeight: 800,
          cursor: 'pointer', transition: 'all 0.1s ease',
          boxShadow: lastTap && Date.now() - lastTap < 300 ? '0 0 40px rgba(255,51,51,0.3)' : 'none',
          letterSpacing: '2px', textTransform: 'uppercase',
        }}
      >
        TAP
      </button>
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: '#555' }}>{taps.length} taps</span>
        <button onClick={reset} style={{
          padding: '4px 14px', background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px',
          color: '#888', fontSize: '11px', cursor: 'pointer',
        }}>Reset</button>
      </div>
      <p style={{ fontSize: '11px', color: '#444', marginTop: '16px' }}>
        Tap along with the beat. More taps = more accurate. Auto-resets after 5s idle.
      </p>
    </div>
  );
};

// ─── Beat Counter Tool ───
const BeatCounterTool = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [bpm, setBpm] = useState(128);
  const [beatsPerBar, setBeatsPerBar] = useState(4);
  const [currentBar, setCurrentBar] = useState(0);
  const intervalRef = useRef(null);

  const start = useCallback(() => {
    setIsRunning(true);
    setCurrentBeat(1);
    setCurrentBar(0);
    const ms = 60000 / bpm;
    let beat = 1;
    let bar = 0;
    intervalRef.current = setInterval(() => {
      beat++;
      if (beat > beatsPerBar) { beat = 1; bar++; }
      setCurrentBeat(beat);
      setCurrentBar(bar);
    }, ms);
  }, [bpm, beatsPerBar]);

  const stop = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCurrentBeat(0);
    setCurrentBar(0);
  }, []);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  return (
    <div data-testid="beat-counter-tool" style={{ padding: '1.5rem', textAlign: 'center' }}>
      {/* BPM selector */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <button onClick={() => setBpm(p => Math.max(60, p - 1))} disabled={isRunning}
          style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '18px', cursor: 'pointer' }}>-</button>
        <div>
          <div style={{ fontSize: '36px', fontWeight: 800, color: '#ff3333', fontFamily: 'monospace' }}>{bpm}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>BPM</div>
        </div>
        <button onClick={() => setBpm(p => Math.min(200, p + 1))} disabled={isRunning}
          style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '18px', cursor: 'pointer' }}>+</button>
      </div>

      {/* Time signature */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '8px' }}>
        {[3, 4, 6, 8].map(n => (
          <button key={n} onClick={() => { if (!isRunning) setBeatsPerBar(n); }}
            style={{
              padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              background: beatsPerBar === n ? 'rgba(255,51,51,0.2)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${beatsPerBar === n ? 'rgba(255,51,51,0.4)' : 'rgba(255,255,255,0.1)'}`,
              color: beatsPerBar === n ? '#ff3333' : '#888',
            }}>{n}/4</button>
        ))}
      </div>

      {/* Beat dots */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
        {Array.from({ length: beatsPerBar }).map((_, i) => {
          const isActive = currentBeat === i + 1;
          const isDownbeat = i === 0 && isActive;
          return (
            <div key={i} style={{
              width: isDownbeat ? '60px' : '50px', height: isDownbeat ? '60px' : '50px',
              borderRadius: '50%',
              background: isActive
                ? isDownbeat
                  ? 'radial-gradient(circle, #ff3333, #cc0000)'
                  : 'radial-gradient(circle, #fbbf24, #f59e0b)'
                : 'rgba(255,255,255,0.04)',
              border: `2px solid ${isActive ? (isDownbeat ? '#ff3333' : '#fbbf24') : 'rgba(255,255,255,0.1)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: 800, color: isActive ? '#000' : '#444',
              transition: 'all 0.08s ease',
              boxShadow: isActive ? `0 0 30px ${isDownbeat ? 'rgba(255,51,51,0.5)' : 'rgba(251,191,36,0.4)'}` : 'none',
            }}>
              {i + 1}
            </div>
          );
        })}
      </div>

      {/* Bar counter */}
      {isRunning && (
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
          Bar <span style={{ color: '#ff3333', fontWeight: 700, fontSize: '16px' }}>{currentBar + 1}</span>
        </div>
      )}

      {/* Start/Stop */}
      <button
        data-testid="beat-counter-toggle"
        onClick={isRunning ? stop : start}
        style={{
          padding: '12px 40px', borderRadius: '12px',
          background: isRunning ? 'rgba(255,51,51,0.2)' : 'linear-gradient(135deg, #ff3333, #ff0066)',
          border: isRunning ? '1px solid rgba(255,51,51,0.4)' : 'none',
          color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          letterSpacing: '1px',
        }}
      >{isRunning ? 'STOP' : 'START'}</button>
    </div>
  );
};

// ─── Set Timer Tool ───
const SetTimerTool = () => {
  const [mode, setMode] = useState('stopwatch'); // 'stopwatch' or 'countdown'
  const [isRunning, setIsRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms
  const [countdownTarget, setCountdownTarget] = useState(3600000); // 1hr default
  const [laps, setLaps] = useState([]);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const formatTime = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h > 0 ? h + ':' : ''}${h > 0 ? String(m).padStart(2, '0') : m}:${String(s).padStart(2, '0')}`;
  };

  const start = () => {
    setIsRunning(true);
    startTimeRef.current = Date.now() - elapsed;
    intervalRef.current = setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 100);
  };

  const pause = () => {
    setIsRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const reset = () => {
    pause();
    setElapsed(0);
    setLaps([]);
  };

  const addLap = () => {
    setLaps(prev => [...prev, { time: elapsed, label: `Track ${prev.length + 1}` }]);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const displayTime = mode === 'countdown' ? Math.max(0, countdownTarget - elapsed) : elapsed;
  const isOvertime = mode === 'countdown' && elapsed > countdownTarget;
  const progress = mode === 'countdown' ? Math.min(1, elapsed / countdownTarget) : 0;

  return (
    <div data-testid="set-timer-tool" style={{ padding: '1.5rem', textAlign: 'center' }}>
      {/* Mode toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px', width: 'fit-content', margin: '0 auto 1.5rem' }}>
        {['stopwatch', 'countdown'].map(m => (
          <button key={m} onClick={() => { if (!isRunning) { setMode(m); reset(); } }}
            style={{
              padding: '6px 20px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              background: mode === m ? 'rgba(255,51,51,0.2)' : 'transparent',
              border: mode === m ? '1px solid rgba(255,51,51,0.3)' : '1px solid transparent',
              color: mode === m ? '#ff3333' : '#666', textTransform: 'capitalize',
            }}>{m}</button>
        ))}
      </div>

      {/* Countdown target selector */}
      {mode === 'countdown' && !isRunning && elapsed === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { label: '30m', ms: 1800000 }, { label: '1hr', ms: 3600000 },
            { label: '1.5hr', ms: 5400000 }, { label: '2hr', ms: 7200000 },
            { label: '3hr', ms: 10800000 }, { label: '4hr', ms: 14400000 },
          ].map(opt => (
            <button key={opt.ms} onClick={() => setCountdownTarget(opt.ms)}
              style={{
                padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                background: countdownTarget === opt.ms ? 'rgba(255,51,51,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${countdownTarget === opt.ms ? 'rgba(255,51,51,0.3)' : 'rgba(255,255,255,0.08)'}`,
                color: countdownTarget === opt.ms ? '#ff3333' : '#888',
              }}>{opt.label}</button>
          ))}
        </div>
      )}

      {/* Progress ring for countdown */}
      {mode === 'countdown' && (
        <div style={{ position: 'relative', width: '200px', height: '200px', margin: '0 auto 1rem' }}>
          <svg width="200" height="200" viewBox="0 0 200 200" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
            <circle cx="100" cy="100" r="88" fill="none"
              stroke={isOvertime ? '#ff0066' : '#ff3333'}
              strokeWidth="6" strokeDasharray={553} strokeDashoffset={553 * (1 - progress)}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.3s' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 800, color: isOvertime ? '#ff0066' : '#fff', fontFamily: 'monospace' }}>
              {isOvertime ? '+' : ''}{formatTime(isOvertime ? elapsed - countdownTarget : displayTime)}
            </div>
            {isOvertime && <div style={{ fontSize: '11px', color: '#ff0066', fontWeight: 600 }}>OVERTIME</div>}
          </div>
        </div>
      )}

      {/* Stopwatch display */}
      {mode === 'stopwatch' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '52px', fontWeight: 800, color: '#fff', fontFamily: 'monospace', letterSpacing: '2px' }}>
            {formatTime(elapsed)}
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '1.5rem' }}>
        <button onClick={isRunning ? pause : start} data-testid="set-timer-toggle"
          style={{
            padding: '12px 32px', borderRadius: '10px',
            background: isRunning ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #ff3333, #ff0066)',
            border: isRunning ? '1px solid rgba(255,255,255,0.15)' : 'none',
            color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          }}>{isRunning ? 'PAUSE' : elapsed > 0 ? 'RESUME' : 'START'}</button>
        {isRunning && (
          <button onClick={addLap} data-testid="set-timer-lap"
            style={{
              padding: '12px 20px', borderRadius: '10px',
              background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)',
              color: '#fbbf24', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            }}>TRACK</button>
        )}
        {elapsed > 0 && !isRunning && (
          <button onClick={reset}
            style={{
              padding: '12px 20px', borderRadius: '10px',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              color: '#888', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}>RESET</button>
        )}
      </div>

      {/* Lap/Track markers */}
      {laps.length > 0 && (
        <div style={{ maxHeight: '180px', overflowY: 'auto', textAlign: 'left', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '8px 12px' }}>
          <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', fontWeight: 700 }}>Track Markers</div>
          {laps.map((lap, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '12px' }}>
              <span style={{ color: '#fbbf24', fontWeight: 600 }}>{lap.label}</span>
              <span style={{ color: '#aaa', fontFamily: 'monospace' }}>{formatTime(lap.time)}</span>
              {i > 0 && <span style={{ color: '#555', fontFamily: 'monospace', fontSize: '10px' }}>+{formatTime(lap.time - laps[i-1].time)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Mix Point Calculator Tool ───
const MixPointCalculator = () => {
  const [bpmA, setBpmA] = useState('');
  const [bpmB, setBpmB] = useState('');

  const a = parseFloat(bpmA);
  const b = parseFloat(bpmB);
  const valid = a > 0 && b > 0 && a >= 60 && a <= 200 && b >= 60 && b <= 200;

  const diff = valid ? Math.abs(a - b) : 0;
  const pctDiff = valid ? (diff / Math.min(a, b)) * 100 : 0;
  const midBpm = valid ? Math.round((a + b) / 2 * 10) / 10 : 0;
  // Bars needed for smooth transition (roughly 1 bar per 0.5 BPM diff)
  const barsNeeded = valid ? Math.max(4, Math.min(32, Math.round(diff * 2))) : 0;
  const transitionSec = valid ? Math.round(barsNeeded * (4 * 60 / midBpm)) : 0;
  const pitchA = valid ? ((midBpm - a) / a * 100).toFixed(1) : 0;
  const pitchB = valid ? ((midBpm - b) / b * 100).toFixed(1) : 0;

  const getDifficulty = () => {
    if (!valid) return null;
    if (pctDiff < 2) return { label: 'Easy', color: '#00ff88', tip: 'Smooth long blend works perfectly' };
    if (pctDiff < 4) return { label: 'Moderate', color: '#fbbf24', tip: 'Use a gradual tempo ride or quick 8-bar blend' };
    if (pctDiff < 7) return { label: 'Tricky', color: '#ff6600', tip: 'Loop the incoming track and tempo ride, or use a breakdown to switch' };
    return { label: 'Hard Cut', color: '#ff3333', tip: 'Use a drop swap, echo out, or dead stop. Avoid blending.' };
  };
  const difficulty = getDifficulty();

  return (
    <div data-testid="mix-point-calc" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '1.5rem', justifyContent: 'center', alignItems: 'flex-end' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#ff3333', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Track A</div>
          <input data-testid="mix-bpm-a" value={bpmA} onChange={(e) => setBpmA(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="BPM" maxLength={6}
            style={{ width: '100px', padding: '12px', background: 'rgba(255,51,51,0.08)', border: '1px solid rgba(255,51,51,0.3)', borderRadius: '10px', color: '#fff', fontSize: '20px', fontWeight: 800, textAlign: 'center', fontFamily: 'monospace', outline: 'none' }} />
        </div>
        <div style={{ fontSize: '24px', color: '#555', paddingBottom: '12px' }}>→</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: '#00d4ff', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Track B</div>
          <input data-testid="mix-bpm-b" value={bpmB} onChange={(e) => setBpmB(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="BPM" maxLength={6}
            style={{ width: '100px', padding: '12px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '10px', color: '#fff', fontSize: '20px', fontWeight: 800, textAlign: 'center', fontFamily: 'monospace', outline: 'none' }} />
        </div>
      </div>

      {valid && (
        <div data-testid="mix-results" style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '16px' }}>
          {/* Difficulty badge */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span style={{ padding: '4px 16px', borderRadius: '12px', background: `${difficulty.color}18`, border: `1px solid ${difficulty.color}40`, color: difficulty.color, fontSize: '13px', fontWeight: 700 }}>
              {difficulty.label} Mix
            </span>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>{difficulty.tip}</div>
          </div>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>BPM Difference</div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: difficulty.color, fontFamily: 'monospace' }}>{diff.toFixed(1)}</div>
              <div style={{ fontSize: '10px', color: '#555' }}>{pctDiff.toFixed(1)}% change</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Meet at</div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#fff', fontFamily: 'monospace' }}>{midBpm}</div>
              <div style={{ fontSize: '10px', color: '#555' }}>BPM midpoint</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Transition</div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#fbbf24', fontFamily: 'monospace' }}>{barsNeeded}</div>
              <div style={{ fontSize: '10px', color: '#555' }}>bars (~{transitionSec}s)</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pitch Adjust</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '4px' }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#ff3333', fontFamily: 'monospace' }}>{pitchA > 0 ? '+' : ''}{pitchA}%</span>
                  <div style={{ fontSize: '9px', color: '#666' }}>Track A</div>
                </div>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#00d4ff', fontFamily: 'monospace' }}>{pitchB > 0 ? '+' : ''}{pitchB}%</span>
                  <div style={{ fontSize: '9px', color: '#666' }}>Track B</div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual BPM bar */}
          <div style={{ position: 'relative', height: '30px', background: 'rgba(255,255,255,0.04)', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ position: 'absolute', left: '10%', top: 0, bottom: 0, width: '2px', background: '#ff3333' }} />
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', background: '#fbbf24', opacity: 0.5 }} />
            <div style={{ position: 'absolute', right: '10%', top: 0, bottom: 0, width: '2px', background: '#00d4ff' }} />
            <div style={{ position: 'absolute', left: '10%', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', color: '#ff3333', fontWeight: 700, marginLeft: '6px' }}>{a}</div>
            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', fontSize: '9px', color: '#fbbf24', fontWeight: 700 }}>{midBpm}</div>
            <div style={{ position: 'absolute', right: '10%', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', color: '#00d4ff', fontWeight: 700, marginRight: '6px' }}>{b}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Transition Advisor Tool ───
const CAMELOT_COMPAT = {
  '1A': ['1A','1B','12A','2A'], '1B': ['1B','1A','12B','2B'],
  '2A': ['2A','2B','1A','3A'], '2B': ['2B','2A','1B','3B'],
  '3A': ['3A','3B','2A','4A'], '3B': ['3B','3A','2B','4B'],
  '4A': ['4A','4B','3A','5A'], '4B': ['4B','4A','3B','5B'],
  '5A': ['5A','5B','4A','6A'], '5B': ['5B','5A','4B','6B'],
  '6A': ['6A','6B','5A','7A'], '6B': ['6B','6A','5B','7B'],
  '7A': ['7A','7B','6A','8A'], '7B': ['7B','7A','6B','8B'],
  '8A': ['8A','8B','7A','9A'], '8B': ['8B','8A','7B','9B'],
  '9A': ['9A','9B','8A','10A'], '9B': ['9B','9A','8B','10B'],
  '10A': ['10A','10B','9A','11A'], '10B': ['10B','10A','9B','11B'],
  '11A': ['11A','11B','10A','12A'], '11B': ['11B','11A','10B','12B'],
  '12A': ['12A','12B','11A','1A'], '12B': ['12B','12A','11B','1B'],
};
const CAMELOT_COLORS = {
  '1A': '#00bfff', '1B': '#00bfff', '2A': '#0099cc', '2B': '#0099cc',
  '3A': '#00cc66', '3B': '#00cc66', '4A': '#66cc00', '4B': '#66cc00',
  '5A': '#cccc00', '5B': '#cccc00', '6A': '#ffcc00', '6B': '#ffcc00',
  '7A': '#ff9900', '7B': '#ff9900', '8A': '#ff6600', '8B': '#ff6600',
  '9A': '#ff3300', '9B': '#ff3300', '10A': '#ff0066', '10B': '#ff0066',
  '11A': '#cc00ff', '11B': '#cc00ff', '12A': '#6600ff', '12B': '#6600ff',
};
const ALL_KEYS = Object.keys(CAMELOT_COMPAT);

const TransitionAdvisor = () => {
  const [keyA, setKeyA] = useState('');
  const [bpmA, setBpmA] = useState('');
  const [keyB, setKeyB] = useState('');
  const [bpmB, setBpmB] = useState('');

  const a = parseFloat(bpmA);
  const b = parseFloat(bpmB);
  const validBpm = a > 0 && b > 0;
  const validKeys = keyA && keyB && CAMELOT_COMPAT[keyA] && CAMELOT_COMPAT[keyB];

  const keyCompat = validKeys ? CAMELOT_COMPAT[keyA].includes(keyB) : null;
  const bpmDiff = validBpm ? Math.abs(a - b) : 0;
  const pctDiff = validBpm ? (bpmDiff / Math.min(a, b)) * 100 : 0;

  // Energy shift
  const getEnergyShift = () => {
    if (!validBpm) return null;
    if (b > a + 3) return { label: 'Energy Up', icon: '▲', color: '#ff3333' };
    if (b < a - 3) return { label: 'Energy Down', icon: '▼', color: '#00d4ff' };
    return { label: 'Steady', icon: '●', color: '#fbbf24' };
  };
  const energy = getEnergyShift();

  // Transition technique suggestion
  const getTechnique = () => {
    if (!validBpm || !validKeys) return [];
    const techniques = [];
    if (keyCompat && pctDiff < 3) {
      techniques.push({ name: 'Long Blend', desc: '16-32 bar gradual mix — both tracks harmonize', quality: 'best' });
    }
    if (keyCompat && pctDiff < 5) {
      techniques.push({ name: 'Bass Swap', desc: 'Swap bass at a phrase point, let melodies layer', quality: 'great' });
    }
    if (pctDiff < 4) {
      techniques.push({ name: 'Tempo Ride', desc: `Gradually shift BPM over 8-16 bars to meet at ${Math.round((a+b)/2)}`, quality: keyCompat ? 'great' : 'good' });
    }
    if (!keyCompat && pctDiff < 3) {
      techniques.push({ name: 'EQ Mix', desc: 'High-pass the outgoing track to avoid key clash, blend the lows', quality: 'good' });
    }
    if (pctDiff >= 4 || !keyCompat) {
      techniques.push({ name: 'Drop Swap', desc: 'Echo/reverb out Track A, drop Track B on the one', quality: !keyCompat && pctDiff > 5 ? 'best' : 'good' });
    }
    if (pctDiff >= 5) {
      techniques.push({ name: 'Dead Stop', desc: 'Cut Track A completely, pause, drop Track B fresh', quality: pctDiff > 8 ? 'best' : 'good' });
    }
    techniques.push({ name: 'Loop & Switch', desc: 'Loop a 4-bar section of Track A, bring in Track B underneath', quality: 'good' });
    return techniques;
  };
  const techniques = getTechnique();

  const qualityColors = { best: '#00ff88', great: '#fbbf24', good: '#888' };

  // Overall verdict
  const getVerdict = () => {
    if (!validBpm || !validKeys) return null;
    if (keyCompat && pctDiff < 3) return { text: 'Perfect mix — harmonically compatible, BPM aligned', color: '#00ff88' };
    if (keyCompat && pctDiff < 6) return { text: 'Good mix — keys match, watch the tempo shift', color: '#fbbf24' };
    if (!keyCompat && pctDiff < 3) return { text: 'BPM aligned but keys clash — use quick transitions', color: '#ff9900' };
    return { text: 'Challenging — consider a hard transition or breakdown', color: '#ff3333' };
  };
  const verdict = getVerdict();

  return (
    <div data-testid="transition-advisor" style={{ padding: '1.5rem' }}>
      {/* Input grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'end', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontSize: '10px', color: '#ff3333', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Track A</div>
          <select value={keyA} onChange={(e) => setKeyA(e.target.value)} data-testid="trans-key-a"
            style={{ width: '100%', padding: '8px', marginBottom: '6px', background: 'rgba(255,51,51,0.08)', border: '1px solid rgba(255,51,51,0.3)', borderRadius: '8px', color: keyA ? (CAMELOT_COLORS[keyA] || '#fff') : '#666', fontSize: '14px', fontWeight: 700, outline: 'none' }}>
            <option value="">Key</option>
            {ALL_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={bpmA} onChange={(e) => setBpmA(e.target.value.replace(/[^\d.]/g, ''))} placeholder="BPM"
            data-testid="trans-bpm-a"
            style={{ width: '100%', padding: '8px', background: 'rgba(255,51,51,0.08)', border: '1px solid rgba(255,51,51,0.3)', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: 700, textAlign: 'center', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ fontSize: '24px', color: '#555', paddingBottom: '20px' }}>→</div>
        <div>
          <div style={{ fontSize: '10px', color: '#00d4ff', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Track B</div>
          <select value={keyB} onChange={(e) => setKeyB(e.target.value)} data-testid="trans-key-b"
            style={{ width: '100%', padding: '8px', marginBottom: '6px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '8px', color: keyB ? (CAMELOT_COLORS[keyB] || '#fff') : '#666', fontSize: '14px', fontWeight: 700, outline: 'none' }}>
            <option value="">Key</option>
            {ALL_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={bpmB} onChange={(e) => setBpmB(e.target.value.replace(/[^\d.]/g, ''))} placeholder="BPM"
            data-testid="trans-bpm-b"
            style={{ width: '100%', padding: '8px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: 700, textAlign: 'center', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>

      {(validBpm || validKeys) && (
        <div data-testid="transition-results" style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '16px' }}>
          {/* Verdict */}
          {verdict && (
            <div style={{ textAlign: 'center', marginBottom: '14px', padding: '10px', background: `${verdict.color}0a`, border: `1px solid ${verdict.color}25`, borderRadius: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: verdict.color }}>{verdict.text}</div>
            </div>
          )}

          {/* Key + BPM + Energy badges */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {validKeys && (
              <span style={{
                padding: '4px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                background: keyCompat ? 'rgba(0,255,136,0.12)' : 'rgba(255,51,51,0.12)',
                border: `1px solid ${keyCompat ? 'rgba(0,255,136,0.3)' : 'rgba(255,51,51,0.3)'}`,
                color: keyCompat ? '#00ff88' : '#ff3333',
              }}>{keyCompat ? 'Keys Compatible' : 'Keys Clash'}</span>
            )}
            {validBpm && (
              <span style={{
                padding: '4px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                background: pctDiff < 3 ? 'rgba(0,255,136,0.12)' : pctDiff < 6 ? 'rgba(251,191,36,0.12)' : 'rgba(255,51,51,0.12)',
                border: `1px solid ${pctDiff < 3 ? 'rgba(0,255,136,0.3)' : pctDiff < 6 ? 'rgba(251,191,36,0.3)' : 'rgba(255,51,51,0.3)'}`,
                color: pctDiff < 3 ? '#00ff88' : pctDiff < 6 ? '#fbbf24' : '#ff3333',
              }}>{bpmDiff.toFixed(1)} BPM diff</span>
            )}
            {energy && (
              <span style={{
                padding: '4px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                background: `${energy.color}15`, border: `1px solid ${energy.color}30`, color: energy.color,
              }}>{energy.icon} {energy.label}</span>
            )}
          </div>

          {/* Suggested techniques */}
          {techniques.length > 0 && (
            <div>
              <div style={{ fontSize: '10px', color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Suggested Techniques</div>
              {techniques.map((t, i) => (
                <div key={i} data-testid={`technique-${i}`} style={{
                  display: 'flex', gap: '10px', padding: '8px 10px', marginBottom: '4px',
                  background: 'rgba(255,255,255,0.02)', borderRadius: '8px',
                  borderLeft: `3px solid ${qualityColors[t.quality]}`,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{t.name}</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{t.desc}</div>
                  </div>
                  <span style={{ fontSize: '9px', color: qualityColors[t.quality], fontWeight: 700, textTransform: 'uppercase', alignSelf: 'center', flexShrink: 0 }}>{t.quality}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Cue Point Notepad Tool ───
const CuePointNotepad = () => {
  const [tracks, setTracks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTrack, setNewTrack] = useState('');
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [newCueTime, setNewCueTime] = useState('');
  const [newCueLabel, setNewCueLabel] = useState('');

  const getToken = () => localStorage.getItem('beatseek_token');

  // Load cue notes on mount
  useEffect(() => {
    const loadNotes = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tools/cue-notes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setTracks(data.tracks || []);
            return;
          }
        } catch (e) { console.error('Failed to load cue notes from server:', e); }
      }
      try { setTracks(JSON.parse(localStorage.getItem('beatseek_cue_notes') || '[]')); } catch { setTracks([]); }
    };
    loadNotes();
  }, []);

  const save = useCallback((updated) => {
    setTracks(updated);
    localStorage.setItem('beatseek_cue_notes', JSON.stringify(updated));
    // Sync to server
    const token = getToken();
    if (token) {
      fetch(`${BACKEND_URL}/api/tools/cue-notes`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracks: updated }),
      }).catch(e => console.error('Failed to sync cue notes:', e));
    }
  }, []);

  const addTrack = () => {
    if (!newTrack.trim()) return;
    save([...tracks, { id: Date.now(), name: newTrack.trim(), cues: [] }]);
    setNewTrack('');
    setShowAdd(false);
    setExpandedTrack(tracks.length); // auto-expand the new one
  };

  const removeTrack = (id) => save(tracks.filter(t => t.id !== id));

  const addCue = (trackId) => {
    if (!newCueTime.trim() && !newCueLabel.trim()) return;
    save(tracks.map(t =>
      t.id === trackId
        ? { ...t, cues: [...t.cues, { id: Date.now(), time: newCueTime.trim(), label: newCueLabel.trim() || 'Cue Point' }] }
        : t
    ));
    setNewCueTime('');
    setNewCueLabel('');
  };

  const removeCue = (trackId, cueId) => {
    save(tracks.map(t =>
      t.id === trackId ? { ...t, cues: t.cues.filter(c => c.id !== cueId) } : t
    ));
  };

  const exportNotes = () => {
    let text = '=== BEATSEEK CUE POINT NOTES ===\n\n';
    tracks.forEach(t => {
      text += `▸ ${t.name}\n`;
      t.cues.forEach(c => { text += `  ${c.time || '??:??'} — ${c.label}\n`; });
      text += '\n';
    });
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'cue-points.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  const CUE_PRESETS = ['Drop', 'Breakdown', 'Vocal', 'Build Up', 'Loop In', 'Loop Out', 'Mix Out'];

  return (
    <div data-testid="cue-notepad" style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '11px', color: '#666' }}>{tracks.length} tracks · {tracks.reduce((s, t) => s + t.cues.length, 0)} cue points</span>
        {tracks.length > 0 && (
          <button onClick={exportNotes} data-testid="cue-export-btn"
            style={{ padding: '4px 12px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '6px', color: '#fbbf24', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
            Export .txt
          </button>
        )}
      </div>

      {/* Track list */}
      {tracks.map((track, ti) => {
        const isOpen = expandedTrack === ti;
        return (
          <div key={track.id} data-testid={`cue-track-${ti}`} style={{ marginBottom: '6px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${isOpen ? 'rgba(255,51,51,0.2)' : 'rgba(255,255,255,0.04)'}`, borderRadius: '10px', overflow: 'hidden' }}>
            <div
              onClick={() => setExpandedTrack(isOpen ? null : ti)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: '18px', color: '#555', transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{track.name}</div>
                <div style={{ fontSize: '10px', color: '#555' }}>{track.cues.length} cue points</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); removeTrack(track.id); }}
                style={{ background: 'none', border: 'none', color: '#ff3333', cursor: 'pointer', fontSize: '16px', padding: '2px 6px' }}>×</button>
            </div>
            {isOpen && (
              <div style={{ padding: '0 12px 12px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                {track.cues.map((cue, ci) => (
                  <div key={cue.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#ff3333', fontFamily: 'monospace', width: '50px', flexShrink: 0 }}>{cue.time || '--:--'}</span>
                    <span style={{ fontSize: '12px', color: '#ddd', flex: 1 }}>{cue.label}</span>
                    <button onClick={() => removeCue(track.id, cue.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '14px' }}>×</button>
                  </div>
                ))}
                {/* Add cue form */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
                  <input value={newCueTime} onChange={(e) => setNewCueTime(e.target.value)} placeholder="0:00"
                    style={{ width: '55px', padding: '6px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#fff', fontSize: '12px', fontFamily: 'monospace', outline: 'none', textAlign: 'center' }} />
                  <input value={newCueLabel} onChange={(e) => setNewCueLabel(e.target.value)} placeholder="Label..."
                    onKeyDown={(e) => e.key === 'Enter' && addCue(track.id)}
                    style={{ flex: 1, padding: '6px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
                  <button onClick={() => addCue(track.id)}
                    style={{ padding: '6px 12px', background: 'rgba(255,51,51,0.15)', border: '1px solid rgba(255,51,51,0.3)', borderRadius: '6px', color: '#ff3333', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>+</button>
                </div>
                {/* Quick presets */}
                <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                  {CUE_PRESETS.map(p => (
                    <button key={p} onClick={() => { setNewCueLabel(p); }}
                      style={{ padding: '2px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', color: '#666', fontSize: '9px', cursor: 'pointer' }}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add track */}
      {showAdd ? (
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <input value={newTrack} onChange={(e) => setNewTrack(e.target.value)} placeholder="Track name..." autoFocus
            onKeyDown={(e) => e.key === 'Enter' && addTrack()}
            style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }} />
          <button onClick={addTrack} disabled={!newTrack.trim()}
            style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #ff3333, #ff0066)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>Add</button>
          <button onClick={() => { setShowAdd(false); setNewTrack(''); }}
            style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#888', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} data-testid="cue-add-track-btn"
          style={{ width: '100%', padding: '10px', marginTop: '8px', borderRadius: '10px', background: 'rgba(255,51,51,0.08)', border: '1px dashed rgba(255,51,51,0.3)', color: '#ff3333', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          + Add Track
        </button>
      )}
    </div>
  );
};

// ─── Gain Staging Guide Tool ───
const GainStagingGuide = () => {
  const [activeSection, setActiveSection] = useState('signal');

  const sections = [
    { id: 'signal', label: 'Signal Flow' },
    { id: 'levels', label: 'Level Guide' },
    { id: 'tips', label: 'Pro Tips' },
  ];

  const signalChain = [
    { name: 'Source', detail: 'CDJ / Controller / Laptop', level: '-12 to -6 dBFS', color: '#00d4ff', tip: 'Set your source output to line level. Digital outputs should peak around -6dBFS.' },
    { name: 'Channel Gain', detail: 'Mixer input trim/gain knob', level: 'Unity (12 o\'clock)', color: '#00ff88', tip: 'Adjust so channel meters peak in the yellow zone during the loudest sections. Never into red.' },
    { name: 'Channel EQ', detail: 'Hi / Mid / Low knobs', level: '±0 dB (flat)', color: '#fbbf24', tip: 'Start flat. Cut before you boost. Cutting bass of the outgoing track is cleaner than boosting the incoming.' },
    { name: 'Channel Fader', detail: 'Vertical slider', level: 'Full open', color: '#ff9900', tip: 'Keep at max for full signal. Use only for blending during transitions.' },
    { name: 'Master Output', detail: 'Main mix bus', level: '0 dB / Unity', color: '#ff3333', tip: 'Master should sit at 0dB. If the PA needs more volume, the sound engineer adjusts the amp — not you.' },
    { name: 'Booth Monitor', detail: 'Your headphone/booth out', level: 'Comfortable', color: '#ff00ff', tip: 'Don\'t match booth volume to PA. Protect your hearing — you need your ears for the next 20 years.' },
  ];

  const levelGuide = [
    { range: '-∞ to -18 dB', label: 'Too Quiet', color: '#00d4ff', bar: 15, desc: 'Noise floor issues. Raise the gain.' },
    { range: '-18 to -12 dB', label: 'Low', color: '#00ff88', bar: 30, desc: 'Safe but quiet. Acceptable for ambient/breakdown sections.' },
    { range: '-12 to -6 dB', label: 'Nominal', color: '#00ff88', bar: 55, desc: 'Ideal operating level. Most of your signal should live here.' },
    { range: '-6 to -3 dB', label: 'Hot', color: '#fbbf24', bar: 75, desc: 'Peaks are fine here. Sustained signal here means gain is too high.' },
    { range: '-3 to 0 dB', label: 'Danger', color: '#ff6600', bar: 90, desc: 'Only momentary transient peaks. Back off the gain.' },
    { range: '0 dB+', label: 'Clipping', color: '#ff3333', bar: 100, desc: 'Digital distortion. Always avoid. No headroom left.' },
  ];

  const proTips = [
    { title: 'Gain Before EQ', text: 'Always set your gain/trim FIRST, then shape with EQ. If you EQ first, you\'re chasing your tail.' },
    { title: 'Match Levels Visually', text: 'Both channels should hit the same peak level on the mixer meters before you start blending.' },
    { title: 'The -6dB Rule', text: 'When two tracks play simultaneously during a blend, each should be around -6dB to prevent clipping (+3dB from summing).' },
    { title: 'Check With Headphones', text: 'If it sounds distorted in your headphones but looks fine on the meters, trust your ears — the signal is probably too hot upstream.' },
    { title: 'Sound Check Ritual', text: 'Play your loudest track. Set gain so meters peak in yellow. Set master to 0dB. Don\'t touch master again all night.' },
    { title: 'Leave Headroom', text: 'Aim for peaks at -6dBFS in your DJ software. The next 6dB is your safety net for the PA system.' },
  ];

  return (
    <div data-testid="gain-staging-guide" style={{ padding: '1.5rem' }}>
      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            style={{
              flex: 1, padding: '8px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              background: activeSection === s.id ? 'rgba(255,51,51,0.2)' : 'transparent',
              border: activeSection === s.id ? '1px solid rgba(255,51,51,0.3)' : '1px solid transparent',
              color: activeSection === s.id ? '#ff3333' : '#666',
            }}>{s.label}</button>
        ))}
      </div>

      {/* Signal Flow */}
      {activeSection === 'signal' && (
        <div>
          {signalChain.map((stage, i) => (
            <div key={i} data-testid={`signal-stage-${i}`} style={{ marginBottom: '8px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
                background: 'rgba(255,255,255,0.02)', border: `1px solid ${stage.color}20`,
                borderLeft: `3px solid ${stage.color}`, borderRadius: '8px',
              }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${stage.color}18`, border: `1px solid ${stage.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, color: stage.color, flexShrink: 0 }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{stage.name}</div>
                  <div style={{ fontSize: '10px', color: '#888' }}>{stage.detail}</div>
                  <div style={{ fontSize: '11px', color: stage.color, fontWeight: 600, marginTop: '2px' }}>{stage.level}</div>
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '3px', lineHeight: 1.4 }}>{stage.tip}</div>
                </div>
              </div>
              {i < signalChain.length - 1 && (
                <div style={{ textAlign: 'center', color: '#333', fontSize: '16px', lineHeight: '20px' }}>↓</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Level Guide */}
      {activeSection === 'levels' && (
        <div>
          {levelGuide.map((level, i) => (
            <div key={i} data-testid={`level-${i}`} style={{ marginBottom: '8px', padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: level.color, fontFamily: 'monospace' }}>{level.range}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: level.color }}>{level.label}</span>
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginBottom: '4px' }}>
                <div style={{ height: '100%', width: `${level.bar}%`, background: level.color, borderRadius: '3px', transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: '10px', color: '#888' }}>{level.desc}</div>
            </div>
          ))}
        </div>
      )}

      {/* Pro Tips */}
      {activeSection === 'tips' && (
        <div>
          {proTips.map((tip, i) => (
            <div key={i} data-testid={`pro-tip-${i}`} style={{
              marginBottom: '8px', padding: '12px',
              background: 'rgba(255,255,255,0.02)', borderRadius: '8px',
              borderLeft: '3px solid rgba(255,51,51,0.3)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>{tip.title}</div>
              <div style={{ fontSize: '11px', color: '#999', lineHeight: 1.5 }}>{tip.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════
// PRODUCER TOOLS
// ═══════════════════════════════════════════════════

const CHORD_DATA = {
  electronic: [
    { name: 'Classic Trance Gate', chords: ['Am', 'F', 'C', 'G'], genre: 'Trance', mood: 'Euphoric' },
    { name: 'Epic Trance Rise', chords: ['Am', 'G', 'F', 'G'], genre: 'Trance', mood: 'Euphoric' },
    { name: 'The Avicii Anthem', chords: ['C', 'G', 'Am', 'F'], genre: 'EDM', mood: 'Uplifting' },
    { name: 'Festival Banger', chords: ['Fm', 'Ab', 'Eb', 'Bb'], genre: 'Big Room', mood: 'Energetic' },
    { name: 'Classic House', chords: ['Am', 'G', 'F', 'E'], genre: 'House', mood: 'Groovy' },
    { name: 'Deep House Groove', chords: ['Dm7', 'G7', 'Cmaj7', 'Am7'], genre: 'Deep House', mood: 'Smooth' },
    { name: 'Afro House Groove', chords: ['Cm', 'Fm', 'Bb', 'Eb'], genre: 'Afro House', mood: 'Groovy' },
    { name: 'Disco House Funk', chords: ['Dm7', 'G7', 'Em7', 'Am7'], genre: 'Disco House', mood: 'Funky' },
    { name: 'Garage Swing', chords: ['Dm7', 'Bbmaj7', 'Am7', 'Gm7'], genre: 'UK Garage', mood: 'Groovy' },
    { name: 'Acid House Riff', chords: ['Em', 'G', 'Am', 'B7'], genre: 'Acid House', mood: 'Raw' },
    { name: 'Melodic Techno', chords: ['Am', 'Dm', 'Em', 'Am'], genre: 'Melodic Techno', mood: 'Driving' },
    { name: 'Dark Melodic', chords: ['Em', 'Bm', 'C', 'Am'], genre: 'Melodic Techno', mood: 'Dark' },
    { name: 'Dark Techno Drive', chords: ['Em', 'Cm', 'Gm', 'Dm'], genre: 'Techno', mood: 'Dark' },
    { name: 'Minimal Techno Loop', chords: ['Am', 'Am/G', 'Fmaj7', 'E7'], genre: 'Minimal', mood: 'Hypnotic' },
    { name: 'Progressive Builder', chords: ['Am', 'Em', 'F', 'G'], genre: 'Progressive', mood: 'Building' },
    { name: 'Future Bass Feels', chords: ['Fmaj7', 'Am7', 'Dm9', 'G7'], genre: 'Future Bass', mood: 'Emotional' },
    { name: 'Melodic Dubstep', chords: ['Bbmaj7', 'Gm7', 'Eb', 'F'], genre: 'Dubstep', mood: 'Cinematic' },
    { name: 'Drum & Bass Anthem', chords: ['Cm', 'Ab', 'Eb', 'Bb'], genre: 'DnB', mood: 'Energetic' },
  ]
};

// Chord name to actual notes for Tone.js playback
const CHORD_TO_NOTES = {
  'C': ['C4','E4','G4'], 'Cm': ['C4','Eb4','G4'], 'Cmaj7': ['C4','E4','G4','B4'], 'Cmaj9': ['C4','E4','G4','B4','D5'],
  'C#': ['C#4','F4','G#4'], 'Db': ['Db4','F4','Ab4'],
  'D': ['D4','F#4','A4'], 'Dm': ['D4','F4','A4'], 'Dm7': ['D4','F4','A4','C5'], 'Dm9': ['D4','F4','A4','C5','E5'],
  'Eb': ['Eb4','G4','Bb4'], 'E': ['E4','G#4','B4'], 'Em': ['E4','G4','B4'], 'Em7': ['E4','G4','B4','D5'], 'E7': ['E4','G#4','B4','D5'],
  'F': ['F4','A4','C5'], 'Fm': ['F4','Ab4','C5'], 'Fmaj7': ['F4','A4','C5','E5'], 'Fmaj9': ['F4','A4','C5','E5','G5'],
  'F#': ['F#4','A#4','C#5'], 'Gb': ['Gb4','Bb4','Db5'],
  'G': ['G4','B4','D5'], 'Gm': ['G4','Bb4','D5'], 'Gm7': ['G4','Bb4','D5','F5'], 'G7': ['G4','B4','D5','F5'], 'Gsus4': ['G4','C5','D5'],
  'Ab': ['Ab4','C5','Eb5'], 'A': ['A4','C#5','E5'], 'Am': ['A4','C5','E5'], 'Am7': ['A4','C5','E5','G5'], 'Am11': ['A4','C5','E5','G5','D5'],
  'Am/G': ['G3','A4','C5','E5'],
  'Bb': ['Bb4','D5','F5'], 'Bbmaj7': ['Bb4','D5','F5','A5'],
  'B': ['B4','D#5','F#5'], 'Bm': ['B4','D5','F#5'], 'B7': ['B4','D#5','F#5','A5'],
};

// Chord substitution map for generating variations
const CHORD_SUBS = {
  'C': ['Cmaj7', 'Am', 'Em'], 'Cm': ['Cm7', 'Eb', 'Ab'], 'Cmaj7': ['C', 'Am7', 'Em7'], 'Cmaj9': ['Cmaj7', 'Am7'],
  'D': ['Dm', 'Bm', 'F#m'], 'Dm': ['Dm7', 'F', 'Am'], 'Dm7': ['Dm', 'Fmaj7', 'Am7'], 'Dm9': ['Dm7', 'Fmaj7'],
  'E': ['Em', 'E7', 'C#m'], 'Em': ['Em7', 'G', 'C'], 'Em7': ['Em', 'G', 'Cmaj7'], 'E7': ['Em', 'E', 'G'],
  'F': ['Fmaj7', 'Dm', 'Am'], 'Fm': ['Ab', 'Cm', 'Fm7'], 'Fmaj7': ['F', 'Dm7', 'Am7'], 'Fmaj9': ['Fmaj7', 'Dm7'],
  'G': ['G7', 'Em', 'Bm'], 'Gm': ['Gm7', 'Bb', 'Eb'], 'Gm7': ['Gm', 'Bbmaj7', 'Dm7'], 'G7': ['G', 'Em7', 'Bm'],
  'A': ['Am', 'A7', 'F#m'], 'Am': ['Am7', 'C', 'Dm'], 'Am7': ['Am', 'Cmaj7', 'Fmaj7'], 'Am11': ['Am7', 'Dm7'],
  'Am/G': ['Am7', 'Cmaj7', 'Fmaj7'],
  'Bb': ['Bbmaj7', 'Gm', 'Dm'], 'Bbmaj7': ['Bb', 'Gm7', 'Dm7'],
  'B': ['Bm', 'B7', 'G#m'], 'Bm': ['Bm7', 'D', 'G'], 'B7': ['Bm', 'Em', 'G'],
  'Ab': ['Abmaj7', 'Fm', 'Cm'], 'Eb': ['Ebmaj7', 'Cm', 'Gm'],
  'Gsus4': ['G', 'G7', 'Em'],
};

const generateVariation = (chords) => {
  const newChords = [...chords];
  const swapIdx = Math.floor(Math.random() * chords.length);
  const original = chords[swapIdx];
  const subs = CHORD_SUBS[original];
  if (subs && subs.length > 0) {
    const validSubs = subs.filter(s => CHORD_TO_NOTES[s] && s !== original);
    if (validSubs.length > 0) {
      newChords[swapIdx] = validSubs[Math.floor(Math.random() * validSubs.length)];
    }
  }
  return newChords;
};

const NOTE_SEMITONES = { 'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11 };

const noteToFreq = (note) => {
  const match = note.match(/^([A-G][#b]?)(\d)$/);
  if (!match) return 440;
  const midi = (parseInt(match[2]) + 1) * 12 + NOTE_SEMITONES[match[1]];
  return 440 * Math.pow(2, (midi - 69) / 12);
};

const SYNTH_PRESETS = {
  sine: { name: 'Sine', layers: [{ oscType: 'sine', detune: 0, vol: 0.15 }], attack: 0.01, release: 0.03 },
  saw: { name: 'Saw', layers: [{ oscType: 'sawtooth', detune: 0, vol: 0.06 }], attack: 0.03, release: 0.04 },
  triangle: { name: 'Triangle', layers: [{ oscType: 'triangle', detune: 0, vol: 0.12 }], attack: 0.02, release: 0.03 },
  square: { name: 'Square', layers: [{ oscType: 'square', detune: 0, vol: 0.05 }], attack: 0.005, release: 0.03 },
  supersaw: { name: 'Super Saw', layers: [
    { oscType: 'sawtooth', detune: -15, vol: 0.035 },
    { oscType: 'sawtooth', detune: 0, vol: 0.045 },
    { oscType: 'sawtooth', detune: 15, vol: 0.035 },
  ], attack: 0.02, release: 0.06 },
  organ: { name: 'Organ', layers: [
    { oscType: 'sine', detune: 0, vol: 0.10 },
    { oscType: 'sine', detune: 1200, vol: 0.05 },
  ], attack: 0.005, release: 0.02 },
  pluck: { name: 'Pluck', layers: [{ oscType: 'triangle', detune: 0, vol: 0.18 }], attack: 0.002, release: 0.01, decay: 0.25 },
  retro: { name: 'Retro Pad', layers: [
    { oscType: 'square', detune: -8, vol: 0.035 },
    { oscType: 'square', detune: 8, vol: 0.035 },
  ], attack: 0.04, release: 0.05 },
  strings: { name: 'Strings', layers: [
    { oscType: 'sawtooth', detune: -7, vol: 0.025 },
    { oscType: 'sawtooth', detune: 0, vol: 0.03 },
    { oscType: 'sawtooth', detune: 7, vol: 0.025 },
  ], attack: 0.08, release: 0.1 },
  bell: { name: 'FM Bell', layers: [{ oscType: 'sine', detune: 700, vol: 0.10 }], attack: 0.005, release: 0.05 },
  // ── Electronic Music Presets ──
  reese: { name: 'Reese Bass', layers: [
    { oscType: 'sawtooth', detune: -25, vol: 0.06 },
    { oscType: 'sawtooth', detune: 25, vol: 0.06 },
    { oscType: 'sine', detune: -1200, vol: 0.08 },
  ], attack: 0.01, release: 0.04 },
  acid: { name: 'Acid', layers: [
    { oscType: 'sawtooth', detune: 0, vol: 0.08 },
    { oscType: 'square', detune: 5, vol: 0.03 },
  ], attack: 0.002, release: 0.01, decay: 0.18 },
  hoover: { name: 'Hoover', layers: [
    { oscType: 'sawtooth', detune: -40, vol: 0.03 },
    { oscType: 'sawtooth', detune: -12, vol: 0.035 },
    { oscType: 'sawtooth', detune: 12, vol: 0.035 },
    { oscType: 'sawtooth', detune: 40, vol: 0.03 },
  ], attack: 0.01, release: 0.06 },
  sub: { name: 'Sub Bass', layers: [
    { oscType: 'sine', detune: -1200, vol: 0.20 },
    { oscType: 'sine', detune: 0, vol: 0.04 },
  ], attack: 0.01, release: 0.04 },
  trance: { name: 'Trance Lead', layers: [
    { oscType: 'sawtooth', detune: -10, vol: 0.04 },
    { oscType: 'sawtooth', detune: 10, vol: 0.04 },
    { oscType: 'square', detune: 1200, vol: 0.015 },
  ], attack: 0.03, release: 0.08 },
  stab: { name: 'Stab', layers: [
    { oscType: 'sawtooth', detune: -5, vol: 0.05 },
    { oscType: 'sawtooth', detune: 5, vol: 0.05 },
    { oscType: 'square', detune: 0, vol: 0.03 },
  ], attack: 0.001, release: 0.005, decay: 0.12 },
  ambient: { name: 'Ambient Pad', layers: [
    { oscType: 'sine', detune: -5, vol: 0.06 },
    { oscType: 'triangle', detune: 5, vol: 0.05 },
    { oscType: 'sine', detune: 1200, vol: 0.02 },
  ], attack: 0.15, release: 0.2 },
  wobble: { name: 'Wobble', layers: [
    { oscType: 'sawtooth', detune: -30, vol: 0.04 },
    { oscType: 'sawtooth', detune: 30, vol: 0.04 },
    { oscType: 'square', detune: -1200, vol: 0.03 },
  ], attack: 0.01, release: 0.03, decay: 0.3 },
};

const BPM_PRESETS = [90, 120, 125, 128, 130, 140, 174];

const ChordProgressions = () => {
  const [activeChord, setActiveChord] = useState(null);
  const [filterMood, setFilterMood] = useState('All');
  const [playingChord, setPlayingChord] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [barCount, setBarCount] = useState(4);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [currentPassLabel, setCurrentPassLabel] = useState(null);
  const [variations, setVariations] = useState([]);
  const [activeVariation, setActiveVariation] = useState(null);
  const [bpm, setBpm] = useState(120);
  const [synthType, setSynthType] = useState('sine');
  const synthRef = useRef(null);
  const activeNodesRef = useRef([]);
  const stopRef = useRef(false);
  const loopRef = useRef(false);
  const bpmRef = useRef(120);
  const synthTypeRef = useRef('sine');

  // --- Notepad state ---
  const [savedNotes, setSavedNotes] = useState([]);
  const [showNotepad, setShowNotepad] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [copiedNoteId, setCopiedNoteId] = useState(null);
  const [downloadingNoteId, setDownloadingNoteId] = useState(null);
  const [chordExpanded, setChordExpanded] = useState(true);

  const getToken = () => localStorage.getItem('beatseek_token');

  const fetchSavedNotes = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tools/chord-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setSavedNotes(json.notes || []);
      }
    } catch (e) { console.error('Failed to fetch chord notes:', e); }
  }, []);

  const saveToNotes = async (chords, sourceName) => {
    const token = getToken();
    if (!token) { setSaveMsg('Log in to save notes'); setTimeout(() => setSaveMsg(null), 2000); return; }
    setSavingNote(true);
    try {
      const prog = CHORD_DATA.electronic.find(p => p.chords.join(',') === chords.join(','));
      const res = await fetch(`${BACKEND_URL}/api/tools/chord-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: prog ? prog.name : `${sourceName || 'Variation'}`,
          chords,
          key_sig: prog ? `${prog.genre} / ${prog.mood}` : '',
          bpm: bpmRef.current,
          synth_preset: synthTypeRef.current,
          source: sourceName || 'manual',
          notes: '',
        }),
      });
      if (res.ok) {
        setSaveMsg('Saved!');
        fetchSavedNotes();
      } else { setSaveMsg('Save failed'); }
    } catch (e) { setSaveMsg('Save failed'); }
    setSavingNote(false);
    setTimeout(() => setSaveMsg(null), 1800);
  };

  const deleteNote = async (noteId) => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/tools/chord-notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) { console.error('Delete note failed:', e); }
  };

  const updateNote = async (noteId) => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tools/chord-notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: editTitle, notes: editText }),
      });
      if (res.ok) {
        setSavedNotes(prev => prev.map(n => n.id === noteId ? { ...n, title: editTitle, notes: editText } : n));
        setEditingNoteId(null);
      }
    } catch (e) { console.error('Update note failed:', e); }
  };

  useEffect(() => { fetchSavedNotes(); }, [fetchSavedNotes]);

  useEffect(() => { loopRef.current = loopEnabled; }, [loopEnabled]);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { synthTypeRef.current = synthType; }, [synthType]);

  useEffect(() => {
    return () => {
      stopRef.current = true;
      killAllNotes();
    };
  }, []);

  const moods = ['All', ...new Set(CHORD_DATA.electronic.map(p => p.mood))];
  const filtered = filterMood === 'All' ? CHORD_DATA.electronic : CHORD_DATA.electronic.filter(p => p.mood === filterMood);

  const getAudioCtx = () => {
    if (!synthRef.current || synthRef.current.state === 'closed') {
      synthRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (synthRef.current.state === 'suspended') {
      synthRef.current.resume();
    }
    return synthRef.current;
  };

  const startNotes = (noteNames) => {
    const ctx = getAudioCtx();
    const preset = SYNTH_PRESETS[synthTypeRef.current] || SYNTH_PRESETS.sine;
    const now = ctx.currentTime;
    const allNodes = [];

    noteNames.forEach(noteName => {
      const freq = noteToFreq(noteName);
      preset.layers.forEach(layer => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = layer.oscType;
        osc.frequency.value = freq;
        if (layer.detune) osc.detune.value = layer.detune;
        gain.gain.setValueAtTime(0, now);
        if (preset.decay) {
          // Pluck style: quick peak then fast decay
          gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack);
          gain.gain.exponentialRampToValueAtTime(0.001, now + preset.attack + preset.decay);
        } else {
          gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack);
        }
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        allNodes.push({ osc, gain });
      });
    });

    activeNodesRef.current = allNodes;
    return allNodes;
  };

  const fadeOutNodes = (nodes) => {
    if (!nodes || !nodes.length) return;
    const ctx = synthRef.current;
    if (!ctx || ctx.state === 'closed') return;
    const preset = SYNTH_PRESETS[synthTypeRef.current] || SYNTH_PRESETS.sine;
    const now = ctx.currentTime;
    nodes.forEach(({ osc, gain }) => {
      try {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + preset.release);
        osc.stop(now + preset.release + 0.01);
      } catch(e) {}
    });
  };

  const killAllNotes = () => {
    fadeOutNodes(activeNodesRef.current);
    activeNodesRef.current = [];
  };

  const stopPlayback = () => {
    stopRef.current = true;
    setIsPlaying(false);
    setPlayingChord(null);
    setCurrentPassLabel(null);
    setActiveVariation(null);
    killAllNotes();
  };

  const playChordSequence = async (chords, progIdx) => {
    for (let ci = 0; ci < chords.length; ci++) {
      if (stopRef.current) return false;
      const notes = CHORD_TO_NOTES[chords[ci]];
      if (!notes) continue;
      const msPerBeat = 60000 / bpmRef.current;
      const holdTime = msPerBeat * 0.85;

      killAllNotes();
      setPlayingChord(`${progIdx}-${ci % 4}`);
      const nodes = startNotes(notes);

      await new Promise(r => setTimeout(r, holdTime));
      if (stopRef.current) { fadeOutNodes(nodes); return false; }

      fadeOutNodes(nodes);
      activeNodesRef.current = [];
      await new Promise(r => setTimeout(r, msPerBeat - holdTime));
      if (stopRef.current) return false;
    }
    return true;
  };

  const playChord = async (chordName, progIdx, chordIdx) => {
    try {
      stopRef.current = true;
      setIsPlaying(false);
      setCurrentPassLabel(null);
      stopRef.current = false;
      killAllNotes();
      const notes = CHORD_TO_NOTES[chordName];
      if (!notes) return;
      setPlayingChord(`${progIdx}-${chordIdx}`);
      const nodes = startNotes(notes);
      setTimeout(() => {
        fadeOutNodes(nodes);
        activeNodesRef.current = [];
        setPlayingChord(null);
      }, 500);
    } catch (e) {
      console.error('Chord play error:', e);
    }
  };

  const playProgression = async (prog, progIdx) => {
    try {
      stopRef.current = false;
      killAllNotes();
      setIsPlaying(true);
      setVariations([]);
      const chords = barCount === 8 ? [...prog.chords, ...prog.chords] : prog.chords;
      do {
        setCurrentPassLabel(barCount === 8 ? 'Playing (8 bars)' : 'Playing');
        const completed = await playChordSequence(chords, progIdx);
        if (!completed || stopRef.current) break;
      } while (loopRef.current && !stopRef.current);
      if (!stopRef.current) {
        setPlayingChord(null);
        setIsPlaying(false);
        setCurrentPassLabel(null);
      }
    } catch (e) {
      console.error('Progression play error:', e);
      setIsPlaying(false);
    }
  };

  const playInspire = async (prog, progIdx) => {
    try {
      stopRef.current = false;
      killAllNotes();
      setIsPlaying(true);
      const vars = [
        { label: 'Original', chords: [...prog.chords] },
        { label: 'Variation 1', chords: generateVariation(prog.chords) },
        { label: 'Variation 2', chords: generateVariation(prog.chords) },
        { label: 'Variation 3', chords: generateVariation(prog.chords) },
      ];
      setVariations(vars);

      for (let vi = 0; vi < vars.length; vi++) {
        if (stopRef.current) break;
        setCurrentPassLabel(vars[vi].label);
        setActiveVariation(vi);
        const chords = barCount === 8 ? [...vars[vi].chords, ...vars[vi].chords] : vars[vi].chords;
        const completed = await playChordSequence(chords, progIdx);
        if (!completed || stopRef.current) break;
        await new Promise(r => setTimeout(r, 300));
      }
      if (!stopRef.current) {
        setPlayingChord(null);
        setIsPlaying(false);
        setCurrentPassLabel('Done — tap any variation to replay');
        setActiveVariation(null);
      }
    } catch (e) {
      console.error('Inspire play error:', e);
      setIsPlaying(false);
    }
  };

  const replayVariation = async (varChords, progIdx, varIdx) => {
    try {
      stopRef.current = false;
      killAllNotes();
      setIsPlaying(true);
      setActiveVariation(varIdx);
      setCurrentPassLabel(variations[varIdx]?.label || `Variation ${varIdx}`);
      const chords = barCount === 8 ? [...varChords, ...varChords] : varChords;
      do {
        await playChordSequence(chords, progIdx);
        if (stopRef.current) break;
      } while (loopRef.current && !stopRef.current);
      if (!stopRef.current) {
        setPlayingChord(null);
        setIsPlaying(false);
        setCurrentPassLabel('Done — tap any variation to replay');
        setActiveVariation(null);
      }
    } catch (e) {
      console.error('Replay error:', e);
      setIsPlaying(false);
    }
  };

  return (
    <div data-testid="chord-progressions-tool" style={{ padding: '1.5rem' }}>

      {/* Beat Analyzer - upload a clip for AI chord suggestions */}
      <BeatAnalyzer toolContext="chord_progressions" />

      {/* Collapsible Chord Progressions */}
      <div style={{ marginTop: '10px', borderRadius: '10px', border: '1px solid rgba(6,182,212,0.2)', background: 'rgba(6,182,212,0.03)', overflow: 'hidden' }}>
        <div
          data-testid="chord-progressions-toggle"
          onClick={() => setChordExpanded(!chordExpanded)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '10px 14px', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Music size={13} style={{ color: '#06b6d4', filter: 'drop-shadow(0 0 4px rgba(6,182,212,0.4))' }} />
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#06b6d4' }}>Chord Progressions</span>
            {!chordExpanded && activeChord && (
              <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(6,182,212,0.1)', color: 'rgba(6,182,212,0.7)' }}>
                {activeChord}
              </span>
            )}
          </div>
          {chordExpanded ? <ChevronUp size={13} style={{ color: '#555' }} /> : <ChevronDown size={13} style={{ color: '#555' }} />}
        </div>

        {chordExpanded && (
          <div style={{ padding: '0 14px 14px', borderTop: '1px solid rgba(6,182,212,0.08)' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', margin: '12px 0' }}>Tap a chord to hear it, play the full progression, or get inspired. <span style={{ color: '#06b6d4' }}>Notepad at the bottom to save your ideas!</span></p>

      {/* Controls bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1rem' }}>
        {/* Row 1: Bars + Loop */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
            {[4, 8].map(b => (
              <button key={b} onClick={() => setBarCount(b)} data-testid={`bar-count-${b}`} style={{
                padding: '5px 14px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: barCount === b ? 'rgba(0,255,255,0.15)' : 'transparent',
                color: barCount === b ? '#00ffff' : '#666', transition: 'all 0.2s',
              }}>{b} bars</button>
            ))}
          </div>
          <button onClick={() => setLoopEnabled(!loopEnabled)} data-testid="loop-toggle" style={{
            padding: '5px 14px', fontSize: '11px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
            border: loopEnabled ? '1px solid rgba(0,255,136,0.4)' : '1px solid rgba(255,255,255,0.08)',
            background: loopEnabled ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.03)',
            color: loopEnabled ? '#00ff88' : '#666', transition: 'all 0.2s',
          }}>{'\u{1F501}'} Loop {loopEnabled ? 'ON' : 'OFF'}</button>
        </div>

        {/* Row 2: BPM */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginRight: '2px' }}>BPM</span>
          <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
            {BPM_PRESETS.map(b => (
              <button key={b} onClick={() => setBpm(b)} data-testid={`bpm-${b}`} style={{
                padding: '4px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: bpm === b ? 'rgba(255,170,0,0.15)' : 'transparent',
                color: bpm === b ? '#ffaa00' : '#555', transition: 'all 0.2s', minWidth: '32px',
              }}>{b}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <button onClick={() => setBpm(prev => Math.max(60, prev - 1))} style={{
              width: '22px', height: '22px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '12px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>-</button>
            <input
              data-testid="bpm-input"
              type="number" min="60" max="200" value={bpm}
              onChange={(e) => { const v = parseInt(e.target.value); if (v >= 60 && v <= 200) setBpm(v); }}
              style={{
                width: '44px', padding: '3px 4px', fontSize: '12px', fontWeight: 700, textAlign: 'center',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px', color: '#ffaa00', outline: 'none',
              }}
            />
            <button onClick={() => setBpm(prev => Math.min(200, prev + 1))} style={{
              width: '22px', height: '22px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '12px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>+</button>
          </div>
        </div>

        {/* Row 3: Synth sound */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginRight: '2px' }}>Sound</span>
          <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
            {Object.entries(SYNTH_PRESETS).map(([key, preset]) => (
              <button key={key} onClick={() => { setSynthType(key); killAllNotes(); }} data-testid={`synth-${key}`} style={{
                padding: '4px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: synthType === key ? 'rgba(255,0,255,0.15)' : 'transparent',
                color: synthType === key ? '#ff66ff' : '#555', transition: 'all 0.2s',
              }}>{preset.name}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Mood filter */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {moods.map(m => (
          <button
            key={m}
            onClick={() => setFilterMood(m)}
            data-testid={`chord-mood-${m.toLowerCase()}`}
            style={{
              padding: '5px 14px', fontSize: '11px', fontWeight: 600, borderRadius: '16px', cursor: 'pointer', border: 'none',
              background: filterMood === m ? 'rgba(255,0,255,0.25)' : 'rgba(255,255,255,0.04)',
              color: filterMood === m ? '#ff00ff' : '#888',
              transition: 'all 0.2s',
            }}
          >{m}</button>
        ))}
      </div>

      {/* Progression cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map((prog, i) => {
          const isActive = activeChord === i;
          return (
            <div
              key={i}
              data-testid={`chord-prog-${i}`}
              style={{
                padding: '14px 16px', borderRadius: '10px', transition: 'all 0.25s',
                background: isActive ? 'rgba(255,0,255,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isActive ? 'rgba(255,0,255,0.3)' : 'rgba(255,255,255,0.06)'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer' }} onClick={() => { setActiveChord(isActive ? null : i); setVariations([]); setCurrentPassLabel(null); }}>{prog.name}</span>
                  {currentPassLabel && isPlaying && playingChord?.startsWith(`${i}-`) && (
                    <span data-testid={`pass-label-${i}`} style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 10px', borderRadius: '10px',
                      animation: 'passFlash 0.3s ease-out',
                      background: currentPassLabel.startsWith('Variation') ? 'rgba(255,170,0,0.15)' :
                                 currentPassLabel === 'Original' ? 'rgba(0,255,255,0.15)' : 'rgba(255,0,255,0.12)',
                      color: currentPassLabel.startsWith('Variation') ? '#ffaa00' :
                             currentPassLabel === 'Original' ? '#00ffff' : '#ff66ff',
                    }}>{currentPassLabel}</span>
                  )}
                  {currentPassLabel && !isPlaying && currentPassLabel.startsWith('Done') && variations.length > 0 && activeChord === i && (
                    <span style={{ fontSize: '10px', fontWeight: 600, color: '#666' }}>{currentPassLabel}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {isPlaying && playingChord?.startsWith(`${i}-`) ? (
                    <button onClick={(e) => { e.stopPropagation(); stopPlayback(); }} data-testid={`chord-stop-${i}`} style={{
                      padding: '3px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '12px', cursor: 'pointer', border: 'none',
                      background: 'rgba(255,51,51,0.2)', color: '#ff5555', transition: 'all 0.2s',
                    }}>{'\u25A0'} Stop</button>
                  ) : (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); playProgression(prog, i); }} data-testid={`chord-play-${i}`} style={{
                        padding: '3px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '12px', cursor: 'pointer', border: 'none',
                        background: 'rgba(255,0,255,0.15)', color: '#ff66ff', transition: 'all 0.2s',
                      }}>{'\u25B6'} Play</button>
                      <button onClick={(e) => { e.stopPropagation(); playInspire(prog, i); }} data-testid={`chord-inspire-${i}`} style={{
                        padding: '3px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '12px', cursor: 'pointer', border: 'none',
                        background: 'rgba(255,170,0,0.12)', color: '#ffaa00', transition: 'all 0.2s',
                      }}>{'\u{1F3B2}'} Inspire</button>
                    </>
                  )}
                  <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, background: 'rgba(255,0,255,0.12)', color: '#ff00ff' }}>{prog.genre}</span>
                  <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '6px', fontWeight: 600, background: 'rgba(255,255,255,0.06)', color: '#aaa' }}>{prog.mood}</span>
                  <button
                    data-testid={`chord-save-prog-${i}`}
                    onClick={(e) => { e.stopPropagation(); saveToNotes(prog.chords, prog.name); }}
                    disabled={savingNote}
                    title="Save to Notepad"
                    style={{
                      padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                      border: '1px solid rgba(0,255,136,0.2)', background: 'rgba(0,255,136,0.06)',
                      color: '#00ff88', transition: 'all 0.2s',
                    }}
                  >{savingNote ? '...' : '+ Save'}</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {prog.chords.map((chord, ci) => {
                  const chordPlaying = playingChord === `${i}-${ci}`;
                  return (
                    <div key={ci} onClick={(e) => { e.stopPropagation(); playChord(chord, i, ci); }} style={{
                      flex: 1, textAlign: 'center', padding: '10px 6px', borderRadius: '8px', cursor: 'pointer',
                      background: chordPlaying ? 'rgba(255,0,255,0.25)' : isActive ? `rgba(255,0,255,${0.08 + ci * 0.04})` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${chordPlaying ? 'rgba(255,0,255,0.5)' : isActive ? 'rgba(255,0,255,0.2)' : 'rgba(255,255,255,0.06)'}`,
                      transition: 'all 0.15s',
                      transform: chordPlaying ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: chordPlaying ? '0 0 12px rgba(255,0,255,0.3)' : 'none',
                    }}>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: chordPlaying ? '#ff88ff' : isActive ? '#ff66ff' : '#ccc', letterSpacing: '0.5px' }}>{chord}</div>
                      <div style={{ fontSize: '9px', color: '#555', marginTop: '3px' }}>{ci + 1}</div>
                    </div>
                  );
                })}
              </div>

              {/* Variations list (after Inspire) */}
              {variations.length > 0 && activeChord === i && (
                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Inspiration Results</div>
                  {variations.map((v, vi) => (
                    <div
                      key={vi}
                      data-testid={`variation-${vi}`}
                      onClick={(e) => { e.stopPropagation(); replayVariation(v.chords, i, vi); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '4px',
                        borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
                        background: activeVariation === vi ? 'rgba(255,170,0,0.1)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${activeVariation === vi ? 'rgba(255,170,0,0.3)' : 'transparent'}`,
                      }}
                      onMouseEnter={(e) => { if (activeVariation !== vi) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={(e) => { if (activeVariation !== vi) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                    >
                      <span style={{
                        fontSize: '10px', fontWeight: 700, minWidth: '70px',
                        color: vi === 0 ? '#00ffff' : '#ffaa00',
                      }}>{v.label}</span>
                      <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                        {v.chords.map((c, ci) => {
                          const changed = vi > 0 && c !== variations[0].chords[ci];
                          return (
                            <span key={ci} style={{
                              fontSize: '13px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px',
                              background: changed ? 'rgba(255,170,0,0.15)' : 'rgba(255,255,255,0.04)',
                              color: changed ? '#ffcc00' : '#aaa',
                              border: changed ? '1px solid rgba(255,170,0,0.3)' : '1px solid transparent',
                            }}>{c}</span>
                          );
                        })}
                      </div>
                      <button
                        data-testid={`save-variation-${vi}`}
                        onClick={(e) => { e.stopPropagation(); saveToNotes(v.chords, v.label); }}
                        disabled={savingNote}
                        title="Save to Notepad"
                        style={{
                          padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                          border: '1px solid rgba(0,255,136,0.25)', background: 'rgba(0,255,136,0.08)',
                          color: '#00ff88', transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                      >{savingNote ? '...' : '+ Save'}</button>
                      <span style={{ fontSize: '16px', opacity: 0.4 }}>{'\u25B6'}</span>
                    </div>
                  ))}
                  {saveMsg && (
                    <div data-testid="chord-save-feedback" style={{
                      textAlign: 'center', fontSize: '11px', fontWeight: 700, padding: '6px',
                      color: saveMsg === 'Saved!' ? '#00ff88' : '#ff5555', marginTop: '4px',
                    }}>{saveMsg}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '10px', color: '#444', fontStyle: 'italic' }}>
        Tap a chord to hear it — Play to hear the progression — Inspire for random variations
      </div>

      {/* ─── CHORD NOTEPAD ─── */}
      <div data-testid="chord-notepad-section" style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '20px' }}>
        <div
          data-testid="chord-notepad-toggle"
          onClick={() => setShowNotepad(!showNotepad)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
            padding: '10px 14px', borderRadius: '10px', transition: 'all 0.2s',
            background: showNotepad ? 'rgba(0,255,136,0.06)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${showNotepad ? 'rgba(0,255,136,0.2)' : 'rgba(255,255,255,0.06)'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>{'\u{1F4D3}'}</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>Chord Notepad</span>
            {savedNotes.length > 0 && (
              <span style={{
                fontSize: '10px', fontWeight: 700, padding: '1px 8px', borderRadius: '10px',
                background: 'rgba(0,255,136,0.15)', color: '#00ff88',
              }}>{savedNotes.length}</span>
            )}
          </div>
          <span style={{ fontSize: '12px', color: '#666', transform: showNotepad ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>{'\u25BC'}</span>
        </div>

        {showNotepad && (
          <div data-testid="chord-notepad-list" style={{ marginTop: '10px' }}>
            {savedNotes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px', color: '#555', fontSize: '12px' }}>
                <p style={{ marginBottom: '6px', fontWeight: 600 }}>No saved chord progressions yet</p>
                <p style={{ fontSize: '11px', color: '#444' }}>Use Inspire, then tap "+ Save" on any variation you like</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {savedNotes.map((note) => (
                  <div
                    key={note.id}
                    data-testid={`chord-note-${note.id}`}
                    style={{
                      padding: '12px 14px', borderRadius: '10px', transition: 'all 0.2s',
                      background: 'rgba(255,255,255,0.025)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    {editingNoteId === note.id ? (
                      /* ── Edit Mode ── */
                      <div data-testid={`chord-note-edit-${note.id}`}>
                        <input
                          data-testid={`chord-note-title-input-${note.id}`}
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Title"
                          style={{
                            width: '100%', padding: '6px 10px', marginBottom: '6px', fontSize: '12px', fontWeight: 700,
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '6px', color: '#fff', outline: 'none',
                          }}
                        />
                        <textarea
                          data-testid={`chord-note-text-input-${note.id}`}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          placeholder="Add notes... (e.g., works great for drops, try with reese bass)"
                          rows={2}
                          style={{
                            width: '100%', padding: '6px 10px', fontSize: '11px', resize: 'vertical',
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '6px', color: '#ccc', outline: 'none', fontFamily: 'inherit',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                          <button data-testid={`chord-note-save-edit-${note.id}`} onClick={() => updateNote(note.id)} style={{
                            padding: '4px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                            border: 'none', background: 'rgba(0,255,136,0.15)', color: '#00ff88',
                          }}>Save</button>
                          <button data-testid={`chord-note-cancel-edit-${note.id}`} onClick={() => setEditingNoteId(null)} style={{
                            padding: '4px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                            border: 'none', background: 'rgba(255,255,255,0.06)', color: '#888',
                          }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* ── View Mode ── */
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>{note.title || 'Untitled'}</div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {note.bpm && <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(255,170,0,0.12)', color: '#ffaa00', fontWeight: 600 }}>{note.bpm} BPM</span>}
                              {note.synth_preset && <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(255,0,255,0.12)', color: '#ff66ff', fontWeight: 600 }}>{(SYNTH_PRESETS[note.synth_preset] || {}).name || note.synth_preset}</span>}
                              {note.source && note.source !== 'manual' && <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(0,255,255,0.1)', color: '#00cccc', fontWeight: 600 }}>{note.source}</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            <button
                              data-testid={`chord-note-copy-${note.id}`}
                              onClick={async () => {
                                await copyToClipboard(formatChordText(note));
                                setCopiedNoteId(note.id);
                                setTimeout(() => setCopiedNoteId(null), 1500);
                              }}
                              style={{ padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: copiedNoteId === note.id ? 'rgba(0,255,136,0.15)' : 'rgba(6,182,212,0.1)', color: copiedNoteId === note.id ? '#00ff88' : '#22d3ee', display: 'flex', alignItems: 'center', gap: '3px' }}
                            >{copiedNoteId === note.id ? <><Check size={9} /> Copied</> : <><Copy size={9} /> Copy</>}</button>
                            <button
                              data-testid={`chord-note-download-${note.id}`}
                              onClick={async () => {
                                setDownloadingNoteId(note.id);
                                const dataUrl = await generateShareImage(`chord-share-card-${note.id}`);
                                if (dataUrl) downloadImage(dataUrl, `beatseek-chords-${(note.title || 'progression').replace(/\s+/g, '-').toLowerCase()}.png`);
                                setDownloadingNoteId(null);
                              }}
                              style={{ padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'rgba(139,92,246,0.1)', color: '#a78bfa', display: 'flex', alignItems: 'center', gap: '3px' }}
                            ><Download size={9} /> {downloadingNoteId === note.id ? '...' : 'Image'}</button>
                            <button
                              data-testid={`chord-note-edit-btn-${note.id}`}
                              onClick={() => { setEditingNoteId(note.id); setEditTitle(note.title || ''); setEditText(note.notes || ''); }}
                              style={{ padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'rgba(255,255,255,0.06)', color: '#888' }}
                            >Edit</button>
                            <button
                              data-testid={`chord-note-delete-btn-${note.id}`}
                              onClick={() => deleteNote(note.id)}
                              style={{ padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'rgba(255,51,51,0.1)', color: '#ff5555' }}
                            >Del</button>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {(note.chords || []).map((c, ci) => (
                            <span key={ci} onClick={() => {
                              const notes = CHORD_TO_NOTES[c];
                              if (!notes) return;
                              killAllNotes();
                              const nodes = startNotes(notes);
                              setTimeout(() => { fadeOutNodes(nodes); activeNodesRef.current = []; }, 500);
                            }} style={{
                              fontSize: '13px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                              background: 'rgba(255,0,255,0.08)', border: '1px solid rgba(255,0,255,0.15)',
                              color: '#dd88ff', transition: 'all 0.15s',
                            }}>{c}</span>
                          ))}
                        </div>
                        {note.notes && <p style={{ fontSize: '11px', color: '#777', marginTop: '6px', fontStyle: 'italic', lineHeight: 1.4 }}>{note.notes}</p>}
                        <div style={{ fontSize: '9px', color: '#444', marginTop: '6px' }}>
                          {note.created_at ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

          </div>
        )}
      </div>

      {/* Hidden share cards for image export */}
      {savedNotes.map((note) => (
        <div key={`share-${note.id}`} id={`chord-share-card-${note.id}`} style={{ position: 'fixed', left: '-9999px', top: 0, width: '320px', padding: '3px', background: 'linear-gradient(135deg, #c026d3, #06b6d4)', borderRadius: '16px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
          <div style={{ background: '#0b1120', borderRadius: '14px', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
            {/* Ghost music notes background */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
              {[
                { ch: '\u266A', top: '8%', left: '8%', size: '28px', rot: '-15deg', op: 0.10 },
                { ch: '\u266B', top: '22%', right: '10%', size: '22px', rot: '20deg', op: 0.035 },
                { ch: '\u266C', top: '45%', left: '5%', size: '20px', rot: '10deg', op: 0.10 },
                { ch: '\u2669', top: '38%', right: '6%', size: '26px', rot: '-25deg', op: 0.10 },
                { ch: '\u266A', top: '62%', left: '12%', size: '18px', rot: '30deg', op: 0.035 },
                { ch: '\u266B', top: '70%', right: '14%', size: '24px', rot: '-10deg', op: 0.10 },
                { ch: '\u266C', top: '85%', left: '6%', size: '22px', rot: '15deg', op: 0.10 },
                { ch: '\u2669', top: '90%', right: '8%', size: '18px', rot: '-20deg', op: 0.035 },
                { ch: '\u266A', top: '15%', left: '75%', size: '16px', rot: '25deg', op: 0.10 },
                { ch: '\u266B', top: '55%', right: '75%', size: '20px', rot: '-5deg', op: 0.10 },
              ].map((n, i) => (
                <span key={i} style={{ position: 'absolute', top: n.top, left: n.left, right: n.right, fontSize: n.size, transform: `rotate(${n.rot})`, opacity: n.op, color: '#ffffff' }}>{n.ch}</span>
              ))}
            </div>

            {/* BEATSEEK header centered */}
            <div style={{ textAlign: 'center', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '50%', margin: '0 auto 8px', position: 'relative', background: 'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.15) 0%, transparent 50%), linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 25%, #1a1a1a 50%, #0a0a0a 75%, #1a1a1a 100%)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.9), 0 0 12px rgba(0,255,255,0.3), 0 0 24px rgba(255,0,255,0.15)' }}>
                <div style={{ position: 'absolute', inset: '5px', borderRadius: '50%', background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 1.5px, rgba(255,255,255,0.08) 1.5px, rgba(255,255,255,0.08) 2.5px)' }}></div>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '16px', height: '16px', borderRadius: '50%', background: 'linear-gradient(135deg, #00ffff, #ff00ff)', boxShadow: '0 0 8px rgba(0,255,255,0.5)' }}></div>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '5px', height: '5px', borderRadius: '50%', background: 'radial-gradient(circle at 30% 30%, #333, #000)', border: '1px solid rgba(255,255,255,0.4)' }}></div>
              </div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', letterSpacing: '3px' }}>BEATSEEK</div>
              <div style={{ fontSize: '9px', color: '#555', letterSpacing: '2px', marginTop: '2px', textTransform: 'uppercase' }}>Chord Progression</div>
            </div>

            {/* Title */}
            <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 700, color: '#ffffff', marginBottom: '16px', position: 'relative', zIndex: 1 }}>{note.title || 'Untitled'}</div>

            {/* Chords */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '18px', position: 'relative', zIndex: 1 }}>
              {(note.chords || []).map((c, ci) => (
                <span key={ci} style={{ fontSize: '15px', fontWeight: 700, padding: '8px 16px', borderRadius: '10px', background: 'rgba(192,38,211,0.12)', border: '1px solid rgba(192,38,211,0.3)', color: '#e879f9' }}>{c}</span>
              ))}
            </div>

            {/* BPM / Key badges */}
            {(note.bpm || note.key_bpm) && (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
                {note.bpm && <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '8px', background: 'rgba(255,170,0,0.1)', border: '1px solid rgba(255,170,0,0.2)', color: '#fbbf24', fontWeight: 600 }}>{note.bpm} BPM</span>}
                {note.key_bpm && <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '8px', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)', color: '#22d3ee', fontWeight: 600 }}>{note.key_bpm}</span>}
                {note.synth_preset && <span style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '8px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', color: '#a78bfa', fontWeight: 600 }}>{note.synth_preset}</span>}
              </div>
            )}

            {/* Notes */}
            {note.notes && <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.6, textAlign: 'center', marginBottom: '16px', padding: '0 12px', position: 'relative', zIndex: 1 }}>{note.notes}</div>}

            {/* Separator + footer */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600, letterSpacing: '1px' }}>beatseek.io</span>
              <span style={{ fontSize: '10px', color: '#475569' }}>{note.created_at ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
            </div>
            <div style={{ textAlign: 'center', paddingTop: '10px', position: 'relative', zIndex: 1 }}>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, letterSpacing: '0.5px' }}>thebeatseek.com</span>
            </div>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes passFlash { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════
// ENHANCED SCALE REFERENCE
// ═══════════════════════════════════════════════════

const CHROMATIC = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
const ENHARMONIC_MAP = { 'C#':'Db','D#':'Eb','F#':'Gb','G#':'Ab','A#':'Bb','Db':'C#','Eb':'D#','Gb':'F#','Ab':'G#','Bb':'A#' };

const SCALE_TYPES = {
  major:          { name: 'Major (Ionian)',   intervals: [0,2,4,5,7,9,11], intervalNames: ['Root','Maj 2','Maj 3','P4','P5','Maj 6','Maj 7'], category: 'core', chordQualities: ['maj','min','min','maj','maj','min','dim'] },
  natural_minor:  { name: 'Natural Minor',    intervals: [0,2,3,5,7,8,10], intervalNames: ['Root','Maj 2','min 3','P4','P5','min 6','min 7'], category: 'core', chordQualities: ['min','dim','maj','min','min','maj','maj'] },
  harmonic_minor: { name: 'Harmonic Minor',   intervals: [0,2,3,5,7,8,11], intervalNames: ['Root','Maj 2','min 3','P4','P5','min 6','Maj 7'], category: 'core', chordQualities: ['min','dim','aug','min','maj','maj','dim'] },
  melodic_minor:  { name: 'Melodic Minor',    intervals: [0,2,3,5,7,9,11], intervalNames: ['Root','Maj 2','min 3','P4','P5','Maj 6','Maj 7'], category: 'core', chordQualities: ['min','min','aug','maj','maj','dim','dim'] },
  dorian:         { name: 'Dorian',           intervals: [0,2,3,5,7,9,10], intervalNames: ['Root','Maj 2','min 3','P4','P5','Maj 6','min 7'], category: 'modes', chordQualities: ['min','min','maj','maj','min','dim','maj'] },
  phrygian:       { name: 'Phrygian',         intervals: [0,1,3,5,7,8,10], intervalNames: ['Root','min 2','min 3','P4','P5','min 6','min 7'], category: 'modes', chordQualities: ['min','maj','maj','min','dim','maj','min'] },
  lydian:         { name: 'Lydian',           intervals: [0,2,4,6,7,9,11], intervalNames: ['Root','Maj 2','Maj 3','Aug 4','P5','Maj 6','Maj 7'], category: 'modes', chordQualities: ['maj','maj','min','dim','maj','min','min'] },
  mixolydian:     { name: 'Mixolydian',       intervals: [0,2,4,5,7,9,10], intervalNames: ['Root','Maj 2','Maj 3','P4','P5','Maj 6','min 7'], category: 'modes', chordQualities: ['maj','min','dim','maj','min','min','maj'] },
  pent_major:     { name: 'Pentatonic Major', intervals: [0,2,4,7,9],       intervalNames: ['Root','Maj 2','Maj 3','P5','Maj 6'], category: 'pent', chordQualities: null },
  pent_minor:     { name: 'Pentatonic Minor', intervals: [0,3,5,7,10],      intervalNames: ['Root','min 3','P4','P5','min 7'], category: 'pent', chordQualities: null },
  blues:          { name: 'Blues',            intervals: [0,3,5,6,7,10],    intervalNames: ['Root','min 3','P4','b5','P5','min 7'], category: 'pent', chordQualities: null },
};

const SCALE_CATEGORIES = [
  { id: 'core', label: 'Core Scales' },
  { id: 'modes', label: 'Modes' },
  { id: 'pent', label: 'Pent / Blues' },
];

const ROMAN_MAJOR = ['I','ii','iii','IV','V','vi','vii\u00B0'];
const ROMAN_MINOR = ['i','ii\u00B0','III','iv','v','VI','VII'];

const getScaleNotes = (rootNote, intervals) => {
  const rootIdx = CHROMATIC.indexOf(rootNote);
  if (rootIdx === -1) return [];
  return intervals.map(i => CHROMATIC[(rootIdx + i) % 12]);
};

const getRelativeKey = (rootNote, scaleType) => {
  const rootIdx = CHROMATIC.indexOf(rootNote);
  if (scaleType === 'major') {
    const relIdx = (rootIdx + 9) % 12;
    return { note: CHROMATIC[relIdx], type: 'natural_minor', label: `${CHROMATIC[relIdx]} Minor` };
  }
  if (scaleType === 'natural_minor') {
    const relIdx = (rootIdx + 3) % 12;
    return { note: CHROMATIC[relIdx], type: 'major', label: `${CHROMATIC[relIdx]} Major` };
  }
  return null;
};

const getScaleChords = (rootNote, scaleType) => {
  const st = SCALE_TYPES[scaleType];
  if (!st || !st.chordQualities) return [];
  const notes = getScaleNotes(rootNote, st.intervals);
  const romans = scaleType.includes('minor') || scaleType === 'dorian' || scaleType === 'phrygian' ? ROMAN_MINOR : ROMAN_MAJOR;
  return notes.map((note, i) => {
    const q = st.chordQualities[i];
    const suffix = q === 'maj' ? '' : q === 'min' ? 'm' : q === 'dim' ? 'dim' : q === 'aug' ? 'aug' : '';
    return { roman: romans[i] || (i + 1).toString(), chord: `${note}${suffix}`, quality: q, root: note };
  });
};

// Piano key layout for 2 octaves
const PIANO_WHITE = [
  { note: 'C', oct: 4 }, { note: 'D', oct: 4 }, { note: 'E', oct: 4 }, { note: 'F', oct: 4 }, { note: 'G', oct: 4 }, { note: 'A', oct: 4 }, { note: 'B', oct: 4 },
  { note: 'C', oct: 5 }, { note: 'D', oct: 5 }, { note: 'E', oct: 5 }, { note: 'F', oct: 5 }, { note: 'G', oct: 5 }, { note: 'A', oct: 5 }, { note: 'B', oct: 5 },
];
const PIANO_BLACK = [
  { note: 'C#', oct: 4, pos: 0 }, { note: 'D#', oct: 4, pos: 1 },
  { note: 'F#', oct: 4, pos: 3 }, { note: 'G#', oct: 4, pos: 4 }, { note: 'A#', oct: 4, pos: 5 },
  { note: 'C#', oct: 5, pos: 7 }, { note: 'D#', oct: 5, pos: 8 },
  { note: 'F#', oct: 5, pos: 10 }, { note: 'G#', oct: 5, pos: 11 }, { note: 'A#', oct: 5, pos: 12 },
];

const isNoteInScale = (noteName, scaleNotes) => {
  return scaleNotes.some(sn => sn === noteName || ENHARMONIC_MAP[sn] === noteName || ENHARMONIC_MAP[noteName] === sn);
};

const isRootNote = (noteName, rootNote) => {
  return noteName === rootNote || ENHARMONIC_MAP[noteName] === rootNote || ENHARMONIC_MAP[rootNote] === noteName;
};

// ── PHASE 1: Electronic Genre Presets ──
const GENRE_PRESETS = [
  { id: 'classic-house', name: 'Classic House', root: 'A', scale: 'natural_minor', bpm: 122, synth: 'stab', color: '#ff6633' },
  { id: 'deep-house', name: 'Deep House', root: 'D', scale: 'dorian', bpm: 122, synth: 'reese', color: '#ff9933' },
  { id: 'tech-house', name: 'Tech House', root: 'G', scale: 'natural_minor', bpm: 126, synth: 'acid', color: '#ffcc00' },
  { id: 'afro-house', name: 'Afro House', root: 'G', scale: 'dorian', bpm: 122, synth: 'pluck', color: '#ff8844' },
  { id: 'progressive', name: 'Progressive House', root: 'A', scale: 'natural_minor', bpm: 128, synth: 'trance', color: '#00ccff' },
  { id: 'techno', name: 'Techno', root: 'E', scale: 'natural_minor', bpm: 130, synth: 'stab', color: '#888888' },
  { id: 'dark-techno', name: 'Dark Techno', root: 'E', scale: 'phrygian', bpm: 132, synth: 'reese', color: '#666666' },
  { id: 'melodic-techno', name: 'Melodic Techno', root: 'A', scale: 'harmonic_minor', bpm: 124, synth: 'ambient', color: '#9966ff' },
  { id: 'minimal', name: 'Minimal Techno', root: 'D', scale: 'natural_minor', bpm: 128, synth: 'sub', color: '#555555' },
  { id: 'trance', name: 'Trance', root: 'A', scale: 'natural_minor', bpm: 138, synth: 'trance', color: '#00ffcc' },
  { id: 'psytrance', name: 'Psytrance', root: 'E', scale: 'phrygian', bpm: 145, synth: 'acid', color: '#ff00ff' },
  { id: 'edm', name: 'Festival EDM', root: 'C', scale: 'major', bpm: 128, synth: 'supersaw', color: '#ff3366' },
  { id: 'future-bass', name: 'Future Bass', root: 'F', scale: 'lydian', bpm: 150, synth: 'wobble', color: '#ff66cc' },
  { id: 'dnb', name: 'Melodic DnB', root: 'A', scale: 'harmonic_minor', bpm: 174, synth: 'reese', color: '#33ccff' },
  { id: 'liquid-dnb', name: 'Liquid DnB', root: 'D', scale: 'major', bpm: 174, synth: 'ambient', color: '#66ddff' },
  { id: 'dubstep', name: 'Dubstep', root: 'F', scale: 'natural_minor', bpm: 140, synth: 'wobble', color: '#cc33ff' },
  { id: 'garage', name: 'UK Garage', root: 'D', scale: 'dorian', bpm: 130, synth: 'stab', color: '#ffaa00' },
  { id: 'lofi', name: 'Lo-fi Beats', root: 'C', scale: 'pent_minor', bpm: 85, synth: 'pluck', color: '#aa8866' },
  { id: 'disco', name: 'Disco / Nu-Disco', root: 'D', scale: 'mixolydian', bpm: 120, synth: 'organ', color: '#ff44aa' },
  { id: 'ambient', name: 'Ambient / Chill', root: 'C', scale: 'lydian', bpm: 90, synth: 'ambient', color: '#4488ff' },
];

// ── PHASE 2: Arpeggio Patterns (with rhythm multipliers) ──
const ARP_PATTERNS = [
  { id: 'run', name: 'Scale Run', pattern: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,7,6], rhythm: [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1], desc: 'Straight ascending across 2 octaves' },
  { id: 'triad-bounce', name: 'Triad Bounce', pattern: [0,4,7,4,0,7,11,7,0,4,7,14,7,4,0,7], rhythm: [1,0.5,0.5,1,1,0.5,0.5,1,1,0.5,0.5,2,0.5,0.5,1,1], desc: 'Root-3rd-5th bouncing with octave jumps' },
  { id: 'bass-stab', name: 'Bass Stab', pattern: [0,0,7,0,0,4,0,2,0,0,7,0,4,0,5,0], rhythm: [0.5,0.5,1.5,0.5,0.5,1.5,0.5,0.5,0.5,0.5,1.5,0.5,1,0.5,1,0.5], desc: 'Root-heavy low stabs with 5th accents' },
  { id: 'trance-gate', name: 'Trance Gate', pattern: [0,0,2,0,0,4,0,0,5,0,0,7,0,0,9,7], rhythm: [0.5,0.5,1,0.5,0.5,1,0.5,0.5,1,0.5,0.5,1,0.5,0.5,1,1], desc: 'Pulsing root with rising melody' },
  { id: 'edm-pluck', name: 'EDM Pluck', pattern: [0,7,4,7,2,7,5,7,0,9,4,9,2,9,5,9], rhythm: [0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5], desc: 'Fast alternating festival pluck buildup' },
  { id: 'octave-ping', name: 'Octave Ping', pattern: [0,7,1,8,2,9,3,10,4,11,5,12,6,13,7,0], rhythm: [0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25], desc: 'Low-high octave alternation on each note' },
  { id: 'waterfall', name: 'Waterfall', pattern: [14,12,11,9,7,6,4,2,0,2,4,6,7,9,11,12], rhythm: [0.5,0.5,0.5,0.5,1,0.5,0.5,0.5,2,0.5,0.5,0.5,1,0.5,0.5,0.5], desc: 'Cascading descent with a breath at the bottom' },
  { id: 'stutter', name: 'Stutter Gate', pattern: [0,0,0,2,2,2,4,4,4,7,7,7,4,4,2,0], rhythm: [0.25,0.25,0.5,0.25,0.25,0.5,0.25,0.25,0.5,0.25,0.25,0.5,0.5,0.5,0.5,1], desc: 'Rapid-fire repeats before stepping up' },
  { id: 'spiral', name: 'Spiral', pattern: [0,1,0,2,0,3,0,4,0,5,0,6,0,7,0,8], rhythm: [0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5,0.5], desc: 'Root anchored with expanding reach' },
  { id: 'offbeat', name: 'Offbeat Groove', pattern: [7,0,4,0,7,2,4,0,7,0,5,0,7,2,5,0], rhythm: [0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75,0.25,0.75], desc: 'Syncopated hits on the upbeats' },
  { id: 'chord-spread', name: 'Chord Spread', pattern: [0,2,4,7,9,11,14,11,9,7,4,2,0,4,7,11], rhythm: [1,0.5,0.5,1,0.5,0.5,2,0.5,0.5,1,0.5,0.5,2,0.5,0.5,1], desc: 'Harp-like spread across chord tones' },
  { id: 'acid', name: 'Acid Line', pattern: [0,0,3,0,5,0,3,7,0,0,5,3,0,7,5,3], rhythm: [0.5,0.25,0.25,0.5,0.5,0.25,0.25,1,0.5,0.25,0.25,0.5,0.5,0.25,0.25,1], desc: 'TB-303 inspired squelchy bassline' },
];

// ── PHASE 5: Compatible Scales Helper ──
const getCompatibleScales = (rootNote, currentScaleType) => {
  const currentNotes = getScaleNotes(rootNote, SCALE_TYPES[currentScaleType].intervals);
  const results = [];
  for (const [stKey, stVal] of Object.entries(SCALE_TYPES)) {
    for (const root of CHROMATIC) {
      if (root === rootNote && stKey === currentScaleType) continue;
      const notes = getScaleNotes(root, stVal.intervals);
      const shared = notes.filter(n => isNoteInScale(n, currentNotes)).length;
      if (shared >= Math.min(notes.length, currentNotes.length) - 2 && shared >= 4) {
        results.push({ root, scaleKey: stKey, scaleName: stVal.name, shared, total: notes.length, category: stVal.category });
      }
    }
  }
  results.sort((a, b) => b.shared - a.shared);
  return results.slice(0, 8);
};

const ScaleReference = () => {
  const [rootNote, setRootNote] = useState('C');
  const [scaleType, setScaleType] = useState('major');
  const [activeCategory, setActiveCategory] = useState('core');
  const [synthType, setSynthType] = useState('pluck');
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingNote, setPlayingNote] = useState(null);
  const [showChords, setShowChords] = useState(true);
  const [barCount, setBarCount] = useState(4);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState('run');
  const [playMode, setPlayMode] = useState('up');
  const [aiTips, setAiTips] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [melodyNotes, setMelodyNotes] = useState([]);
  const [showMelody, setShowMelody] = useState(false);
  const [showCompatible, setShowCompatible] = useState(false);
  const [copiedScale, setCopiedScale] = useState(false);
  const [downloadingScale, setDownloadingScale] = useState(false);

  const audioCtxRef = useRef(null);
  const activeNodesRef = useRef([]);
  const heldNodesRef = useRef([]);
  const stopRef = useRef(false);
  const loopRef = useRef(false);
  const synthTypeRef = useRef('pluck');
  const bpmRef = useRef(120);
  const patternRef = useRef('run');
  const playModeRef = useRef('up');
  const playGenRef = useRef(0);
  const scaleNotesRef = useRef([]);

  useEffect(() => { synthTypeRef.current = synthType; }, [synthType]);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { loopRef.current = loopEnabled; }, [loopEnabled]);
  useEffect(() => { patternRef.current = selectedPattern; }, [selectedPattern]);
  useEffect(() => { playModeRef.current = playMode; }, [playMode]);
  useEffect(() => { scaleNotesRef.current = scaleNotes; });
  useEffect(() => { return () => { stopRef.current = true; killNotes(); }; }, []);

  const st = SCALE_TYPES[scaleType];
  const scaleNotes = getScaleNotes(rootNote, st.intervals);
  const relKey = getRelativeKey(rootNote, scaleType);
  const scaleChords = getScaleChords(rootNote, scaleType);
  const compatibleScales = showCompatible ? getCompatibleScales(rootNote, scaleType) : [];
  const filteredTypes = Object.entries(SCALE_TYPES).filter(([, v]) => v.category === activeCategory);

  const getCtx = () => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  };

  const playSingleNote = (noteName, octave, duration) => {
    const ctx = getCtx();
    const preset = SYNTH_PRESETS[synthTypeRef.current] || SYNTH_PRESETS.pluck;
    const now = ctx.currentTime;
    const freq = noteToFreq(`${noteName}${octave}`);
    const dur = duration || 0.4;
    const nodes = [];
    preset.layers.forEach(layer => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = layer.oscType;
      osc.frequency.value = freq;
      if (layer.detune) osc.detune.value = layer.detune;
      gain.gain.setValueAtTime(0, now);
      if (preset.decay) {
        gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack);
        gain.gain.exponentialRampToValueAtTime(0.001, now + preset.attack + preset.decay);
      } else {
        gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack);
        gain.gain.linearRampToValueAtTime(0, now + dur);
      }
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + dur + 0.05);
      nodes.push({ osc, gain });
    });
    activeNodesRef.current = nodes;
    setPlayingNote(`${noteName}${octave}`);
    setTimeout(() => setPlayingNote(null), dur * 800);
  };

  const killNotes = () => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state === 'closed') return;
    const now = ctx.currentTime;
    [...activeNodesRef.current, ...heldNodesRef.current].forEach(({ osc, gain }) => {
      try { gain.gain.cancelScheduledValues(now); gain.gain.setValueAtTime(gain.gain.value, now); gain.gain.linearRampToValueAtTime(0, now + 0.02); osc.stop(now + 0.03); } catch(e) {}
    });
    activeNodesRef.current = [];
    heldNodesRef.current = [];
  };

  // Hold-to-sustain for piano keys
  const startHoldNote = (noteName, octave) => {
    releaseHoldNote();
    const ctx = getCtx();
    const preset = SYNTH_PRESETS[synthTypeRef.current] || SYNTH_PRESETS.pluck;
    const now = ctx.currentTime;
    const freq = noteToFreq(`${noteName}${octave}`);
    const nodes = [];
    preset.layers.forEach(layer => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = layer.oscType;
      osc.frequency.value = freq;
      if (layer.detune) osc.detune.value = layer.detune;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack);
      // Sustain indefinitely (no scheduled fade)
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      nodes.push({ osc, gain });
    });
    heldNodesRef.current = nodes;
    setPlayingNote(`${noteName}${octave}`);
  };

  const releaseHoldNote = () => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state === 'closed') return;
    const now = ctx.currentTime;
    heldNodesRef.current.forEach(({ osc, gain }) => {
      try { gain.gain.cancelScheduledValues(now); gain.gain.setValueAtTime(gain.gain.value, now); gain.gain.linearRampToValueAtTime(0, now + 0.08); osc.stop(now + 0.1); } catch(e) {}
    });
    heldNodesRef.current = [];
    setPlayingNote(null);
  };

  const getNoteWithOctave = (scaleIdx) => {
    const notes = scaleNotesRef.current;
    const len = notes.length || 7;
    const octOffset = Math.floor(scaleIdx / len);
    const noteIdx = ((scaleIdx % len) + len) % len;
    return { note: notes[noteIdx] || 'C', oct: 4 + octOffset };
  };

  const playPatternSequence = async () => {
    stopRef.current = false;
    killNotes();
    setIsPlaying(true);
    const gen = ++playGenRef.current;

    do {
      const pat = ARP_PATTERNS.find(p => p.id === patternRef.current) || ARP_PATTERNS[0];
      const totalBeats = barCount * 4;
      let beatsPlayed = 0;
      let step = 0;

      while (beatsPlayed < totalBeats && !stopRef.current && playGenRef.current === gen) {
        const msPerBeat = 60000 / bpmRef.current;
        const patIdx = step % pat.pattern.length;
        const scaleIdx = pat.pattern[patIdx];
        const rhythmMult = pat.rhythm[patIdx] || 1;
        const { note, oct } = getNoteWithOctave(scaleIdx);
        const noteDur = msPerBeat * rhythmMult * 0.75 / 1000;
        playSingleNote(note, oct, noteDur);
        await new Promise(r => setTimeout(r, msPerBeat * rhythmMult));
        beatsPlayed += rhythmMult;
        step++;
      }
      if (stopRef.current || playGenRef.current !== gen) break;
    } while (loopRef.current);

    if (playGenRef.current === gen) { setIsPlaying(false); setPlayingNote(null); }
  };

  const playScaleSequence = async (direction) => {
    stopRef.current = false;
    killNotes();
    setIsPlaying(true);
    const gen = ++playGenRef.current;

    do {
      const notes = scaleNotesRef.current;
      const oct4 = notes.map(n => ({ note: n, oct: 4 }));
      const oct5first = { note: notes[0], oct: 5 };
      let sequence;
      if (direction === 'up') sequence = [...oct4, oct5first];
      else if (direction === 'down') sequence = [oct5first, ...oct4.slice().reverse()];
      else sequence = [...oct4, oct5first, ...oct4.slice().reverse()];

      const totalSlots = barCount * 4;
      const fullSeq = [];
      while (fullSeq.length < totalSlots) fullSeq.push(...sequence);

      for (let i = 0; i < totalSlots; i++) {
        if (stopRef.current || playGenRef.current !== gen) break;
        const msPerBeat = 60000 / bpmRef.current;
        const item = fullSeq[i % fullSeq.length];
        playSingleNote(item.note, item.oct, msPerBeat * 0.8 / 1000);
        await new Promise(r => setTimeout(r, msPerBeat * 0.5));
      }
      if (stopRef.current || playGenRef.current !== gen) break;
    } while (loopRef.current);

    if (playGenRef.current === gen) { setIsPlaying(false); setPlayingNote(null); }
  };

  // ── PHASE 4: Melody Generator ──
  const generateMelody = async () => {
    stopRef.current = false;
    killNotes();
    setIsPlaying(true);
    const gen = ++playGenRef.current;
    const len = scaleNotes.length;
    const totalNotes = barCount * 4;
    const melody = [];
    let currentIdx = Math.floor(Math.random() * len);

    for (let i = 0; i < totalNotes; i++) {
      melody.push(currentIdx);
      const rand = Math.random();
      if (rand < 0.45) currentIdx = Math.max(0, Math.min(len, currentIdx + 1));
      else if (rand < 0.75) currentIdx = Math.max(0, Math.min(len, currentIdx - 1));
      else if (rand < 0.9) currentIdx = Math.max(0, Math.min(len, currentIdx + 2));
      else currentIdx = Math.floor(Math.random() * len);
    }

    const melodyDisplay = melody.map(idx => {
      const { note, oct } = getNoteWithOctave(idx);
      return { note, oct, idx };
    });
    setMelodyNotes(melodyDisplay);
    setShowMelody(true);

    do {
      for (const m of melodyDisplay) {
        if (stopRef.current || playGenRef.current !== gen) break;
        const msPerBeat = 60000 / bpmRef.current;
        playSingleNote(m.note, m.oct, msPerBeat * 0.8 / 1000);
        await new Promise(r => setTimeout(r, msPerBeat));
      }
      if (stopRef.current || playGenRef.current !== gen) break;
    } while (loopRef.current);

    if (playGenRef.current === gen) { setIsPlaying(false); setPlayingNote(null); }
  };

  const replayMelody = async () => {
    if (!melodyNotes.length) return;
    stopRef.current = false;
    killNotes();
    setIsPlaying(true);
    const gen = ++playGenRef.current;
    do {
      for (const m of melodyNotes) {
        if (stopRef.current || playGenRef.current !== gen) break;
        const msPerBeat = 60000 / bpmRef.current;
        playSingleNote(m.note, m.oct, msPerBeat * 0.8 / 1000);
        await new Promise(r => setTimeout(r, msPerBeat));
      }
      if (stopRef.current || playGenRef.current !== gen) break;
    } while (loopRef.current);
    if (playGenRef.current === gen) { setIsPlaying(false); setPlayingNote(null); }
  };

  const stopPlayback = () => { stopRef.current = true; setIsPlaying(false); setPlayingNote(null); killNotes(); };

  const playCurrentMode = (modeOverride) => {
    const mode = modeOverride || playModeRef.current;
    if (mode === 'up') playScaleSequence('up');
    else if (mode === 'down') playScaleSequence('down');
    else if (mode === 'both') playScaleSequence('both');
    else if (mode === 'pattern') playPatternSequence();
    else if (mode === 'melody') generateMelody();
  };

  const switchMode = (newMode) => {
    setPlayMode(newMode);
    playModeRef.current = newMode;
    if (isPlaying) {
      killNotes();
      playCurrentMode(newMode);
    }
  };

  const switchPattern = (newPatternId) => {
    setSelectedPattern(newPatternId);
    patternRef.current = newPatternId;
    if (isPlaying && playModeRef.current === 'pattern') {
      killNotes();
      playPatternSequence();
    }
  };

  // ── PHASE 3: AI Suggestions ──
  const getAiTips = async () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    setAiLoading(true);
    setShowAiPanel(true);
    try {
      const preset = GENRE_PRESETS.find(p => p.root === rootNote && p.scale === scaleType);
      const res = await fetch(`${BACKEND_URL}/api/tools/scale-ai-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ root: rootNote, scale_type: scaleType, scale_name: st.name, genre: preset?.name || activePreset || '', bpm }),
      });
      if (res.ok) {
        const json = await res.json();
        setAiTips(json.data);
      }
    } catch (e) { console.error('AI tips error:', e); }
    setAiLoading(false);
  };

  const applyPreset = (preset) => {
    setRootNote(preset.root);
    setScaleType(preset.scale);
    setBpm(preset.bpm);
    setSynthType(preset.synth);
    setActiveCategory(SCALE_TYPES[preset.scale].category);
    setActivePreset(preset.id);
    setAiTips(null);
    setMelodyNotes([]);
  };

  const whiteKeyWidth = 100 / PIANO_WHITE.length;

  return (
    <div data-testid="scale-reference-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Interactive scale explorer with genre presets, patterns, AI tips & melody generation</p>

      {/* Beat Analyzer - upload a clip for AI scale suggestions */}
      <BeatAnalyzer toolContext="scale_reference" />

      {/* Genre Presets */}
      <div data-testid="genre-presets-section" style={{ marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Genre Presets</div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {GENRE_PRESETS.map(p => (
            <button key={p.id} data-testid={`genre-preset-${p.id}`} onClick={() => applyPreset(p)} style={{
              padding: '4px 10px', fontSize: '9px', fontWeight: 700, borderRadius: '12px', cursor: 'pointer',
              border: `1px solid ${activePreset === p.id ? p.color + '66' : 'rgba(255,255,255,0.06)'}`,
              background: activePreset === p.id ? `${p.color}20` : 'rgba(255,255,255,0.025)',
              color: activePreset === p.id ? p.color : '#777', transition: 'all 0.2s',
            }}>{p.name}</button>
          ))}
        </div>
      </div>

      {/* ── Root Note Selector ── */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginRight: '2px' }}>Root</span>
        <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
          {CHROMATIC.map(n => (
            <button key={n} data-testid={`scale-root-${n.replace('#','s')}`} onClick={() => { setRootNote(n); setActivePreset(null); setAiTips(null); }} style={{
              padding: '5px 8px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
              background: rootNote === n ? 'rgba(0,200,255,0.18)' : 'transparent',
              color: rootNote === n ? '#00ccff' : '#666', transition: 'all 0.2s', minWidth: '28px',
            }}>{n}</button>
          ))}
        </div>
      </div>

      {/* ── Scale Category + Type ── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '3px' }}>
        {SCALE_CATEGORIES.map(cat => (
          <button key={cat.id} data-testid={`scale-cat-${cat.id}`} onClick={() => setActiveCategory(cat.id)} style={{
            flex: 1, padding: '6px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
            background: activeCategory === cat.id ? 'rgba(0,200,255,0.12)' : 'transparent',
            color: activeCategory === cat.id ? '#00ccff' : '#555', transition: 'all 0.2s',
          }}>{cat.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {filteredTypes.map(([key, val]) => (
          <button key={key} data-testid={`scale-type-${key}`} onClick={() => { setScaleType(key); setActivePreset(null); setAiTips(null); }} style={{
            padding: '5px 12px', fontSize: '10px', fontWeight: 600, borderRadius: '14px', cursor: 'pointer', border: 'none',
            background: scaleType === key ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.04)',
            color: scaleType === key ? '#00ccff' : '#888', transition: 'all 0.2s',
          }}>{val.name}</button>
        ))}
      </div>

      {/* ── Scale Info Bar ── */}
      <div style={{ marginBottom: '10px', padding: '10px 14px', background: 'rgba(0,200,255,0.05)', borderRadius: '8px', border: '1px solid rgba(0,200,255,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#00ccff' }}>{rootNote} {st.name}</span>
          <span style={{ fontSize: '12px', color: '#777', marginLeft: '12px' }}>{scaleNotes.join(' \u2014 ')}</span>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {relKey && (
            <button data-testid="scale-relative-key-btn" onClick={() => { setRootNote(relKey.note); setScaleType(relKey.type); setActiveCategory(SCALE_TYPES[relKey.type].category); setActivePreset(null); }} style={{
              padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer',
              border: '1px solid rgba(255,170,0,0.25)', background: 'rgba(255,170,0,0.08)', color: '#ffaa00',
            }}>Relative: {relKey.label}</button>
          )}
          <button
            data-testid="scale-copy-text-btn"
            onClick={async () => {
              await copyToClipboard(formatScaleText(st.name, scaleNotes.join(' — '), rootNote, aiTips ? (aiTips.genre_fit || '') : ''));
              setCopiedScale(true);
              setTimeout(() => setCopiedScale(false), 1500);
            }}
            style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer', border: 'none', background: copiedScale ? 'rgba(0,255,136,0.15)' : 'rgba(6,182,212,0.1)', color: copiedScale ? '#00ff88' : '#22d3ee', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s' }}
          >{copiedScale ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}</button>
          <button
            data-testid="scale-download-image-btn"
            onClick={async () => {
              setDownloadingScale(true);
              const dataUrl = await generateShareImage('scale-share-card');
              if (dataUrl) downloadImage(dataUrl, `beatseek-scale-${rootNote}-${scaleType}.png`);
              setDownloadingScale(false);
            }}
            style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer', border: 'none', background: 'rgba(139,92,246,0.1)', color: '#a78bfa', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s' }}
          ><Download size={10} /> {downloadingScale ? '...' : 'Image'}</button>
        </div>
      </div>

      {/* ── Controls: Synth + BPM + Bars + Loop ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
        {/* Synth */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>Sound</span>
          <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
            {Object.entries(SYNTH_PRESETS).map(([key, preset]) => (
              <button key={key} data-testid={`scale-synth-${key}`} onClick={() => setSynthType(key)} style={{
                padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: synthType === key ? 'rgba(255,0,255,0.15)' : 'transparent',
                color: synthType === key ? '#ff66ff' : '#555', transition: 'all 0.2s',
              }}>{preset.name}</button>
            ))}
          </div>
        </div>
        {/* BPM + Bars + Loop row */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>BPM</span>
            <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
              {BPM_PRESETS.map(b => (
                <button key={b} data-testid={`scale-bpm-${b}`} onClick={() => setBpm(b)} style={{
                  padding: '3px 7px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                  background: bpm === b ? 'rgba(255,170,0,0.15)' : 'transparent',
                  color: bpm === b ? '#ffaa00' : '#555', transition: 'all 0.2s', minWidth: '28px',
                }}>{b}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <button onClick={() => setBpm(prev => Math.max(60, prev - 1))} style={{ width: '20px', height: '20px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</button>
              <input data-testid="scale-bpm-input" type="number" min="60" max="200" value={bpm} onChange={e => { const v = parseInt(e.target.value); if (v >= 60 && v <= 200) setBpm(v); }}
                style={{ width: '40px', padding: '2px 4px', fontSize: '11px', fontWeight: 700, textAlign: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#ffaa00', outline: 'none' }} />
              <button onClick={() => setBpm(prev => Math.min(200, prev + 1))} style={{ width: '20px', height: '20px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            </div>
          </div>
          {/* Bars */}
          <div style={{ display: 'flex', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '2px' }}>
            {[4, 8].map(b => (
              <button key={b} data-testid={`scale-bar-${b}`} onClick={() => setBarCount(b)} style={{
                padding: '4px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: barCount === b ? 'rgba(0,255,255,0.15)' : 'transparent',
                color: barCount === b ? '#00ffff' : '#666', transition: 'all 0.2s',
              }}>{b} bars</button>
            ))}
          </div>
          {/* Loop */}
          <button data-testid="scale-loop-toggle" onClick={() => setLoopEnabled(!loopEnabled)} style={{
            padding: '4px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
            border: loopEnabled ? '1px solid rgba(0,255,136,0.4)' : '1px solid rgba(255,255,255,0.08)',
            background: loopEnabled ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.03)',
            color: loopEnabled ? '#00ff88' : '#666', transition: 'all 0.2s',
          }}>{'\u{1F501}'} Loop {loopEnabled ? 'ON' : 'OFF'}</button>
        </div>
      </div>

      {/* ════ PLAY MODE + PATTERN SELECTOR + PLAY BUTTON ════ */}
      <div data-testid="playback-section" style={{ marginBottom: '14px', padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Play Mode Selector */}
        <div style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Play Mode</div>
        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {[
            { id: 'up', label: '\u25B2 Ascending', color: '#00ccff' },
            { id: 'down', label: '\u25BC Descending', color: '#00ccff' },
            { id: 'both', label: '\u25B2\u25BC Up & Down', color: '#00ccff' },
            { id: 'pattern', label: '\u{1F3B5} Pattern', color: '#ffaa00' },
            { id: 'melody', label: '\u{1F3B2} Melody', color: '#ff66ff' },
          ].map(m => (
            <button key={m.id} data-testid={`play-mode-${m.id}`} onClick={() => switchMode(m.id)} style={{
              padding: '5px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
              border: playMode === m.id ? `1px solid ${m.color}55` : '1px solid rgba(255,255,255,0.06)',
              background: playMode === m.id ? `${m.color}18` : 'rgba(255,255,255,0.025)',
              color: playMode === m.id ? m.color : '#666', transition: 'all 0.2s',
            }}>{m.label}</button>
          ))}
        </div>

        {/* Pattern selector - only visible when Pattern mode is selected */}
        {playMode === 'pattern' && (
          <div data-testid="arp-patterns-section" style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Arp Pattern</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {ARP_PATTERNS.map(p => (
                <button key={p.id} data-testid={`arp-pattern-${p.id}`} onClick={() => switchPattern(p.id)}
                  title={p.desc}
                  style={{
                    padding: '4px 10px', fontSize: '9px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                    border: selectedPattern === p.id ? '1px solid rgba(255,170,0,0.35)' : '1px solid rgba(255,255,255,0.06)',
                    background: selectedPattern === p.id ? 'rgba(255,170,0,0.12)' : 'rgba(255,255,255,0.025)',
                    color: selectedPattern === p.id ? '#ffaa00' : '#777', transition: 'all 0.2s',
                  }}>{p.name}</button>
              ))}
            </div>
            <div style={{ fontSize: '9px', color: '#555', marginTop: '4px', fontStyle: 'italic' }}>
              {(ARP_PATTERNS.find(p => p.id === selectedPattern) || {}).desc || ''}
            </div>
          </div>
        )}

        {/* Big Play / Stop Button */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isPlaying ? (
            <button data-testid="scale-stop-btn" onClick={stopPlayback} style={{
              flex: 1, padding: '10px 20px', fontSize: '14px', fontWeight: 800, borderRadius: '12px', cursor: 'pointer',
              border: 'none', background: 'rgba(255,51,51,0.2)', color: '#ff5555', transition: 'all 0.2s',
              letterSpacing: '1px',
            }}>{'\u25A0'} STOP</button>
          ) : (
            <button data-testid="scale-play-btn" onClick={() => playCurrentMode()} style={{
              flex: 1, padding: '10px 20px', fontSize: '14px', fontWeight: 800, borderRadius: '12px', cursor: 'pointer',
              border: 'none', transition: 'all 0.2s', letterSpacing: '1px',
              background: playMode === 'pattern' ? 'rgba(255,170,0,0.2)' : playMode === 'melody' ? 'rgba(255,0,255,0.2)' : 'rgba(0,200,255,0.2)',
              color: playMode === 'pattern' ? '#ffaa00' : playMode === 'melody' ? '#ff66ff' : '#00ccff',
            }}>{'\u25B6'} PLAY {playMode === 'pattern' ? (ARP_PATTERNS.find(p => p.id === selectedPattern) || {}).name || '' : playMode.toUpperCase()}</button>
          )}
          <button data-testid="scale-ai-btn" onClick={getAiTips} disabled={aiLoading} style={{
            padding: '10px 16px', fontSize: '11px', fontWeight: 700, borderRadius: '12px', cursor: 'pointer', border: 'none',
            background: 'rgba(0,255,136,0.1)', color: '#00ff88', opacity: aiLoading ? 0.5 : 1, transition: 'all 0.2s',
          }}>{aiLoading ? '...' : '\u{2728} AI Tips'}</button>
        </div>
      </div>

      {/* ── Interactive Piano Roll ── */}
      <div data-testid="scale-piano-roll" style={{ position: 'relative', height: '150px', marginBottom: '14px', userSelect: 'none' }}>
        <div style={{ display: 'flex', height: '100%', position: 'relative' }}>
          {PIANO_WHITE.map((k, i) => {
            const inScale = isNoteInScale(k.note, scaleNotes);
            const isRoot = isRootNote(k.note, rootNote);
            const isActive = playingNote === `${k.note}${k.oct}`;
            return (
              <div key={i} data-testid={`piano-key-${k.note}${k.oct}`}
                onMouseDown={() => startHoldNote(k.note, k.oct)}
                onMouseUp={releaseHoldNote}
                onMouseLeave={releaseHoldNote}
                onTouchStart={(e) => { e.preventDefault(); startHoldNote(k.note, k.oct); }}
                onTouchEnd={releaseHoldNote}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                  paddingBottom: '8px', cursor: 'pointer', transition: 'all 0.12s', borderRadius: '0 0 5px 5px', marginRight: '1px',
                  background: isActive ? 'rgba(0,200,255,0.5)' : isRoot ? 'linear-gradient(180deg, rgba(0,200,255,0.4) 0%, rgba(0,200,255,0.15) 100%)' : inScale ? 'linear-gradient(180deg, rgba(0,200,255,0.2) 0%, rgba(0,200,255,0.06) 100%)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${isRoot ? 'rgba(0,200,255,0.4)' : inScale ? 'rgba(0,200,255,0.15)' : 'rgba(255,255,255,0.08)'}`,
                  transform: isActive ? 'scaleY(0.98)' : 'none', boxShadow: isActive ? '0 0 12px rgba(0,200,255,0.4)' : 'none',
                }}>
                <div style={{ fontSize: '11px', fontWeight: isRoot ? 900 : inScale ? 700 : 500, color: isRoot ? '#00eeff' : inScale ? '#00aadd' : '#444' }}>{k.note}</div>
                {isRoot && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#00ccff', marginTop: '2px' }} />}
              </div>
            );
          })}
        </div>
        {PIANO_BLACK.map((k, i) => {
          const inScale = isNoteInScale(k.note, scaleNotes);
          const isRoot = isRootNote(k.note, rootNote);
          const isActive = playingNote === `${k.note}${k.oct}`;
          const leftPct = ((k.pos + 0.65) * whiteKeyWidth);
          return (
            <div key={`b${i}`} data-testid={`piano-key-${k.note.replace('#','s')}${k.oct}`}
              onMouseDown={(e) => { e.stopPropagation(); startHoldNote(k.note, k.oct); }}
              onMouseUp={releaseHoldNote}
              onMouseLeave={releaseHoldNote}
              onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); startHoldNote(k.note, k.oct); }}
              onTouchEnd={releaseHoldNote}
              style={{
                position: 'absolute', top: 0, left: `${leftPct}%`, width: `${whiteKeyWidth * 0.65}%`, height: '58%',
                cursor: 'pointer', borderRadius: '0 0 4px 4px', transition: 'all 0.12s', zIndex: 2,
                background: isActive ? 'rgba(0,200,255,0.6)' : isRoot ? 'linear-gradient(180deg, rgba(0,180,255,0.5) 0%, #1a1a2e 100%)' : inScale ? 'linear-gradient(180deg, rgba(0,150,220,0.3) 0%, #1a1a2e 100%)' : 'linear-gradient(180deg, #2a2a3e 0%, #1a1a2e 100%)',
                border: `1px solid ${isRoot ? 'rgba(0,200,255,0.5)' : inScale ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.1)'}`,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px',
                transform: isActive ? 'scaleY(0.97)' : 'none', boxShadow: isActive ? '0 0 10px rgba(0,200,255,0.5)' : '0 2px 4px rgba(0,0,0,0.3)',
              }}>
              <span style={{ fontSize: '8px', fontWeight: 700, color: isRoot ? '#00eeff' : inScale ? '#0099cc' : '#444' }}>{k.note}</span>
            </div>
          );
        })}
      </div>

      {/* ════ PHASE 4: Generated Melody Display ════ */}
      {showMelody && melodyNotes.length > 0 && (
        <div data-testid="melody-display" style={{ padding: '12px', background: 'rgba(255,0,255,0.04)', borderRadius: '8px', border: '1px solid rgba(255,0,255,0.15)', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#ff66ff', textTransform: 'uppercase', letterSpacing: '1px' }}>Generated Melody ({barCount} bars)</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {!isPlaying && (
                <>
                  <button data-testid="melody-replay-btn" onClick={replayMelody} style={{ padding: '3px 10px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'rgba(255,0,255,0.15)', color: '#ff66ff' }}>{'\u25B6'} Replay</button>
                  <button data-testid="melody-new-btn" onClick={generateMelody} style={{ padding: '3px 10px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'rgba(255,170,0,0.12)', color: '#ffaa00' }}>{'\u{1F3B2}'} New</button>
                </>
              )}
              <button onClick={() => setShowMelody(false)} style={{ padding: '3px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'rgba(255,255,255,0.06)', color: '#666' }}>Hide</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
            {melodyNotes.map((m, i) => {
              const barBorder = i > 0 && i % 4 === 0;
              return (
                <React.Fragment key={i}>
                  {barBorder && <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 2px', alignSelf: 'stretch' }} />}
                  <span onClick={() => playSingleNote(m.note, m.oct)} style={{
                    fontSize: '12px', fontWeight: 700, padding: '4px 8px', borderRadius: '5px', cursor: 'pointer',
                    background: 'rgba(255,0,255,0.08)', color: '#dd88ff', border: '1px solid rgba(255,0,255,0.12)',
                    transition: 'all 0.12s', minWidth: '28px', textAlign: 'center',
                  }}>{m.note}{m.oct}</span>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Scale Intervals ── */}
      <div style={{ padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.04)', marginBottom: '10px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Intervals</div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {st.intervalNames.map((interval, i) => (
            <div key={i} onClick={() => playSingleNote(scaleNotes[i], 4)} style={{
              padding: '5px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer',
              background: interval === 'Root' ? 'rgba(0,200,255,0.15)' : 'rgba(0,200,255,0.06)',
              color: interval === 'Root' ? '#00eeff' : '#0099bb',
              border: interval === 'Root' ? '1px solid rgba(0,200,255,0.3)' : '1px solid transparent', transition: 'all 0.15s',
            }}>
              <span style={{ fontWeight: 800 }}>{scaleNotes[i]}</span> <span style={{ opacity: 0.7 }}>{interval}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scale-to-Chord Suggestions ── */}
      {scaleChords.length > 0 && (
        <div style={{ padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.04)', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '1px' }}>Chords in this Scale</div>
            <button data-testid="scale-chords-toggle" onClick={() => setShowChords(!showChords)} style={{
              fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: '8px', cursor: 'pointer',
              border: 'none', background: 'rgba(255,255,255,0.04)', color: '#666',
            }}>{showChords ? 'Collapse' : 'Expand'}</button>
          </div>
          {showChords && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {scaleChords.map((sc, i) => {
                const qColor = sc.quality === 'maj' ? '#00ff88' : sc.quality === 'min' ? '#00ccff' : sc.quality === 'dim' ? '#ff6666' : '#ffaa00';
                return (
                  <div key={i} data-testid={`scale-chord-${i}`} onClick={() => {
                    const chordNotes = CHORD_TO_NOTES[sc.chord];
                    if (chordNotes) {
                      killNotes();
                      const ctx = getCtx();
                      const preset = SYNTH_PRESETS[synthTypeRef.current] || SYNTH_PRESETS.pluck;
                      const now = ctx.currentTime;
                      const nodes = [];
                      chordNotes.forEach(noteName => {
                        const freq = noteToFreq(noteName);
                        preset.layers.forEach(layer => {
                          const osc = ctx.createOscillator(); const gain = ctx.createGain();
                          osc.type = layer.oscType; osc.frequency.value = freq;
                          if (layer.detune) osc.detune.value = layer.detune;
                          gain.gain.setValueAtTime(0, now);
                          if (preset.decay) { gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack); gain.gain.exponentialRampToValueAtTime(0.001, now + preset.attack + preset.decay); }
                          else { gain.gain.linearRampToValueAtTime(layer.vol, now + preset.attack); gain.gain.linearRampToValueAtTime(0, now + 0.6); }
                          osc.connect(gain); gain.connect(ctx.destination); osc.start(now); osc.stop(now + 0.7);
                          nodes.push({ osc, gain });
                        });
                      });
                      activeNodesRef.current = nodes;
                    }
                  }}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                    background: 'rgba(255,255,255,0.025)', border: `1px solid ${qColor}20`, transition: 'all 0.15s', minWidth: '60px',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${qColor}12`; e.currentTarget.style.borderColor = `${qColor}40`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; e.currentTarget.style.borderColor = `${qColor}20`; }}>
                    <div style={{ fontSize: '9px', fontWeight: 600, color: '#666', marginBottom: '3px' }}>{sc.roman}</div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: qColor }}>{sc.chord}</div>
                    <div style={{ fontSize: '8px', fontWeight: 600, color: '#555', marginTop: '2px', textTransform: 'capitalize' }}>{sc.quality}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════ PHASE 3: AI Tips Panel ════ */}
      {showAiPanel && (
        <div data-testid="ai-tips-panel" style={{ padding: '14px', background: 'rgba(0,255,136,0.03)', borderRadius: '10px', border: '1px solid rgba(0,255,136,0.15)', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#00ff88', textTransform: 'uppercase', letterSpacing: '1px' }}>{'\u{2728}'} AI Production Tips — {rootNote} {st.name}</span>
            <button onClick={() => setShowAiPanel(false)} style={{ padding: '2px 8px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'rgba(255,255,255,0.06)', color: '#666' }}>Close</button>
          </div>
          {aiLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#00ff88', fontSize: '12px' }}>Generating tips for {rootNote} {st.name}...</div>
          ) : aiTips ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Genre Fit */}
              {aiTips.genre_fit && (
                <div style={{ padding: '10px 12px', background: 'rgba(0,255,136,0.06)', borderRadius: '8px', border: '1px solid rgba(0,255,136,0.1)' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#00cc88', textTransform: 'uppercase', marginBottom: '4px' }}>Genre Fit</div>
                  <div style={{ fontSize: '11px', color: '#bbb', lineHeight: 1.5 }}>{aiTips.genre_fit}</div>
                </div>
              )}
              {/* Production Tips */}
              {aiTips.production_tips?.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#00cc88', textTransform: 'uppercase', marginBottom: '6px' }}>Production Tips</div>
                  {aiTips.production_tips.map((tip, i) => (
                    <div key={i} style={{ fontSize: '11px', color: '#aaa', padding: '4px 0', paddingLeft: '10px', borderLeft: '2px solid rgba(0,255,136,0.2)', marginBottom: '4px', lineHeight: 1.4 }}>{tip}</div>
                  ))}
                </div>
              )}
              {/* Chord Suggestions */}
              {aiTips.chord_suggestions?.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#ffaa00', textTransform: 'uppercase', marginBottom: '6px' }}>Chord Progressions</div>
                  {aiTips.chord_suggestions.map((cs, i) => (
                    <div key={i} style={{ fontSize: '11px', color: '#ccc', padding: '6px 10px', background: 'rgba(255,170,0,0.04)', borderRadius: '6px', marginBottom: '3px', fontWeight: 600 }}>{cs}</div>
                  ))}
                </div>
              )}
              {/* Melody Ideas */}
              {aiTips.melody_ideas?.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#ff66ff', textTransform: 'uppercase', marginBottom: '6px' }}>Melody Ideas</div>
                  {aiTips.melody_ideas.map((mi, i) => (
                    <div key={i} style={{ fontSize: '11px', color: '#aaa', padding: '4px 0', paddingLeft: '10px', borderLeft: '2px solid rgba(255,0,255,0.2)', marginBottom: '4px', lineHeight: 1.4 }}>{mi}</div>
                  ))}
                </div>
              )}
              {/* Compatible Scales */}
              {aiTips.compatible_scales?.length > 0 && (
                <div>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#00ccff', textTransform: 'uppercase', marginBottom: '6px' }}>Transition Scales</div>
                  {aiTips.compatible_scales.map((cs, i) => (
                    <div key={i} style={{ fontSize: '11px', color: '#aaa', padding: '4px 0', paddingLeft: '10px', borderLeft: '2px solid rgba(0,200,255,0.2)', marginBottom: '4px', lineHeight: 1.4 }}>{cs}</div>
                  ))}
                </div>
              )}
              <button data-testid="ai-refresh-btn" onClick={getAiTips} disabled={aiLoading} style={{
                alignSelf: 'flex-start', padding: '4px 12px', fontSize: '9px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                border: 'none', background: 'rgba(0,255,136,0.1)', color: '#00ff88', marginTop: '4px',
              }}>{'\u{1F504}'} Regenerate</button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '12px', color: '#666', fontSize: '11px' }}>No tips loaded. Click "AI Tips" to generate.</div>
          )}
        </div>
      )}

      {/* ════ PHASE 5: Compatible Scales ════ */}
      <div data-testid="compatible-scales-section" style={{ marginBottom: '10px' }}>
        <button data-testid="compatible-scales-toggle" onClick={() => setShowCompatible(!showCompatible)} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
          padding: '10px 14px', borderRadius: '10px', transition: 'all 0.2s', border: 'none',
          background: showCompatible ? 'rgba(0,200,255,0.05)' : 'rgba(255,255,255,0.02)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>Compatible Scales</span>
            <span style={{ fontSize: '9px', color: '#666' }}>Best for transitions & key changes</span>
          </div>
          <span style={{ fontSize: '12px', color: '#666', transform: showCompatible ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>{'\u25BC'}</span>
        </button>
        {showCompatible && compatibleScales.length > 0 && (
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {compatibleScales.map((cs, i) => (
              <div key={i} data-testid={`compatible-scale-${i}`}
                onClick={() => { setRootNote(cs.root); setScaleType(cs.scaleKey); setActiveCategory(cs.category); setActivePreset(null); setAiTips(null); setShowCompatible(false); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px',
                  background: 'rgba(255,255,255,0.02)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
                  border: '1px solid rgba(255,255,255,0.04)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,200,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,200,255,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)'; }}
              >
                <div>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#00ccff' }}>{cs.root} {cs.scaleName}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: '8px',
                    background: cs.shared >= cs.total - 1 ? 'rgba(0,255,136,0.15)' : cs.shared >= cs.total - 2 ? 'rgba(255,170,0,0.12)' : 'rgba(255,255,255,0.06)',
                    color: cs.shared >= cs.total - 1 ? '#00ff88' : cs.shared >= cs.total - 2 ? '#ffaa00' : '#888',
                  }}>{cs.shared}/{cs.total} shared</span>
                  <span style={{ fontSize: '11px', color: '#555' }}>{'\u2192'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '10px', color: '#444', fontStyle: 'italic' }}>
        Tap piano keys to hear notes \u2014 Use Pattern for arpeggio runs \u2014 Melody for AI-generated phrases
      </div>

      {/* Hidden share card for scale image export */}
      <div id="scale-share-card" style={{ position: 'fixed', left: '-9999px', top: 0, width: '320px', padding: '3px', background: 'linear-gradient(135deg, #c026d3, #06b6d4)', borderRadius: '16px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
        <div style={{ background: '#0b1120', borderRadius: '14px', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
          {/* Ghost music notes background */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            {[
              { ch: '\u266A', top: '8%', left: '8%', size: '28px', rot: '-15deg', op: 0.10 },
              { ch: '\u266B', top: '22%', right: '10%', size: '22px', rot: '20deg', op: 0.035 },
              { ch: '\u266C', top: '45%', left: '5%', size: '20px', rot: '10deg', op: 0.10 },
              { ch: '\u2669', top: '38%', right: '6%', size: '26px', rot: '-25deg', op: 0.10 },
              { ch: '\u266A', top: '62%', left: '12%', size: '18px', rot: '30deg', op: 0.035 },
              { ch: '\u266B', top: '70%', right: '14%', size: '24px', rot: '-10deg', op: 0.10 },
              { ch: '\u266C', top: '85%', left: '6%', size: '22px', rot: '15deg', op: 0.10 },
              { ch: '\u2669', top: '90%', right: '8%', size: '18px', rot: '-20deg', op: 0.035 },
              { ch: '\u266A', top: '15%', left: '75%', size: '16px', rot: '25deg', op: 0.10 },
              { ch: '\u266B', top: '55%', right: '75%', size: '20px', rot: '-5deg', op: 0.10 },
            ].map((n, i) => (
              <span key={i} style={{ position: 'absolute', top: n.top, left: n.left, right: n.right, fontSize: n.size, transform: `rotate(${n.rot})`, opacity: n.op, color: '#ffffff' }}>{n.ch}</span>
            ))}
          </div>

          {/* BEATSEEK header centered */}
          <div style={{ textAlign: 'center', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
            <div style={{ width: '50px', height: '50px', borderRadius: '50%', margin: '0 auto 8px', position: 'relative', background: 'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.15) 0%, transparent 50%), linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 25%, #1a1a1a 50%, #0a0a0a 75%, #1a1a1a 100%)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.9), 0 0 12px rgba(0,255,255,0.3), 0 0 24px rgba(255,0,255,0.15)' }}>
              <div style={{ position: 'absolute', inset: '5px', borderRadius: '50%', background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 1.5px, rgba(255,255,255,0.08) 1.5px, rgba(255,255,255,0.08) 2.5px)' }}></div>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '16px', height: '16px', borderRadius: '50%', background: 'linear-gradient(135deg, #00ffff, #ff00ff)', boxShadow: '0 0 8px rgba(0,255,255,0.5)' }}></div>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '5px', height: '5px', borderRadius: '50%', background: 'radial-gradient(circle at 30% 30%, #333, #000)', border: '1px solid rgba(255,255,255,0.4)' }}></div>
            </div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', letterSpacing: '3px' }}>BEATSEEK</div>
            <div style={{ fontSize: '9px', color: '#555', letterSpacing: '2px', marginTop: '2px', textTransform: 'uppercase' }}>Scale Reference</div>
          </div>

          {/* Scale name */}
          <div style={{ textAlign: 'center', fontSize: '22px', fontWeight: 700, color: '#22d3ee', marginBottom: '16px', position: 'relative', zIndex: 1 }}>{rootNote} {st.name}</div>

          {/* Scale notes */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
            {scaleNotes.map((n, i) => (
              <span key={i} style={{ fontSize: '14px', fontWeight: 700, padding: '7px 14px', borderRadius: '10px', background: i === 0 ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.04)', border: i === 0 ? '1px solid rgba(6,182,212,0.35)' : '1px solid rgba(255,255,255,0.08)', color: i === 0 ? '#22d3ee' : '#cbd5e1' }}>{n}</span>
            ))}
          </div>

          {/* Interval names */}
          {st.intervalNames && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
              {st.intervalNames.map((name, i) => (
                <span key={i} style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(192,38,211,0.08)', border: '1px solid rgba(192,38,211,0.15)', color: '#d8b4fe', fontWeight: 600 }}>{name}</span>
              ))}
            </div>
          )}

          {/* Relative key */}
          {relKey && <div style={{ textAlign: 'center', fontSize: '11px', color: '#fbbf24', marginBottom: '14px', fontWeight: 600, position: 'relative', zIndex: 1 }}>Relative: {relKey.label}</div>}

          {/* Scale chords */}
          {showChords && scaleChords.length > 0 && (
            <div style={{ marginBottom: '16px', position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: '9px', color: '#555', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1.5px', textAlign: 'center' }}>Scale Chords</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {scaleChords.map((ch, i) => (
                  <span key={i} style={{ fontSize: '12px', fontWeight: 700, padding: '5px 12px', borderRadius: '8px', background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.2)', color: '#f472b6' }}>{ch}</span>
                ))}
              </div>
            </div>
          )}

          {/* AI tips */}
          {aiTips && aiTips.genre_fit && (
            <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.5, textAlign: 'center', marginBottom: '16px', padding: '10px 12px', background: 'rgba(0,255,136,0.03)', borderRadius: '8px', border: '1px solid rgba(0,255,136,0.08)', position: 'relative', zIndex: 1 }}>
              {aiTips.genre_fit}
            </div>
          )}

          {/* Separator + footer */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600, letterSpacing: '1px' }}>beatseek.io</span>
            <span style={{ fontSize: '10px', color: '#475569' }}>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <div style={{ textAlign: 'center', paddingTop: '10px', position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, letterSpacing: '0.5px' }}>thebeatseek.com</span>
          </div>
        </div>
      </div>

    </div>
  );
};

const FREQ_RANGES = [
  { name: 'Sub Bass', range: '20 – 60 Hz', color: '#ff3333', desc: 'Felt more than heard. Sub bass, rumble.', instruments: 'Sub synths, 808s, kick sub', tips: 'High-pass everything else below 30Hz. Use a spectrum analyzer.' },
  { name: 'Bass', range: '60 – 250 Hz', color: '#ff6633', desc: 'Body and warmth of bass instruments.', instruments: 'Kick body, bass synths, bass guitar', tips: 'Cut muddiness around 200Hz. Sidechain kick to bass.' },
  { name: 'Low Mids', range: '250 – 500 Hz', color: '#ffaa00', desc: 'Warmth, but also muddiness lives here.', instruments: 'Synth pads, vocals, snare body', tips: 'The "mud zone." Cut here to add clarity. Narrow boosts only.' },
  { name: 'Midrange', range: '500 Hz – 2 kHz', color: '#00ff88', desc: 'Presence and definition of most instruments.', instruments: 'Vocals, leads, pianos, guitars', tips: 'Key area for vocal clarity. 1kHz boost = telephone effect.' },
  { name: 'Upper Mids', range: '2 – 4 kHz', color: '#00d4ff', desc: 'Attack, edge, and perceived loudness.', instruments: 'Vocal presence, snare crack, synth bite', tips: 'Human ear is most sensitive here. Small boosts go a long way.' },
  { name: 'Presence', range: '4 – 8 kHz', color: '#6666ff', desc: 'Clarity, sibilance, and definition.', instruments: 'Hi-hats, cymbals, vocal sibilance', tips: 'De-ess vocals in this range. Boost for clarity, cut for smoothness.' },
  { name: 'Brilliance', range: '8 – 16 kHz', color: '#cc66ff', desc: 'Air, sparkle, and shimmer.', instruments: 'Cymbals, synth air, reverb tails', tips: 'Gentle shelf boost for "air." Rolls off naturally with age.' },
  { name: 'Ultra High', range: '16 – 20 kHz', color: '#ff66ff', desc: 'Extreme top end — subtle shimmer.', instruments: 'Electronic overtones, psychoacoustic', tips: 'Most adults can\'t hear above 17kHz. Low-pass here to save headroom.' },
];

const FrequencyGuide = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div data-testid="frequency-guide-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>EQ cheat sheet — tap a range for details, instruments & mixing tips</p>

      {/* Visual frequency spectrum bar */}
      <div data-testid="freq-spectrum-bar" style={{ display: 'flex', height: '32px', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }}>
        {FREQ_RANGES.map((fr, i) => (
          <div
            key={i}
            onClick={() => setExpanded(expanded === i ? null : i)}
            style={{
              flex: 1, background: expanded === i ? fr.color : `${fr.color}88`, cursor: 'pointer',
              transition: 'all 0.3s', opacity: expanded !== null && expanded !== i ? 0.3 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '8px', fontWeight: 700, color: '#000', textShadow: '0 0 4px rgba(255,255,255,0.3)' }}>
              {fr.name.split(' ')[0]}
            </span>
          </div>
        ))}
      </div>

      {/* Frequency cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {FREQ_RANGES.map((fr, i) => (
          <div key={i}>
            <button
              onClick={() => setExpanded(expanded === i ? null : i)}
              data-testid={`freq-range-${i}`}
              style={{
                width: '100%', textAlign: 'left', padding: '12px 14px', cursor: 'pointer', border: 'none',
                background: expanded === i ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                borderRadius: expanded === i ? '10px 10px 0 0' : '10px',
                display: 'flex', alignItems: 'center', gap: '10px',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: fr.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{fr.name}</span>
                <span style={{ fontSize: '11px', color: '#666', marginLeft: '10px' }}>{fr.range}</span>
              </div>
              <span style={{ fontSize: '14px', color: '#555', transition: 'transform 0.2s', transform: expanded === i ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
            </button>

            {expanded === i && (
              <div style={{
                padding: '14px', background: 'rgba(0,0,0,0.3)', borderRadius: '0 0 10px 10px',
                borderTop: `2px solid ${fr.color}40`,
              }}>
                <p style={{ fontSize: '12px', color: '#ccc', margin: '0 0 10px 0' }}>{fr.desc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: fr.color }}>INSTRUMENTS</span>
                    <p style={{ fontSize: '11px', color: '#aaa', margin: '2px 0 0 0' }}>{fr.instruments}</p>
                  </div>
                  <div>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: fr.color }}>MIXING TIPS</span>
                    <p style={{ fontSize: '11px', color: '#aaa', margin: '2px 0 0 0' }}>{fr.tips}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Track DNA link */}
      <div data-testid="freq-trackdna-link" style={{
        marginTop: '1.25rem', padding: '12px 14px', borderRadius: '10px',
        background: 'rgba(255,0,255,0.04)', border: '1px solid rgba(255,0,255,0.12)',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span style={{ fontSize: '16px' }}>&#128269;</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#ff66ff' }}>Want to analyze a specific track?</div>
          <div style={{ fontSize: '10px', color: '#888' }}>Use Track DNA in Producers Corner to search credits, remixes & samples for any track</div>
        </div>
      </div>
    </div>
  );
};

const MIDI_NOTES = (() => {
  const noteNames = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
  const rows = [];
  for (let octave = 0; octave <= 8; octave++) {
    for (let n = 0; n < 12; n++) {
      const midi = octave * 12 + n;
      if (midi > 127) break;
      rows.push({ note: `${noteNames[n]}${octave}`, midi, octave });
    }
  }
  return rows;
})();

const MidiReference = () => {
  const [filterOctave, setFilterOctave] = useState('All');
  const [searchNote, setSearchNote] = useState('');
  const [copiedMidi, setCopiedMidi] = useState(null);

  const filtered = MIDI_NOTES.filter(n => {
    if (filterOctave !== 'All' && n.octave !== parseInt(filterOctave)) return false;
    if (searchNote && !n.note.toLowerCase().includes(searchNote.toLowerCase()) && !String(n.midi).includes(searchNote)) return false;
    return true;
  });

  const copyToClipboard = (text, midi) => {
    navigator.clipboard.writeText(String(text));
    setCopiedMidi(midi);
    setTimeout(() => setCopiedMidi(null), 1200);
  };

  // Range color coding
  const getRangeInfo = (midi) => {
    if (midi <= 27) return { label: 'Sub', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' };
    if (midi <= 48) return { label: 'Bass', color: '#f97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.2)' };
    if (midi <= 72) return { label: 'Mid', color: '#22d3ee', bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.2)' };
    if (midi <= 96) return { label: 'High', color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.2)' };
    return { label: 'Air', color: '#e879f9', bg: 'rgba(232,121,249,0.08)', border: 'rgba(232,121,249,0.2)' };
  };

  // Common reference points
  const landmarks = [
    { note: 'C3', midi: 36, label: 'Kick Drum (GM)' },
    { note: 'D3', midi: 38, label: 'Snare (GM)' },
    { note: 'F#3', midi: 42, label: 'Closed Hi-Hat (GM)' },
    { note: 'C5', midi: 60, label: 'Middle C' },
    { note: 'A5', midi: 69, label: 'A440 Concert Pitch' },
  ];

  return (
    <div data-testid="midi-reference-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Note-to-MIDI chart — search by note name or MIDI number</p>

      {/* Quick landmarks */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {landmarks.map((lm, i) => (
          <div key={i} style={{
            padding: '6px 10px', borderRadius: '8px', fontSize: '10px',
            background: 'rgba(255,0,255,0.06)', border: '1px solid rgba(255,0,255,0.15)',
          }}>
            <span style={{ fontWeight: 700, color: '#ff66ff' }}>{lm.note}</span>
            <span style={{ color: '#888' }}> = {lm.midi}</span>
            <span style={{ color: '#666', marginLeft: '4px' }}>({lm.label})</span>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
        <input
          data-testid="midi-search"
          value={searchNote}
          onChange={(e) => setSearchNote(e.target.value)}
          placeholder="Search note or MIDI #..."
          style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none' }}
        />
        <select
          data-testid="midi-octave-filter"
          value={filterOctave}
          onChange={(e) => setFilterOctave(e.target.value)}
          style={{ padding: '8px 12px', background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none' }}
        >
          <option value="All" style={{ background: '#1a1a2e', color: '#fff' }}>All Octaves</option>
          {[0,1,2,3,4,5,6,7,8].map(o => <option key={o} value={o} style={{ background: '#1a1a2e', color: '#fff' }}>Octave {o}</option>)}
        </select>
      </div>

      {/* Range legend */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {[
          { label: 'Sub (0-27)', color: '#ef4444' },
          { label: 'Bass (28-48)', color: '#f97316' },
          { label: 'Mid (49-72)', color: '#22d3ee' },
          { label: 'High (73-96)', color: '#a78bfa' },
          { label: 'Air (97+)', color: '#e879f9' },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: r.color, opacity: 0.7 }} />
            <span style={{ fontSize: '9px', color: '#666' }}>{r.label}</span>
          </div>
        ))}
        <span style={{ fontSize: '9px', color: '#555', marginLeft: 'auto' }}>tap to copy</span>
      </div>

      {/* MIDI grid */}
      <div data-testid="midi-grid" style={{
        display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '3px',
        maxHeight: '340px', overflowY: 'auto', padding: '4px',
      }}>
        {filtered.map((n) => {
          const isBlack = n.note.includes('#');
          const isLandmark = landmarks.some(l => l.midi === n.midi);
          const range = getRangeInfo(n.midi);
          const isCopied = copiedMidi === n.midi;
          return (
            <div
              key={n.midi}
              onClick={() => copyToClipboard(n.midi, n.midi)}
              style={{
                padding: '6px 4px', textAlign: 'center', borderRadius: '6px', cursor: 'pointer',
                background: isCopied ? 'rgba(34,211,238,0.2)' : isLandmark ? 'rgba(255,0,255,0.12)' : range.bg,
                border: `1px solid ${isCopied ? '#22d3ee' : isLandmark ? 'rgba(255,0,255,0.3)' : range.border}`,
                transition: 'all 0.15s ease',
              }}
              title={`Click to copy MIDI ${n.midi}`}
            >
              <div style={{ fontSize: '10px', fontWeight: 700, color: isCopied ? '#22d3ee' : isLandmark ? '#ff66ff' : isBlack ? '#999' : '#ccc' }}>
                {isCopied ? 'Copied!' : n.note}
              </div>
              <div style={{ fontSize: '9px', color: isCopied ? '#22d3ee' : range.color, fontWeight: 500 }}>{n.midi}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════
// NEW PRODUCER TOOLS
// ═══════════════════════════════════════════════════

const BpmDelayCalculator = () => {
  const [bpm, setBpm] = useState(128);

  const calcMs = (beats) => {
    if (bpm <= 0) return 0;
    return Math.round((60000 / bpm) * beats * 100) / 100;
  };

  const divisions = [
    { label: '1/1 (Whole)', beats: 4 },
    { label: '1/2 (Half)', beats: 2 },
    { label: '1/4 (Quarter)', beats: 1 },
    { label: '1/8 (Eighth)', beats: 0.5 },
    { label: '1/16 (Sixteenth)', beats: 0.25 },
    { label: '1/32 (32nd)', beats: 0.125 },
  ];

  const dotted = (ms) => Math.round(ms * 1.5 * 100) / 100;
  const triplet = (ms) => Math.round(ms * (2/3) * 100) / 100;

  return (
    <div data-testid="bpm-delay-calculator-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Sync your delay, reverb & LFO to tempo — all values in milliseconds</p>

      {/* BPM Input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
        <label style={{ fontSize: '12px', fontWeight: 700, color: '#ff66ff' }}>BPM</label>
        <input
          data-testid="delay-bpm-input"
          type="number"
          value={bpm}
          onChange={(e) => setBpm(Math.max(1, parseInt(e.target.value) || 1))}
          style={{ width: '80px', padding: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,0,255,0.3)', borderRadius: '8px', color: '#ff66ff', fontSize: '18px', fontWeight: 800, textAlign: 'center', outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: '4px' }}>
          {[120, 124, 128, 130, 140, 174].map(v => (
            <button key={v} onClick={() => setBpm(v)} data-testid={`delay-preset-${v}`} style={{
              padding: '5px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none',
              background: bpm === v ? 'rgba(255,0,255,0.2)' : 'rgba(255,255,255,0.04)',
              color: bpm === v ? '#ff00ff' : '#888',
            }}>{v}</button>
          ))}
        </div>
      </div>

      {/* Delay table */}
      <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '8px 12px', background: 'rgba(255,0,255,0.08)' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#ff66ff' }}>Division</span>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#ff66ff', textAlign: 'center' }}>Normal</span>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#ff66ff', textAlign: 'center' }}>Dotted</span>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#ff66ff', textAlign: 'center' }}>Triplet</span>
        </div>
        {divisions.map((div, i) => {
          const ms = calcMs(div.beats);
          return (
            <div key={i} data-testid={`delay-row-${i}`} style={{
              display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', padding: '10px 12px',
              background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
              borderTop: '1px solid rgba(255,255,255,0.04)',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#ddd' }}>{div.label}</span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff', textAlign: 'center', fontFamily: 'monospace' }}>{ms}</span>
              <span style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', fontFamily: 'monospace' }}>{dotted(ms)}</span>
              <span style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', fontFamily: 'monospace' }}>{triplet(ms)}</span>
            </div>
          );
        })}
      </div>

      {/* Hz conversion */}
      <div style={{ marginTop: '1rem', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: '#888', marginBottom: '6px' }}>LFO RATES (Hz)</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {divisions.slice(2).map((div, i) => {
            const hz = Math.round((bpm / 60) / div.beats * 100) / 100;
            return (
              <div key={i} style={{ padding: '4px 10px', borderRadius: '6px', background: 'rgba(255,0,255,0.06)', fontSize: '10px' }}>
                <span style={{ color: '#ff66ff', fontWeight: 700 }}>{div.label.split(' ')[0]}</span>
                <span style={{ color: '#888' }}> = {hz} Hz</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SAMPLE_CLEARANCE_STEPS = [
  { id: 'identify', label: 'Identify the original sample', desc: 'Document the exact track, artist, label, and timestamp of the sample used', category: 'Research' },
  { id: 'ownership', label: 'Research copyright ownership', desc: 'Find who owns the master recording AND the composition (often different entities)', category: 'Research' },
  { id: 'publisher', label: 'Contact the publisher', desc: 'Reach out to the music publisher about a sync/sample license', category: 'Contact' },
  { id: 'master', label: 'Contact the master owner', desc: 'Reach out to the record label or master rights holder', category: 'Contact' },
  { id: 'negotiate', label: 'Negotiate terms', desc: 'Agree on royalty split, flat fee, or advance. Get everything in writing.', category: 'Legal' },
  { id: 'written', label: 'Obtain written clearance', desc: 'Get a signed agreement from ALL rights holders before release', category: 'Legal' },
  { id: 'credit', label: 'Arrange proper credits', desc: 'Confirm how the original artist/writer must be credited', category: 'Metadata' },
  { id: 'metadata', label: 'Update release metadata', desc: 'Include sample credits in ISRC, liner notes, and distribution metadata', category: 'Metadata' },
  { id: 'distribute', label: 'Submit to distributor with documentation', desc: 'Upload clearance proof alongside your release to avoid takedowns', category: 'Release' },
];

const SampleClearanceChecklist = () => {
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState('');

  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  const completedCount = Object.values(checked).filter(Boolean).length;
  const totalSteps = SAMPLE_CLEARANCE_STEPS.length;
  const riskLevel = completedCount === totalSteps ? 'clear' : completedCount >= 6 ? 'low' : completedCount >= 3 ? 'medium' : 'high';
  const riskColors = { clear: '#00ff88', low: '#00ffff', medium: '#ffaa00', high: '#ff3333' };
  const riskLabels = { clear: 'CLEARED', low: 'LOW RISK', medium: 'MEDIUM RISK', high: 'HIGH RISK' };

  return (
    <div data-testid="sample-clearance-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Step-by-step guide to legally clear samples before release</p>

      {/* Risk indicator */}
      <div data-testid="clearance-risk" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', borderRadius: '10px', marginBottom: '1.25rem',
        background: `${riskColors[riskLevel]}08`, border: `1px solid ${riskColors[riskLevel]}30`,
      }}>
        <div>
          <span style={{ fontSize: '12px', fontWeight: 700, color: riskColors[riskLevel] }}>{riskLabels[riskLevel]}</span>
          <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>{completedCount}/{totalSteps} steps complete</span>
        </div>
        <div style={{ width: '100px', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${(completedCount / totalSteps) * 100}%`, height: '100%', background: riskColors[riskLevel], borderRadius: '4px', transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '1rem' }}>
        {SAMPLE_CLEARANCE_STEPS.map(step => (
          <button
            key={step.id}
            onClick={() => toggle(step.id)}
            data-testid={`clearance-step-${step.id}`}
            style={{
              width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', cursor: 'pointer',
              borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '10px',
              background: checked[step.id] ? 'rgba(0,255,136,0.04)' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
            }}
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, marginTop: '1px',
              background: checked[step.id] ? '#00ff88' : 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '10px', fontWeight: 800, color: '#000',
              transition: 'all 0.2s',
            }}>{checked[step.id] ? '✓' : ''}</div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: checked[step.id] ? '#00ff88' : '#ddd', textDecoration: checked[step.id] ? 'line-through' : 'none' }}>{step.label}</div>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{step.desc}</div>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: '9px', color: '#555', fontWeight: 600, flexShrink: 0 }}>{step.category}</span>
          </button>
        ))}
      </div>

      {/* Notes */}
      <textarea
        data-testid="clearance-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (track name, rights holders, contact info, deal terms...)"
        style={{
          width: '100%', minHeight: '80px', padding: '10px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
        }}
      />
    </div>
  );
};

const RELEASE_CHECKLIST_PRESETS = [
  { id: 'master-lufs', label: 'Mastering: LUFS level check (-14 to -8 LUFS)', category: 'Audio' },
  { id: 'master-peak', label: 'Mastering: True peak below -1dBTP', category: 'Audio' },
  { id: 'master-format', label: 'Export: WAV 44.1kHz / 24-bit minimum', category: 'Audio' },
  { id: 'master-mp3', label: 'Export: MP3 320kbps for previews', category: 'Audio' },
  { id: 'meta-title', label: 'Track title finalized', category: 'Metadata' },
  { id: 'meta-artist', label: 'Artist name(s) confirmed', category: 'Metadata' },
  { id: 'meta-genre', label: 'Genre/subgenre tags set', category: 'Metadata' },
  { id: 'meta-isrc', label: 'ISRC code obtained', category: 'Metadata' },
  { id: 'meta-bpm', label: 'BPM and key tagged', category: 'Metadata' },
  { id: 'art-cover', label: 'Cover artwork (3000x3000 min, JPG/PNG)', category: 'Artwork' },
  { id: 'art-promo', label: 'Promo graphics for socials', category: 'Artwork' },
  { id: 'dist-platform', label: 'Distributor selected (DistroKid, TuneCore, etc.)', category: 'Distribution' },
  { id: 'dist-upload', label: 'Track uploaded to distributor', category: 'Distribution' },
  { id: 'dist-date', label: 'Release date set (2-4 weeks ahead)', category: 'Distribution' },
  { id: 'dist-presave', label: 'Pre-save link created', category: 'Distribution' },
  { id: 'promo-send', label: 'Promos sent to DJs/blogs/playlists', category: 'Promo' },
  { id: 'promo-socials', label: 'Social media teasers scheduled', category: 'Promo' },
  { id: 'promo-bio', label: 'Press release / bio updated', category: 'Promo' },
];

const ReleasePrepChecklist = () => {
  const [checklists, setChecklists] = useState([]);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('beatseek_token');

  useEffect(() => {
    const load = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tools/release-checklists`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setChecklists(data.checklists || []);
            setLoading(false);
            return;
          }
        } catch (e) { console.error('Failed to load checklists:', e); }
      }
      try { setChecklists(JSON.parse(localStorage.getItem('beatseek_release_checklists') || '[]')); } catch { setChecklists([]); }
      setLoading(false);
    };
    load();
  }, []);

  const createChecklist = async () => {
    if (!newTitle.trim()) return;
    const items = RELEASE_CHECKLIST_PRESETS.map(p => ({ ...p, checked: false }));
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/release-checklists`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: newTitle, items, notes: '' }),
        });
        if (res.ok) {
          const data = await res.json();
          setChecklists(prev => [data.checklist, ...prev]);
          setActiveChecklist(data.checklist.id);
          setNewTitle('');
          setShowNew(false);
          return;
        }
      } catch (e) { console.error('Failed to save checklist:', e); }
    }
    const cl = { id: String(Date.now()), title: newTitle, items, notes: '', created_at: new Date().toISOString() };
    const updated = [cl, ...checklists];
    setChecklists(updated);
    setActiveChecklist(cl.id);
    localStorage.setItem('beatseek_release_checklists', JSON.stringify(updated));
    setNewTitle('');
    setShowNew(false);
  };

  const toggleItem = async (checklistId, itemId) => {
    const updated = checklists.map(cl => {
      if (cl.id !== checklistId) return cl;
      return { ...cl, items: cl.items.map(it => it.id === itemId ? { ...it, checked: !it.checked } : it) };
    });
    setChecklists(updated);
    localStorage.setItem('beatseek_release_checklists', JSON.stringify(updated));
    const token = getToken();
    const cl = updated.find(c => c.id === checklistId);
    if (token && cl) {
      fetch(`${BACKEND_URL}/api/tools/release-checklists/${checklistId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cl.items }),
      }).catch(e => console.error('Failed to sync checklist:', e));
    }
  };

  const deleteChecklist = async (id) => {
    const token = getToken();
    if (token) {
      fetch(`${BACKEND_URL}/api/tools/release-checklists/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(e => console.error('Failed to delete checklist:', e));
    }
    const updated = checklists.filter(c => c.id !== id);
    setChecklists(updated);
    localStorage.setItem('beatseek_release_checklists', JSON.stringify(updated));
    if (activeChecklist === id) setActiveChecklist(null);
  };

  const active = checklists.find(c => c.id === activeChecklist);
  const completedCount = active ? active.items.filter(it => it.checked).length : 0;
  const totalItems = active ? active.items.length : 0;

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#555' }}>Loading...</div>;

  return (
    <div data-testid="release-prep-checklist-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Track your release workflow — synced to your account</p>

      {!activeChecklist ? (
        <>
          {/* Checklist list */}
          <button onClick={() => setShowNew(!showNew)} data-testid="release-new-btn" style={{
            width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: showNew ? 'rgba(255,51,51,0.15)' : 'rgba(255,0,255,0.1)', color: showNew ? '#ff3333' : '#ff00ff',
            marginBottom: '1rem', transition: 'all 0.2s',
          }}>{showNew ? 'Cancel' : '+ New Release'}</button>

          {showNew && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
              <input data-testid="release-title-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createChecklist()} placeholder="Release name..." style={{ flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none' }} />
              <button onClick={createChecklist} style={{ padding: '10px 20px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #ff00ff, #cc00cc)', color: '#fff' }}>Create</button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {checklists.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#555', fontSize: '12px' }}>No releases yet — create one to start tracking</div>}
            {checklists.map(cl => {
              const done = cl.items.filter(it => it.checked).length;
              const pct = Math.round((done / cl.items.length) * 100);
              return (
                <div key={cl.id} data-testid={`release-item-${cl.id}`} onClick={() => setActiveChecklist(cl.id)} style={{
                  padding: '14px', borderRadius: '10px', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{cl.title}</div>
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{done}/{cl.items.length} — {new Date(cl.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ width: '50px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: pct === 100 ? '#00ff88' : '#ff66ff' }}>{pct}%</div>
                  <button onClick={(e) => { e.stopPropagation(); deleteChecklist(cl.id); }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '14px' }}>×</button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* Active checklist view */}
          <button onClick={() => setActiveChecklist(null)} data-testid="release-back-btn" style={{
            padding: '6px 14px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none',
            background: 'rgba(255,255,255,0.06)', color: '#888', marginBottom: '1rem',
          }}>&#8592; Back to releases</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{active?.title}</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: completedCount === totalItems ? '#00ff88' : '#ff66ff' }}>{completedCount}/{totalItems}</span>
          </div>

          <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ width: `${totalItems > 0 ? (completedCount / totalItems) * 100 : 0}%`, height: '100%', background: completedCount === totalItems ? '#00ff88' : 'linear-gradient(90deg, #ff00ff, #ff66ff)', borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {active?.items.map(item => (
              <button key={item.id} onClick={() => toggleItem(activeChecklist, item.id)} data-testid={`release-check-${item.id}`} style={{
                width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', cursor: 'pointer',
                borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px',
                background: item.checked ? 'rgba(0,255,136,0.03)' : 'rgba(255,255,255,0.02)',
                transition: 'all 0.2s',
              }}>
                <div style={{
                  width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                  background: item.checked ? '#00ff88' : 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '9px', fontWeight: 800, color: '#000',
                }}>{item.checked ? '✓' : ''}</div>
                <span style={{ fontSize: '12px', color: item.checked ? '#00ff88' : '#ddd', textDecoration: item.checked ? 'line-through' : 'none', flex: 1 }}>{item.label}</span>
                <span style={{ fontSize: '9px', color: '#555', fontWeight: 600 }}>{item.category}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const COMMON_SYNTHS = ['Serum', 'Massive X', 'Vital', 'Sylenth1', 'Diva', 'Phase Plant', 'Pigments', 'Omnisphere', 'Analog Lab', 'FM8', 'Zebra', 'Other'];
const PATCH_TAGS = ['Bass', 'Lead', 'Pad', 'Pluck', 'Key', 'FX', 'Arp', 'Sub', 'Chord', 'Texture', 'Riser', 'Vocal'];

const SynthPatchNotes = () => {
  const [patches, setPatches] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [filterSynth, setFilterSynth] = useState('All');
  const [filterTag, setFilterTag] = useState('All');
  const [newPatch, setNewPatch] = useState({ synth: 'Serum', patch_name: '', oscillators: '', filter_notes: '', modulation: '', tags: [], notes: '' });
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('beatseek_token');

  useEffect(() => {
    const load = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tools/patch-notes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setPatches(data.patches || []);
            setLoading(false);
            return;
          }
        } catch (e) { console.error('Failed to load patches:', e); }
      }
      try { setPatches(JSON.parse(localStorage.getItem('beatseek_patch_notes') || '[]')); } catch { setPatches([]); }
      setLoading(false);
    };
    load();
  }, []);

  const toggleTag = (tag) => {
    setNewPatch(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  const savePatch = async () => {
    if (!newPatch.patch_name.trim()) return;
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/patch-notes`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newPatch),
        });
        if (res.ok) {
          const data = await res.json();
          setPatches(prev => [data.patch, ...prev]);
          setNewPatch({ synth: 'Serum', patch_name: '', oscillators: '', filter_notes: '', modulation: '', tags: [], notes: '' });
          setShowAdd(false);
          return;
        }
      } catch (e) { console.error('Failed to save patch:', e); }
    }
    const patch = { ...newPatch, id: String(Date.now()), created_at: new Date().toISOString() };
    const updated = [patch, ...patches];
    setPatches(updated);
    localStorage.setItem('beatseek_patch_notes', JSON.stringify(updated));
    setNewPatch({ synth: 'Serum', patch_name: '', oscillators: '', filter_notes: '', modulation: '', tags: [], notes: '' });
    setShowAdd(false);
  };

  const deletePatch = async (id) => {
    const token = getToken();
    if (token) {
      fetch(`${BACKEND_URL}/api/tools/patch-notes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(e => console.error('Failed to delete patch:', e));
    }
    const updated = patches.filter(p => p.id !== id);
    setPatches(updated);
    localStorage.setItem('beatseek_patch_notes', JSON.stringify(updated));
  };

  const usedSynths = ['All', ...new Set(patches.map(p => p.synth))];
  const filtered = patches.filter(p => {
    if (filterSynth !== 'All' && p.synth !== filterSynth) return false;
    if (filterTag !== 'All' && !(p.tags || []).includes(filterTag)) return false;
    return true;
  });

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#555' }}>Loading...</div>;

  return (
    <div data-testid="synth-patch-notes-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Document your patches so you never forget how you made a sound</p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select data-testid="patch-filter-synth" value={filterSynth} onChange={(e) => setFilterSynth(e.target.value)} style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}>
          {usedSynths.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="patch-filter-tag" value={filterTag} onChange={(e) => setFilterTag(e.target.value)} style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}>
          <option value="All">All Tags</option>
          {PATCH_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Add button */}
      <button onClick={() => setShowAdd(!showAdd)} data-testid="patch-add-toggle" style={{
        width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
        background: showAdd ? 'rgba(255,51,51,0.15)' : 'rgba(255,0,255,0.1)', color: showAdd ? '#ff3333' : '#ff00ff',
        marginBottom: '1rem', transition: 'all 0.2s',
      }}>{showAdd ? 'Cancel' : '+ New Patch'}</button>

      {/* Add form */}
      {showAdd && (
        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <select data-testid="patch-synth-select" value={newPatch.synth} onChange={(e) => setNewPatch(p => ({ ...p, synth: e.target.value }))} style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}>
              {COMMON_SYNTHS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="patch-name-input" value={newPatch.patch_name} onChange={(e) => setNewPatch(p => ({ ...p, patch_name: e.target.value }))} placeholder="Patch name..." style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
          </div>

          {/* Tags */}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {PATCH_TAGS.map(tag => (
              <button key={tag} onClick={() => toggleTag(tag)} style={{
                padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '12px', cursor: 'pointer', border: 'none',
                background: newPatch.tags.includes(tag) ? 'rgba(255,0,255,0.2)' : 'rgba(255,255,255,0.04)',
                color: newPatch.tags.includes(tag) ? '#ff00ff' : '#888',
              }}>{tag}</button>
            ))}
          </div>

          <input data-testid="patch-osc" value={newPatch.oscillators} onChange={(e) => setNewPatch(p => ({ ...p, oscillators: e.target.value }))} placeholder="Oscillator setup (e.g. 'Saw + Sub, detuned +7')" style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', marginBottom: '6px', boxSizing: 'border-box' }} />
          <input data-testid="patch-filter" value={newPatch.filter_notes} onChange={(e) => setNewPatch(p => ({ ...p, filter_notes: e.target.value }))} placeholder="Filter settings (e.g. 'LP 24dB, cutoff 500Hz, reso 30%')" style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', marginBottom: '6px', boxSizing: 'border-box' }} />
          <input data-testid="patch-mod" value={newPatch.modulation} onChange={(e) => setNewPatch(p => ({ ...p, modulation: e.target.value }))} placeholder="Modulation (e.g. 'LFO1 → cutoff, rate 1/8')" style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', marginBottom: '6px', boxSizing: 'border-box' }} />
          <textarea data-testid="patch-notes-text" value={newPatch.notes} onChange={(e) => setNewPatch(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." style={{ width: '100%', minHeight: '50px', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', resize: 'vertical', marginBottom: '8px', boxSizing: 'border-box' }} />

          <button onClick={savePatch} data-testid="patch-save-btn" style={{ width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #ff00ff, #cc00cc)', color: '#fff' }}>Save Patch</button>
        </div>
      )}

      {/* Patch list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto' }}>
        {filtered.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#555', fontSize: '12px' }}>No patches saved yet — start documenting!</div>}
        {filtered.map(patch => (
          <div key={patch.id} data-testid={`patch-entry-${patch.id}`} style={{
            padding: '12px 14px', borderRadius: '10px',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{patch.patch_name}</span>
                <span style={{ fontSize: '10px', color: '#ff66ff', marginLeft: '8px', fontWeight: 600 }}>{patch.synth}</span>
              </div>
              <button onClick={() => deletePatch(patch.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '14px' }}>×</button>
            </div>
            {(patch.tags || []).length > 0 && (
              <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
                {patch.tags.map(t => <span key={t} style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(255,0,255,0.1)', color: '#cc66ff', fontWeight: 600 }}>{t}</span>)}
              </div>
            )}
            {patch.oscillators && <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}><span style={{ color: '#888', fontWeight: 600 }}>OSC:</span> {patch.oscillators}</div>}
            {patch.filter_notes && <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}><span style={{ color: '#888', fontWeight: 600 }}>FLT:</span> {patch.filter_notes}</div>}
            {patch.modulation && <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}><span style={{ color: '#888', fontWeight: 600 }}>MOD:</span> {patch.modulation}</div>}
            {patch.notes && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>{patch.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════
// STREAMER TOOLS
// ═══════════════════════════════════════════════════

const TITLE_TEMPLATES = {
  genres: ['Deep House', 'Techno', 'Trance', 'Drum & Bass', 'House', 'Minimal', 'Progressive', 'Garage', 'Ambient', 'Dubstep', 'Acid', 'Breaks'],
  vibes: ['Late Night', 'Sunday Chill', 'Peak Time', 'After Hours', 'Morning Session', 'Sunset Set', 'Underground', 'Festival Energy', 'Dark Room', 'Poolside'],
  formats: ['Vinyl Only', 'All Vinyl', 'Digital Set', 'B2B', 'Live Production', 'Live Mix', 'DJ Set', 'Extended Set', 'Classics Only', 'New Music Only'],
  ctas: ['!socials', '!schedule', 'Come hang!', 'Drop a follow!', 'Requests open!', 'Chat & chill', 'Type !song for track ID', '!discord'],
  emojis: ['🎧', '🔊', '🎵', '🎶', '💿', '🎚️', '🎛️', '🔥', '✨', '🌙', '☀️', '🌊', '⚡', '💜', '🖤'],
};

const StreamTitleGenerator = () => {
  const [genre, setGenre] = useState('');
  const [vibe, setVibe] = useState('');
  const [format, setFormat] = useState('');
  const [cta, setCta] = useState('');
  const [customText, setCustomText] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState('🎧');
  const [generated, setGenerated] = useState([]);
  const [copied, setCopied] = useState(null);

  const generate = () => {
    const g = genre || TITLE_TEMPLATES.genres[Math.floor(Math.random() * TITLE_TEMPLATES.genres.length)];
    const v = vibe || TITLE_TEMPLATES.vibes[Math.floor(Math.random() * TITLE_TEMPLATES.vibes.length)];
    const f = format || TITLE_TEMPLATES.formats[Math.floor(Math.random() * TITLE_TEMPLATES.formats.length)];
    const c = cta || TITLE_TEMPLATES.ctas[Math.floor(Math.random() * TITLE_TEMPLATES.ctas.length)];
    const custom = customText ? ` | ${customText}` : '';
    const titles = [
      `${selectedEmoji} ${g} ${f} | ${v} | ${c}${custom}`,
      `${selectedEmoji} ${v.toUpperCase()} — ${g} | ${f}${custom} | ${c}`,
      `${g} | ${v} Vibes ${selectedEmoji} ${f}${custom} — ${c}`,
      `${selectedEmoji}${selectedEmoji} ${g.toUpperCase()} ${v.toUpperCase()} ${selectedEmoji}${selectedEmoji} | ${f}${custom} | ${c}`,
      `[${f}] ${g} — ${v}${custom} ${selectedEmoji} ${c}`,
    ];
    setGenerated(titles);
    setCopied(null);
  };

  const copyTitle = (title, idx) => {
    navigator.clipboard.writeText(title);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div data-testid="stream-title-generator-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Generate catchy stream titles — pick or randomize each element</p>

      {/* Emoji picker row */}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {TITLE_TEMPLATES.emojis.map(e => (
          <button key={e} onClick={() => setSelectedEmoji(e)} style={{
            width: '32px', height: '32px', fontSize: '16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
            background: selectedEmoji === e ? 'rgba(0,255,255,0.2)' : 'rgba(255,255,255,0.03)',
            transition: 'all 0.2s',
          }}>{e}</button>
        ))}
      </div>

      {/* Dropdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '1rem' }}>
        {[
          { label: 'Genre', value: genre, setter: setGenre, options: TITLE_TEMPLATES.genres },
          { label: 'Vibe', value: vibe, setter: setVibe, options: TITLE_TEMPLATES.vibes },
          { label: 'Format', value: format, setter: setFormat, options: TITLE_TEMPLATES.formats },
          { label: 'CTA', value: cta, setter: setCta, options: TITLE_TEMPLATES.ctas },
        ].map(({ label, value, setter, options }) => (
          <div key={label}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>{label}</div>
            <select
              data-testid={`title-select-${label.toLowerCase()}`}
              value={value}
              onChange={(e) => setter(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none' }}
            >
              <option value="">Random</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>

      {/* Custom text */}
      <input
        data-testid="title-custom-text"
        value={customText}
        onChange={(e) => setCustomText(e.target.value)}
        placeholder="Add custom text (optional)..."
        style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none', marginBottom: '1rem', boxSizing: 'border-box' }}
      />

      <button
        onClick={generate}
        data-testid="title-generate-btn"
        style={{
          width: '100%', padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
          background: 'linear-gradient(135deg, #00ffff, #00ccff)', color: '#000',
          boxShadow: '0 4px 16px rgba(0,255,255,0.25)', marginBottom: '1.25rem',
        }}
      >Generate Titles</button>

      {/* Results */}
      {generated.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {generated.map((title, i) => (
            <div key={i} onClick={() => copyTitle(title, i)} data-testid={`title-result-${i}`} style={{
              padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
              background: copied === i ? 'rgba(0,255,136,0.08)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${copied === i ? 'rgba(0,255,136,0.2)' : 'rgba(255,255,255,0.06)'}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
              transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: '12px', color: '#ddd', lineHeight: '1.4', flex: 1 }}>{title}</span>
              <span style={{ fontSize: '10px', color: copied === i ? '#00ff88' : '#555', flexShrink: 0, fontWeight: 600 }}>
                {copied === i ? 'Copied!' : 'Copy'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CHAT_RULE_PRESETS = [
  { id: 'respect', text: 'Be respectful to everyone in chat', category: 'Behavior' },
  { id: 'no-spam', text: 'No spamming or excessive caps', category: 'Behavior' },
  { id: 'no-promo', text: 'No self-promotion or advertising', category: 'Behavior' },
  { id: 'english', text: 'English in chat please', category: 'Language' },
  { id: 'no-politics', text: 'No politics or controversial topics', category: 'Topics' },
  { id: 'no-requests', text: 'No song requests unless specified', category: 'Music' },
  { id: 'use-songid', text: 'Use !song to find out the current track', category: 'Music' },
  { id: 'no-backseat', text: 'No backseat DJing — let the DJ do their thing', category: 'Music' },
  { id: 'have-fun', text: 'Most importantly — have fun and enjoy the music!', category: 'Vibe' },
  { id: 'mods-final', text: 'Mods have the final say — respect their decisions', category: 'Moderation' },
  { id: 'no-hate', text: 'Zero tolerance for hate speech, racism, or harassment', category: 'Behavior' },
  { id: 'age', text: '18+ content — viewer discretion advised', category: 'General' },
];

const ChatRulesGenerator = () => {
  const [selectedRules, setSelectedRules] = useState(['respect', 'no-spam', 'have-fun']);
  const [channelName, setChannelName] = useState('');
  const [customRule, setCustomRule] = useState('');
  const [customRules, setCustomRules] = useState([]);
  const [copied, setCopied] = useState(false);

  const toggleRule = (id) => {
    setSelectedRules(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const addCustomRule = () => {
    if (customRule.trim()) {
      setCustomRules(prev => [...prev, customRule.trim()]);
      setCustomRule('');
    }
  };

  const removeCustomRule = (idx) => {
    setCustomRules(prev => prev.filter((_, i) => i !== idx));
  };

  const exportRules = () => {
    const header = channelName ? `${channelName} — Chat Rules` : 'Chat Rules';
    const divider = '━'.repeat(30);
    const preset = selectedRules.map((id, i) => {
      const rule = CHAT_RULE_PRESETS.find(r => r.id === id);
      return rule ? `${i + 1}. ${rule.text}` : null;
    }).filter(Boolean);
    const custom = customRules.map((r, i) => `${preset.length + i + 1}. ${r}`);
    const all = [...preset, ...custom];
    const text = `${header}\n${divider}\n${all.join('\n')}\n${divider}\nEnjoy the stream! ♪`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="chat-rules-generator-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Build formatted chat rules for your channel — copy for Twitch/YouTube panels</p>

      <input
        data-testid="chat-rules-channel"
        value={channelName}
        onChange={(e) => setChannelName(e.target.value)}
        placeholder="Channel name (optional)..."
        style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none', marginBottom: '1rem', boxSizing: 'border-box' }}
      />

      {/* Preset rules */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '1rem' }}>
        {CHAT_RULE_PRESETS.map(rule => (
          <button
            key={rule.id}
            onClick={() => toggleRule(rule.id)}
            data-testid={`chat-rule-${rule.id}`}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', cursor: 'pointer',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px',
              background: selectedRules.includes(rule.id) ? 'rgba(0,255,255,0.06)' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
            }}
          >
            <div style={{
              width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
              background: selectedRules.includes(rule.id) ? '#00ffff' : 'rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', fontWeight: 800, color: '#000',
            }}>{selectedRules.includes(rule.id) ? '✓' : ''}</div>
            <span style={{ fontSize: '12px', color: selectedRules.includes(rule.id) ? '#ddd' : '#777' }}>{rule.text}</span>
            <span style={{ marginLeft: 'auto', fontSize: '9px', color: '#555', fontWeight: 600 }}>{rule.category}</span>
          </button>
        ))}
      </div>

      {/* Custom rules */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          data-testid="chat-rules-custom-input"
          value={customRule}
          onChange={(e) => setCustomRule(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addCustomRule()}
          placeholder="Add a custom rule..."
          style={{ flex: 1, padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none' }}
        />
        <button onClick={addCustomRule} style={{
          padding: '8px 16px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
          background: 'rgba(0,255,255,0.15)', color: '#00ffff',
        }}>Add</button>
      </div>
      {customRules.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '1rem' }}>
          {customRules.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'rgba(0,255,255,0.04)', borderRadius: '6px' }}>
              <span style={{ flex: 1, fontSize: '12px', color: '#ccc' }}>{r}</span>
              <button onClick={() => removeCustomRule(i)} style={{ background: 'none', border: 'none', color: '#ff3333', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>×</button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={exportRules}
        data-testid="chat-rules-copy-btn"
        style={{
          width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
          background: copied ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,255,0.1)',
          color: copied ? '#00ff88' : '#00ffff', transition: 'all 0.2s',
        }}
      >{copied ? 'Copied!' : `Copy Rules (${selectedRules.length + customRules.length})`}</button>
    </div>
  );
};

const RaidTracker = () => {
  const [raids, setRaids] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newRaid, setNewRaid] = useState({ name: '', viewers: '', direction: 'incoming', notes: '' });
  const [syncing, setSyncing] = useState(false);

  const getToken = () => localStorage.getItem('beatseek_token');

  // Load raids on mount
  useEffect(() => {
    const loadRaids = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tools/raids`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setRaids(data.raids || []);
            return;
          }
        } catch (e) { console.error('Failed to load raids from server:', e); }
      }
      // Fallback to localStorage
      try { setRaids(JSON.parse(localStorage.getItem('beatseek_raid_tracker') || '[]')); } catch { setRaids([]); }
    };
    loadRaids();
  }, []);

  const addRaid = async () => {
    if (!newRaid.name.trim()) return;
    setSyncing(true);
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/raids`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newRaid.name, viewers: parseInt(newRaid.viewers) || 0, direction: newRaid.direction, notes: newRaid.notes }),
        });
        if (res.ok) {
          const data = await res.json();
          setRaids(prev => [data.raid, ...prev]);
          setNewRaid({ name: '', viewers: '', direction: 'incoming', notes: '' });
          setShowAdd(false);
          setSyncing(false);
          return;
        }
      } catch (e) { console.error('Failed to save raid to server:', e); }
    }
    // Fallback to localStorage
    const raid = { ...newRaid, id: String(Date.now()), viewers: parseInt(newRaid.viewers) || 0, date: new Date().toISOString() };
    const updated = [raid, ...raids];
    setRaids(updated);
    localStorage.setItem('beatseek_raid_tracker', JSON.stringify(updated));
    setNewRaid({ name: '', viewers: '', direction: 'incoming', notes: '' });
    setShowAdd(false);
    setSyncing(false);
  };

  const deleteRaid = async (id) => {
    const token = getToken();
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/tools/raids/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (e) { console.error('Failed to delete raid from server:', e); }
    }
    const updated = raids.filter(r => r.id !== id);
    setRaids(updated);
    localStorage.setItem('beatseek_raid_tracker', JSON.stringify(updated));
  };

  const incomingCount = raids.filter(r => r.direction === 'incoming').length;
  const outgoingCount = raids.filter(r => r.direction === 'outgoing').length;

  return (
    <div data-testid="raid-tracker-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Log raids given and received — build your network</p>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
        <div style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(0,255,255,0.06)', border: '1px solid rgba(0,255,255,0.15)', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#00ffff' }}>{incomingCount}</div>
          <div style={{ fontSize: '10px', color: '#888' }}>Received</div>
        </div>
        <div style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(255,0,255,0.06)', border: '1px solid rgba(255,0,255,0.15)', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#ff00ff' }}>{outgoingCount}</div>
          <div style={{ fontSize: '10px', color: '#888' }}>Given</div>
        </div>
        <div style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff' }}>{raids.length}</div>
          <div style={{ fontSize: '10px', color: '#888' }}>Total</div>
        </div>
      </div>

      {/* Add button */}
      <button
        onClick={() => setShowAdd(!showAdd)}
        data-testid="raid-add-toggle"
        style={{
          width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
          background: showAdd ? 'rgba(255,51,51,0.15)' : 'rgba(0,255,255,0.1)',
          color: showAdd ? '#ff3333' : '#00ffff', marginBottom: '1rem', transition: 'all 0.2s',
        }}
      >{showAdd ? 'Cancel' : '+ Log a Raid'}</button>

      {/* Add form */}
      {showAdd && (
        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input data-testid="raid-name" value={newRaid.name} onChange={(e) => setNewRaid(p => ({ ...p, name: e.target.value }))} placeholder="Channel name..." style={{ flex: 2, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
            <input data-testid="raid-viewers" value={newRaid.viewers} onChange={(e) => setNewRaid(p => ({ ...p, viewers: e.target.value }))} placeholder="Viewers" type="number" style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            {['incoming', 'outgoing'].map(d => (
              <button key={d} onClick={() => setNewRaid(p => ({ ...p, direction: d }))} data-testid={`raid-dir-${d}`} style={{
                flex: 1, padding: '8px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none',
                background: newRaid.direction === d ? (d === 'incoming' ? 'rgba(0,255,255,0.2)' : 'rgba(255,0,255,0.2)') : 'rgba(255,255,255,0.04)',
                color: newRaid.direction === d ? (d === 'incoming' ? '#00ffff' : '#ff00ff') : '#888',
                textTransform: 'capitalize',
              }}>{d === 'incoming' ? 'Received Raid' : 'Gave Raid'}</button>
            ))}
          </div>
          <input data-testid="raid-notes" value={newRaid.notes} onChange={(e) => setNewRaid(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)..." style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }} />
          <button onClick={addRaid} data-testid="raid-save-btn" style={{ width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #00ffff, #00ccff)', color: '#000' }}>Save Raid</button>
        </div>
      )}

      {/* Raid log */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '300px', overflowY: 'auto' }}>
        {raids.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#555', fontSize: '12px' }}>No raids logged yet — start tracking!</div>}
        {raids.map(raid => (
          <div key={raid.id} data-testid={`raid-entry-${raid.id}`} style={{
            padding: '10px 12px', borderRadius: '8px',
            background: raid.direction === 'incoming' ? 'rgba(0,255,255,0.03)' : 'rgba(255,0,255,0.03)',
            border: `1px solid ${raid.direction === 'incoming' ? 'rgba(0,255,255,0.1)' : 'rgba(255,0,255,0.1)'}`,
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontSize: '14px' }}>{raid.direction === 'incoming' ? '📥' : '📤'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>{raid.name}</span>
                {raid.viewers > 0 && <span style={{ fontSize: '10px', color: '#888' }}>{raid.viewers} viewers</span>}
              </div>
              {raid.notes && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{raid.notes}</div>}
              <div style={{ fontSize: '9px', color: '#444', marginTop: '2px' }}>{new Date(raid.date).toLocaleDateString()}</div>
            </div>
            <button onClick={() => deleteRaid(raid.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SetRecapBuilder = () => {
  const [tracks, setTracks] = useState([]);
  const [newTrack, setNewTrack] = useState({ artist: '', title: '', time: '' });
  const [copied, setCopied] = useState(false);
  const [setTitle, setSetTitle] = useState('');

  const addTrack = () => {
    if (!newTrack.title.trim()) return;
    setTracks(prev => [...prev, { ...newTrack, id: Date.now() }]);
    setNewTrack({ artist: '', title: '', time: '' });
  };

  const removeTrack = (id) => {
    setTracks(prev => prev.filter(t => t.id !== id));
  };

  const moveTrack = (idx, dir) => {
    const newArr = [...tracks];
    const target = idx + dir;
    if (target < 0 || target >= newArr.length) return;
    [newArr[idx], newArr[target]] = [newArr[target], newArr[idx]];
    setTracks(newArr);
  };

  const exportRecap = () => {
    const header = setTitle ? setTitle : 'Set Tracklist';
    const divider = '─'.repeat(40);
    const lines = tracks.map((t, i) => {
      const time = t.time ? `[${t.time}] ` : '';
      const artist = t.artist ? `${t.artist} — ` : '';
      return `${String(i + 1).padStart(2, '0')}. ${time}${artist}${t.title}`;
    });
    const text = `${header}\n${divider}\n${lines.join('\n')}\n${divider}\n${tracks.length} tracks`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="set-recap-builder-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>Build a post-stream tracklist — copy for Discord, socials, or Mixcloud</p>

      <input
        data-testid="set-recap-title"
        value={setTitle}
        onChange={(e) => setSetTitle(e.target.value)}
        placeholder="Set title (e.g. 'Deep House Sunday — Feb 2026')..."
        style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px', outline: 'none', marginBottom: '1rem', boxSizing: 'border-box' }}
      />

      {/* Add track form */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1rem' }}>
        <input data-testid="recap-time" value={newTrack.time} onChange={(e) => setNewTrack(p => ({ ...p, time: e.target.value }))} placeholder="Time" style={{ width: '60px', padding: '8px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
        <input data-testid="recap-artist" value={newTrack.artist} onChange={(e) => setNewTrack(p => ({ ...p, artist: e.target.value }))} placeholder="Artist..." style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
        <input data-testid="recap-title-input" value={newTrack.title} onChange={(e) => setNewTrack(p => ({ ...p, title: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && addTrack()} placeholder="Track..." style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
        <button onClick={addTrack} data-testid="recap-add-btn" style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'rgba(0,255,255,0.15)', color: '#00ffff' }}>+</button>
      </div>

      {/* Track list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '1rem', maxHeight: '280px', overflowY: 'auto' }}>
        {tracks.length === 0 && <div style={{ textAlign: 'center', padding: '2rem', color: '#555', fontSize: '12px' }}>Add tracks to build your setlist recap</div>}
        {tracks.map((t, i) => (
          <div key={t.id} data-testid={`recap-track-${i}`} style={{
            padding: '8px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <span style={{ fontSize: '10px', color: '#555', fontWeight: 700, width: '20px' }}>{String(i + 1).padStart(2, '0')}</span>
            {t.time && <span style={{ fontSize: '10px', color: '#00ffff', fontFamily: 'monospace' }}>[{t.time}]</span>}
            <div style={{ flex: 1, minWidth: 0 }}>
              {t.artist && <span style={{ fontSize: '11px', color: '#aaa' }}>{t.artist} — </span>}
              <span style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>{t.title}</span>
            </div>
            <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
              <button onClick={() => moveTrack(i, -1)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '10px', padding: '2px' }}>▲</button>
              <button onClick={() => moveTrack(i, 1)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '10px', padding: '2px' }}>▼</button>
              <button onClick={() => removeTrack(t.id)} style={{ background: 'none', border: 'none', color: '#ff3333', cursor: 'pointer', fontSize: '12px', padding: '2px 4px' }}>×</button>
            </div>
          </div>
        ))}
      </div>

      {tracks.length > 0 && (
        <button
          onClick={exportRecap}
          data-testid="recap-copy-btn"
          style={{
            width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: copied ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,255,0.1)',
            color: copied ? '#00ff88' : '#00ffff', transition: 'all 0.2s',
          }}
        >{copied ? 'Copied!' : `Copy Tracklist (${tracks.length} tracks)`}</button>
      )}
    </div>
  );
};

const ViewerGoalTracker = () => {
  const [goals, setGoals] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newGoal, setNewGoal] = useState({ name: '', target: '', current: '' });

  const getToken = () => localStorage.getItem('beatseek_token');

  // Load goals on mount
  useEffect(() => {
    const loadGoals = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/tools/goals`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setGoals(data.goals || []);
            return;
          }
        } catch (e) { console.error('Failed to load goals from server:', e); }
      }
      try { setGoals(JSON.parse(localStorage.getItem('beatseek_viewer_goals') || '[]')); } catch { setGoals([]); }
    };
    loadGoals();
  }, []);

  const addGoal = async () => {
    if (!newGoal.name.trim() || !newGoal.target) return;
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/goals`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newGoal.name, target: parseInt(newGoal.target) || 1, current: parseInt(newGoal.current) || 0 }),
        });
        if (res.ok) {
          const data = await res.json();
          setGoals(prev => [...prev, data.goal]);
          setNewGoal({ name: '', target: '', current: '' });
          setShowAdd(false);
          return;
        }
      } catch (e) { console.error('Failed to save goal to server:', e); }
    }
    // Fallback localStorage
    const goal = { id: String(Date.now()), name: newGoal.name, target: parseInt(newGoal.target) || 1, current: parseInt(newGoal.current) || 0, achieved: (parseInt(newGoal.current) || 0) >= (parseInt(newGoal.target) || 1) };
    const updated = [...goals, goal];
    setGoals(updated);
    localStorage.setItem('beatseek_viewer_goals', JSON.stringify(updated));
    setNewGoal({ name: '', target: '', current: '' });
    setShowAdd(false);
  };

  const updateCurrent = async (id, value) => {
    const val = parseInt(value) || 0;
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/goals/${id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ current: val }),
        });
        if (res.ok) {
          const data = await res.json();
          setGoals(prev => prev.map(g => g.id === id ? data.goal : g));
          return;
        }
      } catch (e) { console.error('Failed to update goal on server:', e); }
    }
    // Fallback localStorage
    const updated = goals.map(g => g.id === id ? { ...g, current: val, achieved: val >= g.target } : g);
    setGoals(updated);
    localStorage.setItem('beatseek_viewer_goals', JSON.stringify(updated));
  };

  const deleteGoal = async (id) => {
    const token = getToken();
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/tools/goals/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (e) { console.error('Failed to delete goal from server:', e); }
    }
    const updated = goals.filter(g => g.id !== id);
    setGoals(updated);
    localStorage.setItem('beatseek_viewer_goals', JSON.stringify(updated));
  };

  const addPreset = async (preset) => {
    const token = getToken();
    if (token) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/tools/goals`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: preset.name, target: preset.target, current: 0 }),
        });
        if (res.ok) {
          const data = await res.json();
          setGoals(prev => [...prev, data.goal]);
          return;
        }
      } catch (e) { console.error('Failed to save preset goal to server:', e); }
    }
    // Fallback localStorage
    const goal = { ...preset, id: String(Date.now()), current: 0, achieved: false };
    const updated = [...goals, goal];
    setGoals(updated);
    localStorage.setItem('beatseek_viewer_goals', JSON.stringify(updated));
  };

  const PRESETS = [
    { name: 'First 50 Followers', target: 50 },
    { name: 'Twitch Affiliate', target: 75 },
    { name: '100 Subscribers', target: 100 },
    { name: '500 Followers', target: 500 },
    { name: 'Twitch Partner', target: 1000 },
    { name: '10K Followers', target: 10000 },
  ];

  return (
    <div data-testid="viewer-goal-tracker-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Set milestones, track progress, celebrate wins</p>

      {/* Quick presets */}
      {goals.length === 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#666', marginBottom: '8px' }}>QUICK ADD</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {PRESETS.map((p, i) => (
              <button key={i} onClick={() => addPreset(p)} data-testid={`goal-preset-${i}`} style={{
                padding: '6px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '16px', cursor: 'pointer', border: 'none',
                background: 'rgba(0,255,255,0.06)', color: '#00cccc', transition: 'all 0.2s',
              }}>{p.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Add custom goal */}
      <button
        onClick={() => setShowAdd(!showAdd)}
        data-testid="goal-add-toggle"
        style={{
          width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
          background: showAdd ? 'rgba(255,51,51,0.15)' : 'rgba(0,255,255,0.1)',
          color: showAdd ? '#ff3333' : '#00ffff', marginBottom: '1rem', transition: 'all 0.2s',
        }}
      >{showAdd ? 'Cancel' : '+ Add Goal'}</button>

      {showAdd && (
        <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
          <input data-testid="goal-name" value={newGoal.name} onChange={(e) => setNewGoal(p => ({ ...p, name: e.target.value }))} placeholder="Goal name..." style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input data-testid="goal-current" value={newGoal.current} onChange={(e) => setNewGoal(p => ({ ...p, current: e.target.value }))} placeholder="Current #" type="number" style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
            <input data-testid="goal-target" value={newGoal.target} onChange={(e) => setNewGoal(p => ({ ...p, target: e.target.value }))} placeholder="Target #" type="number" style={{ flex: 1, padding: '8px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }} />
          </div>
          <button onClick={addGoal} data-testid="goal-save-btn" style={{ width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, #00ffff, #00ccff)', color: '#000' }}>Save Goal</button>
        </div>
      )}

      {/* Goals list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {goals.map(goal => {
          const pct = Math.min(100, Math.round((goal.current / goal.target) * 100));
          return (
            <div key={goal.id} data-testid={`goal-entry-${goal.id}`} style={{
              padding: '14px', borderRadius: '10px',
              background: goal.achieved ? 'rgba(0,255,136,0.06)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${goal.achieved ? 'rgba(0,255,136,0.2)' : 'rgba(255,255,255,0.06)'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: goal.achieved ? '#00ff88' : '#fff' }}>
                  {goal.achieved && '✓ '}{goal.name}
                </span>
                <button onClick={() => deleteGoal(goal.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '14px' }}>×</button>
              </div>

              {/* Progress bar */}
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: '4px', transition: 'width 0.5s ease',
                  background: goal.achieved ? 'linear-gradient(90deg, #00ff88, #00cc66)' : 'linear-gradient(90deg, #00ffff, #00ccff)',
                }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    value={goal.current}
                    onChange={(e) => updateCurrent(goal.id, e.target.value)}
                    data-testid={`goal-current-${goal.id}`}
                    style={{ width: '60px', padding: '4px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: '#fff', fontSize: '12px', outline: 'none', textAlign: 'center' }}
                  />
                  <span style={{ fontSize: '11px', color: '#666' }}>/ {goal.target.toLocaleString()}</span>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: goal.achieved ? '#00ff88' : '#00ffff' }}>{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ScheduleGenerator = () => {
  const [schedule, setSchedule] = useState(
    DAYS_OF_WEEK.reduce((acc, day) => ({ ...acc, [day]: { active: false, start: '20:00', end: '23:00', label: '' } }), {})
  );
  const [copied, setCopied] = useState(false);

  const toggleDay = (day) => {
    setSchedule(prev => ({ ...prev, [day]: { ...prev[day], active: !prev[day].active } }));
  };

  const updateDay = (day, field, value) => {
    setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const exportSchedule = () => {
    const lines = DAYS_OF_WEEK
      .filter(d => schedule[d].active)
      .map(d => {
        const s = schedule[d];
        return `${d}: ${s.start} – ${s.end}${s.label ? ` (${s.label})` : ''}`;
      });
    const text = lines.length > 0 ? `Stream Schedule\n${'─'.repeat(30)}\n${lines.join('\n')}` : 'No days scheduled.';
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeDays = DAYS_OF_WEEK.filter(d => schedule[d].active).length;

  return (
    <div data-testid="schedule-generator-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Build your weekly stream schedule — copy and share</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '1.25rem' }}>
        {DAYS_OF_WEEK.map(day => {
          const d = schedule[day];
          return (
            <div key={day} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px',
              background: d.active ? 'rgba(0,255,255,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${d.active ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.04)'}`,
              transition: 'all 0.2s',
            }}>
              <button
                onClick={() => toggleDay(day)}
                data-testid={`schedule-toggle-${day.toLowerCase()}`}
                style={{
                  width: '18px', height: '18px', borderRadius: '4px', border: 'none', cursor: 'pointer', flexShrink: 0,
                  background: d.active ? '#00ffff' : 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', fontWeight: 800, color: '#000',
                }}
              >{d.active ? '✓' : ''}</button>
              <span style={{ fontSize: '12px', fontWeight: 600, color: d.active ? '#fff' : '#555', width: '80px', flexShrink: 0 }}>{day.slice(0, 3)}</span>
              {d.active && (
                <>
                  <input
                    type="time" value={d.start} onChange={(e) => updateDay(day, 'start', e.target.value)}
                    style={{ padding: '4px 6px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                  />
                  <span style={{ color: '#555', fontSize: '11px' }}>to</span>
                  <input
                    type="time" value={d.end} onChange={(e) => updateDay(day, 'end', e.target.value)}
                    style={{ padding: '4px 6px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                  />
                  <input
                    placeholder="Label..."
                    value={d.label} onChange={(e) => updateDay(day, 'label', e.target.value)}
                    style={{ flex: 1, minWidth: '60px', padding: '4px 8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={exportSchedule}
          data-testid="schedule-copy-btn"
          style={{
            flex: 1, padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
            background: copied ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,255,0.1)',
            color: copied ? '#00ff88' : '#00ffff',
            transition: 'all 0.2s',
          }}
        >{copied ? 'Copied!' : `Copy Schedule (${activeDays} day${activeDays !== 1 ? 's' : ''})`}</button>
      </div>
    </div>
  );
};

const SHOUTOUT_TEMPLATES = [
  { id: 'raid', name: 'Raid Shoutout', template: 'Massive shoutout to {name}! Go check them out and drop a follow — they bring incredible vibes! {url}' },
  { id: 'collab', name: 'Collab Thank You', template: 'Big love to {name} for the amazing collab today! If you haven\'t already, give them a follow: {url}' },
  { id: 'host', name: 'Host Welcome', template: 'Welcome raiders from {name}\'s stream! Make yourselves at home — we\'re just getting started! {url}' },
  { id: 'dj', name: 'DJ Shoutout', template: 'Shoutout to {name} — absolute fire on the decks! Go follow them for more incredible sets: {url}' },
  { id: 'sub', name: 'Sub/Supporter Thanks', template: 'Huge thanks to {name} for the support! You\'re keeping the music alive — much love! {url}' },
  { id: 'mod', name: 'Mod Appreciation', template: 'Massive appreciation to {name} for keeping the chat vibes positive! Real ones know. {url}' },
];

const ShoutoutGenerator = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('raid');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const template = SHOUTOUT_TEMPLATES.find(t => t.id === selectedTemplate);
  const output = template ? template.template.replace('{name}', name || '[Name]').replace('{url}', url || '') : '';

  const copyText = () => {
    navigator.clipboard.writeText(output.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="shoutout-generator-tool" style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1.25rem' }}>Quick shoutout templates for raids, collabs & appreciation</p>

      {/* Template selector */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {SHOUTOUT_TEMPLATES.map(t => (
          <button
            key={t.id}
            onClick={() => setSelectedTemplate(t.id)}
            data-testid={`shoutout-template-${t.id}`}
            style={{
              padding: '6px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '16px', cursor: 'pointer', border: 'none',
              background: selectedTemplate === t.id ? 'rgba(0,255,255,0.2)' : 'rgba(255,255,255,0.04)',
              color: selectedTemplate === t.id ? '#00ffff' : '#888',
              transition: 'all 0.2s',
            }}
          >{t.name}</button>
        ))}
      </div>

      {/* Input fields */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
        <input
          data-testid="shoutout-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name / Channel..."
          style={{ flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
        />
        <input
          data-testid="shoutout-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Link (optional)..."
          style={{ flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
        />
      </div>

      {/* Preview */}
      <div data-testid="shoutout-preview" style={{
        padding: '16px', borderRadius: '10px', marginBottom: '1rem',
        background: 'rgba(0,255,255,0.04)', border: '1px solid rgba(0,255,255,0.15)',
        fontSize: '13px', color: '#ddd', lineHeight: '1.6',
      }}>
        {output}
      </div>

      {/* Copy button */}
      <button
        onClick={copyText}
        data-testid="shoutout-copy-btn"
        style={{
          width: '100%', padding: '10px', fontSize: '12px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none',
          background: copied ? 'rgba(0,255,136,0.2)' : 'rgba(0,255,255,0.1)',
          color: copied ? '#00ff88' : '#00ffff',
          transition: 'all 0.2s',
        }}
      >{copied ? 'Copied!' : 'Copy Shoutout'}</button>
    </div>
  );
};


// ─── Resource Directory Tool ───
const RESOURCE_DATA = {
  dj: {
    title: 'DJ Resources & Tools',
    subtitle: 'Curated tools, apps and learning platforms to level up your DJ game',
    sections: [
      {
        name: 'DJ Software',
        items: [
          { name: 'Rekordbox', desc: 'Industry-standard DJ performance & library management', url: 'https://rekordbox.com', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'Serato DJ', desc: 'Professional DJ software with best-in-class DVS support', url: 'https://serato.com', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'Traktor Pro', desc: 'Creative DJ software with advanced FX and Stems', url: 'https://www.native-instruments.com/traktor', tag: 'Popular', tagColor: '#fbbf24' },
          { name: 'Mixed In Key', desc: 'Advanced harmonic analysis and key detection for your library', url: 'https://mixedinkey.com', tag: 'Recommended', tagColor: '#00ff88' },
          { name: 'DJ.Studio', desc: 'AI-powered set building and automix', url: 'https://dj.studio', tag: 'New', tagColor: '#00d4ff' },
          { name: 'VirtualDJ', desc: 'Feature-rich DJ software with AI-powered features', url: 'https://virtualdj.com', tag: 'Popular', tagColor: '#fbbf24' },
        ]
      },
      {
        name: 'Music Theory & Ear Training',
        items: [
          { name: 'Melodics', desc: 'Interactive practice app for keys, pads, and drums', url: 'https://melodics.com', tag: 'Recommended', tagColor: '#00ff88' },
          { name: 'Hooktheory', desc: 'Visual chord and melody theory — understand song structure', url: 'https://hooktheory.com', tag: 'Great', tagColor: '#fbbf24' },
          { name: 'musictheory.net', desc: 'Free music theory fundamentals — lessons & exercises', url: 'https://musictheory.net', tag: 'Free', tagColor: '#00d4ff' },
          { name: 'ToneGym', desc: 'Gamified ear training for identifying frequencies & intervals', url: 'https://tonegym.co', tag: 'Fun', tagColor: '#ff00ff' },
          { name: 'SoundGym', desc: 'Audio perception training — EQ, compression, stereo imaging', url: 'https://soundgym.co', tag: 'Pro', tagColor: '#ff9900' },
        ]
      },
      {
        name: 'Learning & Career',
        items: [
          { name: 'Aulart', desc: 'Masterclasses from Carl Cox, Charlotte de Witte, Richie Hawtin', url: 'https://aulart.com', tag: 'Premium', tagColor: '#fbbf24' },
          { name: 'Crossfader', desc: 'Free DJ tutorials, technique breakdowns, and tips', url: 'https://crossfader.co.uk', tag: 'Free', tagColor: '#00d4ff' },
          { name: 'Digital DJ Tips', desc: 'Courses, community, and gear reviews for DJs', url: 'https://digitaldjtips.com', tag: 'Popular', tagColor: '#fbbf24' },
          { name: 'DJ TechTools', desc: 'Gear news, MIDI mapping, and DJ technique articles', url: 'https://djtechtools.com', tag: 'Resource', tagColor: '#888' },
          { name: 'Beatport DJ Charts', desc: 'See what top DJs are playing — great for track discovery', url: 'https://beatport.com/charts', tag: 'Discovery', tagColor: '#00ff88' },
        ]
      },
    ]
  },
  streamer: {
    title: 'Streamer Resources & Tools',
    subtitle: 'Essential streaming tools, overlays, and growth platforms',
    sections: [
      {
        name: 'Streaming Software',
        items: [
          { name: 'OBS Studio', desc: 'Free, open-source streaming & recording software', url: 'https://obsproject.com', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'Streamlabs', desc: 'All-in-one streaming with alerts, overlays, and chat', url: 'https://streamlabs.com', tag: 'Popular', tagColor: '#fbbf24' },
          { name: 'Restream', desc: 'Multistream to Twitch, YouTube, Facebook simultaneously', url: 'https://restream.io', tag: 'Multi', tagColor: '#00d4ff' },
          { name: 'Riverside.fm', desc: 'High-quality remote recording for podcasts & interviews', url: 'https://riverside.fm', tag: 'Quality', tagColor: '#00ff88' },
        ]
      },
      {
        name: 'Overlays & Visuals',
        items: [
          { name: 'StreamElements', desc: 'Free overlays, alerts, chatbot, and stream management', url: 'https://streamelements.com', tag: 'Free', tagColor: '#00d4ff' },
          { name: 'Nerd or Die', desc: 'Premium stream packages, overlays, and animated alerts', url: 'https://nerdordie.com', tag: 'Premium', tagColor: '#fbbf24' },
          { name: 'JEYLABS', desc: 'DJ-specific stream overlays and visualizers', url: 'https://jeylabs.com', tag: 'DJ Focus', tagColor: '#ff00ff' },
          { name: 'Resolume', desc: 'Real-time VJ software for visual performances', url: 'https://resolume.com', tag: 'Pro', tagColor: '#ff9900' },
        ]
      },
      {
        name: 'Growth & Community',
        items: [
          { name: 'Mixcloud', desc: 'DJ mix hosting and discovery platform — legal streaming', url: 'https://mixcloud.com', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'TwitchTracker', desc: 'Analytics and statistics for your Twitch channel', url: 'https://twitchtracker.com', tag: 'Analytics', tagColor: '#00ff88' },
          { name: 'StreamScheme', desc: 'Guides and tips for growing your streaming audience', url: 'https://streamscheme.com', tag: 'Guide', tagColor: '#888' },
          { name: 'Epidemic Sound', desc: 'Royalty-free music for content creators and streamers', url: 'https://epidemicsound.com', tag: 'Audio', tagColor: '#fbbf24' },
        ]
      },
    ]
  },
  producer: {
    title: 'Producer Resources & Tools',
    subtitle: 'DAWs, plugins, sample libraries, and production education',
    sections: [
      {
        name: 'DAWs & Production',
        items: [
          { name: 'Ableton Live', desc: 'Creative production and performance — electronic music standard', url: 'https://ableton.com', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'FL Studio', desc: 'Feature-packed DAW with legendary piano roll and workflow', url: 'https://image-line.com', tag: 'Popular', tagColor: '#fbbf24' },
          { name: 'Logic Pro', desc: 'Apple\'s professional DAW with massive built-in library', url: 'https://apple.com/logic-pro', tag: 'Mac', tagColor: '#00d4ff' },
          { name: 'Bitwig Studio', desc: 'Modular approach to production with deep sound design', url: 'https://bitwig.com', tag: 'Creative', tagColor: '#ff00ff' },
        ]
      },
      {
        name: 'Plugins & Sound Design',
        items: [
          { name: 'Serum', desc: 'Xfer\'s wavetable synth — the most used synth in electronic music', url: 'https://xferrecords.com/products/serum', tag: 'Essential', tagColor: '#ff3333' },
          { name: 'Vital', desc: 'Free spectral warping wavetable synth — Serum alternative', url: 'https://vital.audio', tag: 'Free', tagColor: '#00d4ff' },
          { name: 'FabFilter', desc: 'Pro-Q, Pro-L, Saturn — industry standard mixing/mastering plugins', url: 'https://fabfilter.com', tag: 'Pro', tagColor: '#ff9900' },
          { name: 'Splice', desc: 'Sample library, rent-to-own plugins, and AI tools', url: 'https://splice.com', tag: 'Samples', tagColor: '#00ff88' },
          { name: 'Loopcloud', desc: 'Cloud-based sample library with tempo/key matching', url: 'https://loopcloud.com', tag: 'Samples', tagColor: '#00ff88' },
        ]
      },
      {
        name: 'Learning & Masterclasses',
        items: [
          { name: 'Aulart', desc: 'Production masterclasses from top electronic artists', url: 'https://aulart.com', tag: 'Premium', tagColor: '#fbbf24' },
          { name: 'Syntorial', desc: 'Interactive synth programming lessons — learn synthesis by ear', url: 'https://syntorial.com', tag: 'Recommended', tagColor: '#00ff88' },
          { name: 'Sonic Academy', desc: 'Tutorials and courses for electronic music production', url: 'https://sonicacademy.com', tag: 'Courses', tagColor: '#fbbf24' },
          { name: 'ADSR', desc: 'Tutorials, samples, and presets for producers', url: 'https://adsr.com', tag: 'Resource', tagColor: '#888' },
          { name: 'Point Blank', desc: 'Accredited music production and DJ courses', url: 'https://pointblankmusicschool.com', tag: 'School', tagColor: '#ff00ff' },
        ]
      },
    ]
  },
};

const ResourceDirectory = ({ category }) => {
  const data = RESOURCE_DATA[category] || RESOURCE_DATA.dj;
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <div data-testid={`resource-directory-${category}`} style={{ padding: '1.5rem' }}>
      <p style={{ fontSize: '12px', color: '#888', marginBottom: '1.5rem' }}>{data.subtitle}</p>

      {data.sections.map((section, si) => (
        <div key={si} style={{ marginBottom: '12px' }}>
          <button
            onClick={() => setExpandedSection(expandedSection === si ? -1 : si)}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px',
              background: expandedSection === si ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${expandedSection === si ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)'}`,
              borderRadius: expandedSection === si ? '10px 10px 0 0' : '10px',
              color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              transition: 'all 0.2s',
            }}
          >
            <span>{section.name}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '10px', color: '#555', fontWeight: 400 }}>{section.items.length} resources</span>
              <span style={{ fontSize: '16px', color: '#555', transition: 'transform 0.2s', transform: expandedSection === si ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
            </div>
          </button>

          {expandedSection === si && (
            <div style={{
              background: 'rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderTop: 'none',
              borderRadius: '0 0 10px 10px',
              padding: '8px',
            }}>
              {section.items.map((item, ii) => (
                <a
                  key={ii}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-${category}-${si}-${ii}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', marginBottom: '2px',
                    background: 'transparent',
                    borderRadius: '8px', textDecoration: 'none',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{item.name}</span>
                      <span style={{
                        padding: '1px 8px', fontSize: '9px', fontWeight: 700,
                        background: `${item.tagColor}15`, border: `1px solid ${item.tagColor}30`,
                        borderRadius: '6px', color: item.tagColor,
                      }}>{item.tag}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{item.desc}</div>
                  </div>
                  <span style={{ fontSize: '16px', color: '#444', flexShrink: 0 }}>↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      ))}

      <div style={{ textAlign: 'center', marginTop: '16px', padding: '8px', fontSize: '10px', color: '#444', fontStyle: 'italic' }}>
        Curated by BEATSEEK — links open in a new tab
      </div>
    </div>
  );
};

// ── Suggest Tool Button (shared across DJ, Streamer, Producer) ──
const SuggestToolButton = ({ category }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token || !text.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tools/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ suggestion: text, url: url.trim(), category }),
      });
      if (res.ok) { setStatus('sent'); setText(''); setUrl(''); setTimeout(() => { setOpen(false); setStatus(null); }, 1800); }
      else setStatus('error');
    } catch { setStatus('error'); }
    setSending(false);
  };

  return (
    <>
      <button
        data-testid={`suggest-tool-btn-${category}`}
        onClick={() => setOpen(true)}
        style={{
          padding: '5px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '14px', cursor: 'pointer',
          border: '1px solid rgba(0,255,136,0.25)', background: 'rgba(0,255,136,0.06)',
          color: '#00ff88', transition: 'all 0.2s', whiteSpace: 'nowrap',
        }}
      >+ Suggest Tools / Resources</button>
      {open && ReactDOM.createPortal(
        <div onClick={(e) => { if (e.target === e.currentTarget) { setOpen(false); setStatus(null); } }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div data-testid={`suggest-modal-${category}`} style={{
            width: '100%', maxWidth: '400px', background: '#0c0c12', borderRadius: '16px',
            border: '1px solid rgba(0,255,136,0.2)', padding: '24px', position: 'relative',
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700, color: '#fff' }}>Suggest a Tool or Resource</h3>
            <p style={{ margin: '0 0 14px', fontSize: '11px', color: '#888' }}>Help shape BEATSEEK — tell us what you'd love to see added!</p>
            {status === 'sent' ? (
              <div data-testid="suggest-success" style={{ textAlign: 'center', padding: '20px 0', color: '#00ff88', fontSize: '13px', fontWeight: 700 }}>
                Thanks for your suggestion! We love hearing from our community.
              </div>
            ) : (
              <>
                <textarea
                  data-testid={`suggest-input-${category}`}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Describe a tool, feature, or resource you'd like us to add..."
                  rows={3}
                  maxLength={1000}
                  style={{
                    width: '100%', padding: '10px 12px', fontSize: '12px', borderRadius: '10px', resize: 'vertical',
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
                <input
                  data-testid={`suggest-url-${category}`}
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="Link / URL for reference (optional)"
                  style={{
                    width: '100%', padding: '8px 12px', fontSize: '12px', borderRadius: '10px', marginTop: '8px',
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                  <span style={{ fontSize: '9px', color: '#555' }}>{text.length}/1000</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => { setOpen(false); setStatus(null); }} style={{
                      padding: '6px 14px', fontSize: '11px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                      border: 'none', background: 'rgba(255,255,255,0.06)', color: '#888',
                    }}>Cancel</button>
                    <button
                      data-testid={`suggest-submit-${category}`}
                      onClick={submit}
                      disabled={sending || !text.trim()}
                      style={{
                        padding: '6px 16px', fontSize: '11px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                        border: 'none', background: text.trim() ? 'rgba(0,255,136,0.15)' : 'rgba(255,255,255,0.04)',
                        color: text.trim() ? '#00ff88' : '#555', opacity: sending ? 0.5 : 1,
                      }}
                    >{sending ? 'Sending...' : 'Submit'}</button>
                  </div>
                </div>
                {status === 'error' && <div style={{ fontSize: '10px', color: '#ff5555', marginTop: '6px', textAlign: 'center' }}>Failed to submit. Please try again.</div>}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const DJToolsTab = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [activeView, setActiveView] = useState('tools'); // 'tips' or 'tools'

  // Category definitions
  const categories = [
    {
      id: 'djs',
      title: "DJ's",
      icon: Headphones,
      description: 'Professional tools for DJs',
      color: '#ff3333',
      gradient: 'linear-gradient(135deg, #ff3333, #ff0066)',
      tools: [
        { id: 'camelot', name: 'Camelot Wheel', icon: '🎡', description: 'Harmonic mixing key wheel', available: true },
        { id: 'track-lookup', name: 'Track Lookup', icon: '🔍', description: 'MusicBrainz remix chains & credits', available: true },
        { id: 'sample-chain', name: 'Sample Chain', icon: '🔗', description: 'Visualize sample connections', available: true },
        { id: 'bpm-tap', name: 'BPM Tap Tempo', icon: '🥁', description: 'Tap to find the BPM', available: true },
        { id: 'mix-calc', name: 'Mix Point Calculator', icon: '🔄', description: 'Calculate crossfade math between tracks', available: true },
        { id: 'transition-advisor', name: 'Transition Advisor', icon: '🎯', description: 'Key + BPM compatibility & technique tips', available: true },
        { id: 'set-timer', name: 'Set Timer', icon: '⏱️', description: 'Track your set length', available: true },
        { id: 'beat-counter', name: 'Beat Counter', icon: '🔢', description: 'Visual 4/4 beat counter', available: true },
        { id: 'cue-notepad', name: 'Cue Point Notepad', icon: '📝', description: 'Mark and export cue points', available: true },
        { id: 'gain-staging', name: 'Gain Staging Guide', icon: '📊', description: 'Signal flow & level reference', available: true },
        { id: 'dj-resources', name: 'DJ Resources', icon: '🧭', description: 'Curated tools, apps & learning platforms', available: true },
      ]
    },
    {
      id: 'streamers',
      title: "STREAMER's",
      icon: Video,
      description: 'Tools for live streaming',
      color: '#00ffff',
      gradient: 'linear-gradient(135deg, #00ffff, #00ccff)',
      tools: [
        { id: 'camelot', name: 'Camelot Wheel', icon: '🎡', description: 'Harmonic mixing key wheel', available: true },
        { id: 'title-gen', name: 'Stream Title Generator', icon: '✏️', description: 'Generate catchy stream titles', available: true },
        { id: 'chat-rules', name: 'Chat Rules Generator', icon: '📋', description: 'Build formatted channel rules', available: true },
        { id: 'raid-tracker', name: 'Raid Tracker', icon: '📥', description: 'Log raids given & received', available: true },
        { id: 'set-recap', name: 'Set Recap Builder', icon: '📝', description: 'Build post-stream tracklists', available: true },
        { id: 'goal-tracker', name: 'Viewer Goal Tracker', icon: '🎯', description: 'Set milestones & track growth', available: true },
        { id: 'schedule', name: 'Schedule Generator', icon: '📅', description: 'Create stream schedule', available: true },
        { id: 'shoutout', name: 'Shoutout Generator', icon: '📣', description: 'Quick raid shoutouts', available: true },
        { id: 'emote-lab', name: 'BEATSEEK Emote Lab', icon: '🪄', description: 'AI-generated Twitch/Discord emotes (premium)', available: true },
        { id: 'streamer-resources', name: 'Streamer Resources', icon: '🧭', description: 'Streaming tools, overlays & growth platforms', available: true },
      ]
    },
  ];

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedTool(null);
  };

  const handleToolClick = (tool) => {
    setSelectedTool(tool);
  };

  const handleBack = () => {
    if (selectedTool) {
      setSelectedTool(null);
    } else {
      setSelectedCategory(null);
    }
  };

  const handleKeySelect = (camelotKey, musicalKey) => {
    setSelectedKey(camelotKey);
  };

  // Render tool content
  const renderToolContent = () => {
    if (!selectedTool) return null;

    if (selectedTool.id === 'camelot') {
      return (
        <div style={{ padding: '1rem' }}>
          <CamelotWheel 
            onKeySelect={handleKeySelect}
            selectedKey={selectedKey}
            showLegend={true}
            size={380}
          />
        </div>
      );
    }

    // MusicBrainz Track Lookup Tool
    if (selectedTool.id === 'track-lookup') {
      return <TrackLookupTool />;
    }

    // MusicBrainz Sample Chain Visualization
    if (selectedTool.id === 'sample-chain') {
      return <SampleChainTool />;
    }

    // BPM Tap Tempo
    if (selectedTool.id === 'bpm-tap') {
      return <BpmTapTool />;
    }

    // Beat Counter
    if (selectedTool.id === 'beat-counter') {
      return <BeatCounterTool />;
    }

    // Set Timer
    if (selectedTool.id === 'set-timer') {
      return <SetTimerTool />;
    }

    // Setlist Planner — replaced by new tools
    if (selectedTool.id === 'setlist') {
      return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Replaced — see Mix Point Calculator, Transition Advisor, Cue Point Notepad, and Gain Staging Guide</div>;
    }

    // Mix Point Calculator
    if (selectedTool.id === 'mix-calc') {
      return <MixPointCalculator />;
    }

    // Transition Advisor
    if (selectedTool.id === 'transition-advisor') {
      return <TransitionAdvisor />;
    }

    // Cue Point Notepad
    if (selectedTool.id === 'cue-notepad') {
      return <CuePointNotepad />;
    }

    // Gain Staging Guide
    if (selectedTool.id === 'gain-staging') {
      return <GainStagingGuide />;
    }

    // Resource Directories
    if (selectedTool.id === 'dj-resources') {
      return <ResourceDirectory category="dj" />;
    }
    if (selectedTool.id === 'emote-lab') {
      let user = null;
      try { user = JSON.parse(localStorage.getItem('beatseek_user') || 'null'); } catch (_e) { /* ignore */ }
      if (!user?.id) {
        return (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
            Sign in to use the Emote Lab.
          </div>
        );
      }
      return <EmoteLabInline currentUser={user} />;
    }
    if (selectedTool.id === 'streamer-resources') {
      return <ResourceDirectory category="streamer" />;
    }
    if (selectedTool.id === 'producer-resources') {
      return <ResourceDirectory category="producer" />;
    }

    // Chord Progressions (Producer)
    if (selectedTool.id === 'chord-gen') {
      return <ChordProgressions />;
    }
    // Scale Reference (Producer)
    if (selectedTool.id === 'scale-ref') {
      return <ScaleReference />;
    }
    // Frequency Guide (Producer)
    if (selectedTool.id === 'freq-sheet') {
      return <FrequencyGuide />;
    }
    // MIDI Reference (Producer)
    if (selectedTool.id === 'midi-ref') {
      return <MidiReference />;
    }
    // BPM-to-Delay Calculator (Producer)
    if (selectedTool.id === 'bpm-delay') {
      return <BpmDelayCalculator />;
    }
    // Sample Clearance Checklist (Producer)
    if (selectedTool.id === 'sample-clearance') {
      return <SampleClearanceChecklist />;
    }
    // Release Prep Checklist (Producer)
    if (selectedTool.id === 'release-prep') {
      return <ReleasePrepChecklist />;
    }
    // Synth Patch Notes (Producer)
    if (selectedTool.id === 'patch-notes') {
      return <SynthPatchNotes />;
    }

    // Stream Title Generator (Streamer)
    if (selectedTool.id === 'title-gen') {
      return <StreamTitleGenerator />;
    }
    // Chat Rules Generator (Streamer)
    if (selectedTool.id === 'chat-rules') {
      return <ChatRulesGenerator />;
    }
    // Raid Tracker (Streamer)
    if (selectedTool.id === 'raid-tracker') {
      return <RaidTracker />;
    }
    // Set Recap Builder (Streamer)
    if (selectedTool.id === 'set-recap') {
      return <SetRecapBuilder />;
    }
    // Viewer Goal Tracker (Streamer)
    if (selectedTool.id === 'goal-tracker') {
      return <ViewerGoalTracker />;
    }
    // Schedule Generator (Streamer)
    if (selectedTool.id === 'schedule') {
      return <ScheduleGenerator />;
    }
    // Shoutout Generator (Streamer)
    if (selectedTool.id === 'shoutout') {
      return <ShoutoutGenerator />;
    }

    // LANDR AI Mastering - Coming Soon Page
    if (selectedTool.id === 'landr-mastering') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '2rem',
          background: 'linear-gradient(180deg, rgba(255, 0, 255, 0.1) 0%, rgba(0, 0, 0, 0) 100%)',
          borderRadius: '16px'
        }}>
          {/* Animated Header */}
          <div style={{
            position: 'relative',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              position: 'absolute',
              inset: '-20px',
              background: 'radial-gradient(circle, rgba(255, 0, 255, 0.3) 0%, transparent 70%)',
              filter: 'blur(30px)',
              animation: 'landrPulse 3s ease-in-out infinite'
            }} />
            <div style={{ position: 'relative', fontSize: '80px', marginBottom: '1rem' }}>🎚️</div>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '800',
              margin: '0 0 0.5rem 0',
              background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              AI Mastering with LANDR
            </h2>
            <span style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.3), rgba(0, 255, 255, 0.3))',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '25px',
              fontSize: '0.75rem',
              fontWeight: '700',
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '3px',
              animation: 'badgeGlow 2s ease-in-out infinite'
            }}>
              ✨ Coming Soon ✨
            </span>
          </div>

          {/* Description */}
          <p style={{
            fontSize: '1.1rem',
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            maxWidth: '600px',
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            Get your tracks release-ready with professional AI-powered mastering. 
            LANDR's industry-leading technology analyzes your music and delivers 
            studio-quality masters in minutes.
          </p>

          {/* Features Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            width: '100%',
            maxWidth: '700px',
            marginBottom: '2rem'
          }}>
            {[
              { icon: '🎯', title: 'Genre-Aware', desc: 'Optimized for EDM, House, Techno & more' },
              { icon: '⚡', title: 'Instant Results', desc: 'Get your master in minutes, not days' },
              { icon: '🔊', title: 'Loudness Ready', desc: 'Streaming-optimized LUFS levels' },
              { icon: '💎', title: 'Pro Quality', desc: 'Used by thousands of producers worldwide' }
            ].map((feature, i) => (
              <div key={i} style={{
                padding: '1.25rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{feature.icon}</div>
                <h4 style={{ color: '#fff', margin: '0 0 0.25rem 0', fontSize: '0.95rem' }}>{feature.title}</h4>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '0.8rem' }}>{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.4)',
            textAlign: 'center',
            maxWidth: '500px',
            marginBottom: '1.5rem',
            fontStyle: 'italic'
          }}>
            We're working on integrating LANDR's professional mastering directly into BEATSEEK. 
            In the meantime, check out LANDR to master your tracks today!
          </p>

          {/* CTA Button */}
          <a
            href="https://www.landr.com/mastering"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #ff00ff, #cc00ff)',
              border: 'none',
              borderRadius: '30px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: '700',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(255, 0, 255, 0.4)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 30px rgba(255, 0, 255, 0.6)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 0, 255, 0.4)';
            }}
          >
            Try LANDR Mastering →
          </a>

          {/* Notify Me */}
          <p style={{
            marginTop: '1.5rem',
            fontSize: '0.85rem',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            Want to know when it's available? Stay tuned! 🔔
          </p>

          <style>{`
            @keyframes landrPulse {
              0%, 100% { opacity: 0.5; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.1); }
            }
            @keyframes badgeGlow {
              0%, 100% { box-shadow: 0 0 10px rgba(255, 0, 255, 0.3); }
              50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.5); }
            }
          `}</style>
        </div>
      );
    }

    // Coming soon for other tools
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px', filter: 'grayscale(0.5)' }}>
          {selectedTool.icon}
        </div>
        <h3 style={{ fontSize: '24px', margin: '0 0 12px 0', color: '#fff' }}>
          {selectedTool.name}
        </h3>
        <span style={{
          display: 'inline-block',
          padding: '6px 16px',
          background: `linear-gradient(135deg, ${selectedCategory.color}40, rgba(0, 0, 0, 0.2))`,
          border: `1px solid ${selectedCategory.color}60`,
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          color: selectedCategory.color,
          textTransform: 'uppercase',
          letterSpacing: '2px',
          marginBottom: '16px'
        }}>
          Coming Soon
        </span>
        <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
          {selectedTool.description}
        </p>
      </div>
    );
  };

  // Render tools list for a category
  const renderToolsList = () => {
    if (!selectedCategory) return null;

    return (
      <div style={{ padding: '2rem' }}>
        {/* Back button and header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: 'none',
              color: selectedCategory.color,
              fontSize: '14px',
              cursor: 'pointer',
              padding: '8px 0',
              marginBottom: '1rem'
            }}
          >
            <ArrowLeft size={18} />
            Back to Categories
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: selectedCategory.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {React.createElement(selectedCategory.icon, { size: 24, color: '#fff' })}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', margin: '0 0 4px 0' }}>
                {selectedCategory.title} Tools
              </h2>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                {selectedCategory.description}
              </p>
            </div>
            <SuggestToolButton category={selectedCategory.id} />
          </div>
        </div>

        {/* Tools grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {selectedCategory.tools.map(tool => (
            <ToolCard 
              key={tool.id} 
              tool={tool} 
              category={selectedCategory}
              onClick={() => handleToolClick(tool)}
            />
          ))}
        </div>

        {/* Slide-up tool panel */}
        {selectedTool && ReactDOM.createPortal(
          <div
            data-testid="tool-panel-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedTool(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
              animation: 'toolPanelFadeIn 0.25s ease-out',
            }}
          >
            <div
              data-testid="tool-panel-content"
              style={{
                position: 'relative',
                maxHeight: '82vh',
                background: 'rgba(12,12,18,0.98)',
                borderRadius: '20px 20px 0 0',
                overflow: 'hidden',
                animation: 'toolPanelSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {/* Animated gradient border */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff, #ff00ff)',
                backgroundSize: '300% 100%',
                animation: 'gradientBorderFlow 3s linear infinite',
              }} />

              {/* Drag handle */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                <div style={{ width: '36px', height: '4px', borderRadius: '4px', background: 'rgba(255,255,255,0.15)' }} />
              </div>

              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '8px 20px 14px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}>
                <span style={{ fontSize: '28px' }}>{selectedTool.icon}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>{selectedTool.name}</h3>
                  <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>{selectedTool.description}</p>
                </div>
                <button
                  onClick={() => setSelectedTool(null)}
                  data-testid="tool-panel-close"
                  style={{
                    width: '32px', height: '32px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.06)', color: '#888',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px', transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,51,51,0.15)'; e.currentTarget.style.color = '#ff3333'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#888'; }}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Scrollable tool content */}
              <div style={{ overflowY: 'auto', maxHeight: 'calc(82vh - 90px)', padding: '0 0 2rem' }}>
                {renderToolContent()}
              </div>

              {/* Bottom gradient fade for scroll hint */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '40px',
                background: 'linear-gradient(transparent, rgba(12,12,18,0.95))',
                pointerEvents: 'none',
              }} />
            </div>

            <style>{`
              @keyframes toolPanelSlideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
              @keyframes toolPanelFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes gradientBorderFlow {
                0% { background-position: 0% 50%; }
                100% { background-position: 300% 50%; }
              }
            `}</style>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // If a category is selected, show tools
  if (selectedCategory) {
    return renderToolsList();
  }

  // Pro Tips view
  if (activeView === 'tips') {
    return (
      <div style={{ padding: '2rem', color: '#fff' }}>
        {/* View Toggle */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '4px' }}>
          <button onClick={() => setActiveView('tips')} data-testid="dj-view-tips" style={{
            flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
            background: 'linear-gradient(135deg, rgba(255,170,0,0.2), rgba(255,136,0,0.15))',
            color: '#ffaa00', transition: 'all 0.2s',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Lightbulb size={16} /> Pro Tips
            </span>
          </button>
          <button onClick={() => setActiveView('tools')} data-testid="dj-view-tools" style={{
            flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
            background: 'rgba(255,255,255,0.04)',
            color: '#888', transition: 'all 0.2s',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Wrench size={16} /> DJ Tools
            </span>
          </button>
        </div>
        <ProTipsTab mode="dj" />
      </div>
    );
  }

  // Default: show category selection with toggle
  return (
    <div style={{ padding: '2rem', color: '#fff' }}>
      {/* View Toggle */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '4px' }}>
        <button onClick={() => setActiveView('tips')} data-testid="dj-view-tips" style={{
          flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
          background: 'rgba(255,255,255,0.04)',
          color: '#888', transition: 'all 0.2s',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Lightbulb size={16} /> Pro Tips
          </span>
        </button>
        <button onClick={() => setActiveView('tools')} data-testid="dj-view-tools" style={{
          flex: 1, padding: '12px', fontSize: '13px', fontWeight: 700, borderRadius: '10px', cursor: 'pointer', border: 'none',
          background: 'linear-gradient(135deg, rgba(0,255,255,0.15), rgba(255,0,255,0.1))',
          color: '#00ffff', transition: 'all 0.2s',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Wrench size={16} /> DJ Tools
          </span>
        </button>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
          Select Your Category
        </h2>
        <p style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.6)' }}>
          Choose the tools designed for your workflow
        </p>
      </div>

      {/* Category Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px'
        }}
      >
        {categories.map((cat) => {
          const IconComponent = cat.icon;
          const availableTools = cat.tools.filter(t => t.available).length;
          return (
            <CategoryCard
              key={cat.id}
              category={cat}
              IconComponent={IconComponent}
              onClick={() => handleCategoryClick(cat)}
              availableTools={availableTools}
            />
          );
        })}
      </div>
    </div>
  );
};

// Tool Card Component
const ToolCard = ({ tool, category, onClick, isSelected }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '1.25rem',
        background: isSelected 
          ? `linear-gradient(135deg, ${category.color}30, rgba(0, 0, 0, 0.3))`
          : isHovered 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(255, 255, 255, 0.03)',
        border: isSelected 
          ? `2px solid ${category.color}` 
          : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        opacity: tool.available ? 1 : 0.6
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '28px' }}>{tool.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: isSelected ? category.color : '#fff' 
            }}>
              {tool.name}
            </span>
            {!tool.available && (
              <span style={{
                fontSize: '9px',
                padding: '2px 6px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'uppercase'
              }}>
                Soon
              </span>
            )}
          </div>
          <p style={{ 
            margin: '4px 0 0 0', 
            fontSize: '12px', 
            color: 'rgba(255, 255, 255, 0.5)' 
          }}>
            {tool.description}
          </p>
        </div>
      </div>
    </div>
  );
};

// Category Card Component
const CategoryCard = ({ category, IconComponent, onClick, availableTools }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        padding: '2rem',
        background: isHovered
          ? `linear-gradient(135deg, ${category.color}20, rgba(26, 26, 46, 0.9))`
          : 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.9))',
        border: isHovered ? `2px solid ${category.color}` : '2px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
        boxShadow: isHovered ? `0 20px 40px ${category.color}40` : '0 4px 6px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}
    >
      {/* Animated background */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: category.gradient,
          opacity: isHovered ? 0.1 : 0,
          transition: 'opacity 0.3s ease'
        }}
      />

      {/* Available tools badge */}
      {availableTools > 0 && (
        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: category.color,
          color: '#000',
          fontSize: '11px',
          fontWeight: '700',
          padding: '4px 10px',
          borderRadius: '12px'
        }}>
          {availableTools} Available
        </div>
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Icon */}
        <div
          style={{
            width: '80px',
            height: '80px',
            marginBottom: '1.5rem',
            borderRadius: '16px',
            background: category.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${category.color}40`,
            transition: 'all 0.3s ease',
            transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)'
          }}
        >
          <IconComponent size={40} color="#fff" />
        </div>

        {/* Title */}
        <h3
          style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#fff',
            marginBottom: '0.75rem'
          }}
        >
          {category.title}
        </h3>

        {/* Description */}
        <p
          style={{
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: '1.5',
            margin: 0
          }}
        >
          {category.description}
        </p>

        {/* Arrow indicator */}
        <div
          style={{
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: category.color,
            fontSize: '0.9rem',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            transform: isHovered ? 'translateX(8px)' : 'translateX(0)'
          }}
        >
          Explore Tools
          <span style={{ fontSize: '1.2rem' }}>→</span>
        </div>
      </div>
    </div>
  );
};

export default DJToolsTab;

// Named exports for producer tool components (used by ProducerToolsPanel)
export {
  ChordProgressions,
  ScaleReference,
  FrequencyGuide,
  MidiReference,
  BpmDelayCalculator,
  SampleClearanceChecklist,
  ReleasePrepChecklist,
  SynthPatchNotes,
  ResourceDirectory,
  ToolCard,
  SuggestToolButton,
};
